// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as BABYLON from 'babylonjs';
import * as Interfaces from '../../Interfaces/Interfaces';
import * as Enumerations from '../../Enumerations/Enumerations';
import * as Data from '../Data/WebData';
import { VersionedArray } from '../../helpers/Helpers';

class MeshManager implements Interfaces.Interfaces.IMeshManager {
    private static readonly XRAY_LAYER_SOURCE = "xray";
    private static readonly XRAY_LAYER_PRIORITY = 1;
    private static readonly CYLINDER_INSTANCE_TESSELLATION = 35;
    private static readonly CYLINDER_INSTANCE_COMPLEXITY_LEVEL = MeshManager.CYLINDER_INSTANCE_TESSELLATION;
    private static readonly SKIP_CYLINDER_INSTANCE_MESH_CREATION = false;
    private static readonly NUMERIC_ARRAY_ENTRY_BYTES = 4;
    private static readonly STRING_CHARACTER_BYTES = 2;
    private static readonly JS_REFERENCE_BYTES = 8;
    private static readonly MESH_OBJECT_INFO_BYTES = 160;
    private static readonly MESH_OBJECT_TRACKING_BYTES = 48;
    private static readonly APPEARANCE_OVERRIDE_ENTRY_BYTES = 24;
    private static readonly CYLINDER_INSTANCE_ENTRY_BYTES = MeshManager.JS_REFERENCE_BYTES * 2;
    private static readonly CYLINDER_INSTANCE_MASTER_OBJECT_BYTES = 224 * 4;
    private static readonly CYLINDER_INSTANCE_GROUP_OBJECT_BYTES = 72 * 4;
    private static readonly CYLINDER_INSTANCE_MASTER_BYTES = 192 * 4;
    private static readonly CYLINDER_INSTANCE_GROUP_BYTES = 160 * 4;
    private static readonly CYLINDER_INSTANCE_KEEP_MASTERS_ON_WHOLE_SWAP = true;
    private static readonly CYLINDER_INSTANCE_MAX_ENTRIES = 10000;
    private static readonly CLEANUP_UNUSED_CYLINDER_INSTANCE_MASTERS = true;
    private static readonly REBUILD_CYLINDER_INSTANCE_MESH_ON_SYNC = false;

    private _pointXrayEnabled: boolean = false;
    private _pointCollisionEnabled: boolean = false;
    private _pageToMesh: { [n: string]: { [n: string]: string[]; }; } = {};
    private _pageToRegion: { [n: string]: { [n: string]: string[]; }; } = {};

    private MERGE_MESH_THRESHOLD = 200;
    public _babScene: BABYLON.Scene;
    public _materialManager: Interfaces.Interfaces.IMaterialsManager;
    private _sceneContentExtents: BABYLON.BoundingBox;

    // Map of meshes and their indexes to object id.
    public _masterMeshToObjectInfo: { [masterMeshId: string]: SubMeshObjectInfo[]; } = {};
    private _masterMeshObjectIndex: { [masterMeshId: string]: { [objectId: string]: number; }; } = {};
    public _masterMeshKeys: string[] = [];

    private _mergedMeshKeys: string[] = [];

    private _remappedMeshId: { [objectId: string]: number; } = {};
    private _objectIdToMasterMeshIds: { [objectId: string]: string[]; } = {};
    private _objectBlockPageToMaster: {
        [objectId: string]: {
            [pageKey: string]: string | string[];
        };
    } = {};

    // When meshManager is told to add a mesh, meshes are added here, until they are later merged into "mega" meshes (the mergedMeshes collection). When this merging occurs, they are removed from this
    // collection, and mered into the second collection, mergedMeshes.
    private _meshesToMerge: { [appearanceId: string]: BABYLON.Mesh[]; } = {};
    private _masterMeshes: { [masterMeshId: string]: BABYLON.Mesh; } = {};
    private _masterPointClouds: { [masterMeshId: string]: BABYLON.Mesh; } = {};
    private _meshes: VersionedArray<BABYLON.AbstractMesh> = new VersionedArray();

    private _masterSPS: { [masterMeshId: string]: CustomSolidParticleSystem; } = {};
    private _cylinderInstanceMasters: { [masterKey: string]: CylinderInstanceMasterData; } = {};
    private _cylinderInstanceGroups: { [masterMeshId: string]: CylinderInstanceGroupData; } = {};
    private _cylinderInstanceDisposalBatchDepth: number = 0;
    private _pendingCylinderInstancePageDisposals: { [pageKey: string]: boolean; } = {};
    private _objectToAppearanceOverridesList: { [objectId: string]: string[]; } = {};


    private _pendingHiddenAssets: { [objectId: string]: boolean; } = {};
    private _hiddenAssets: { [objectId: string]: string[]; } = {};
    private _hiddenOriginalMaterials: { [objectId: string]: string[]; } = {};
    private _assetsUnhiddenCallback: ((assetIds: string[]) => void) | null = null;

    private _evenHandler: Interfaces.Interfaces.IEventManager;

    private _octreeMeshes: { [blockId: string]: BABYLON.Mesh; } = {};
    private _octreePageCount: { [blockId: string]: number } = {};
    private _octreeLoadedPageCount: { [blockId: string]: number } = {};
    private _octreeVisiblePageCount: { [blockId: string]: number } = {};

    private _unloadedMaterial: BABYLON.StandardMaterial;
    private _partiallyMaterial: BABYLON.StandardMaterial;
    private _loadedMaterial: BABYLON.StandardMaterial;
    private _loadedVisibleMaterial: BABYLON.StandardMaterial;

    // Track original materials for region meshes so we can toggle XRay on/off.
    private _regionOriginalMaterial: { [masterMeshId: string]: string } = {};
    private _regionMaterialBlock: { [masterMeshId: string]: string } = {};
    // Tracks XRay active state for creation-time initialization
    private _regionXRayActive: boolean = false;

    // Helper array used to keep track of what materials have been added to the primitives. This is useful because we are organizing meshesToMerge, and mergedMeshes based on materials as the key.
    public meshesDirty: boolean = false;

    private _applyCylinderMasterMeshSync(masterData: CylinderInstanceMasterData, useSetBuffer: boolean, needsRefresh: boolean): void {
        if (!masterData || !masterData.sourceMesh) {
            return;
        }

        if (MeshManager.REBUILD_CYLINDER_INSTANCE_MESH_ON_SYNC) {
            this._recreateCylinderInstanceSourceMesh(masterData);
        }

        // Avoid Babylon's incremental thin-instance update path for cylinder masters.
        // Replacing the matrix buffer each sync preserves the non-rebuild behavior while
        // matching the code path that makes the disappearing-instance bug go away.
        masterData.sourceMesh.thinInstanceSetBuffer("matrix", masterData.matrixBuffer, 16, false);

        masterData.sourceMesh.thinInstanceCount = masterData.entries.objectIds.length;
        masterData.sourceMesh.thinInstanceEnablePicking = false;
        masterData.sourceMesh.setEnabled(masterData.entries.objectIds.length > 0);

        if (needsRefresh && masterData.entries.objectIds.length > 0) {
            masterData.sourceMesh.thinInstanceRefreshBoundingInfo();
        }
    }

    private _resolveExactCylinderMaterial(materialKey: string): BABYLON.Material {
        if (!materialKey) {
            return this._materialManager.getMaterial(materialKey);
        }

        const sceneMaterial = this._babScene.getMaterialByID(materialKey)
            || this._babScene.materials.find(material => material && (material.id === materialKey || material.name === materialKey));

        return sceneMaterial || this._materialManager.getMaterial(materialKey);
    }

    get meshExtents(): BABYLON.BoundingBox {
        return this._sceneContentExtents;
    }

    constructor(eventHander: Interfaces.Interfaces.IEventManager, scene: BABYLON.Scene, materialManager: Interfaces.Interfaces.IMaterialsManager) {
        this._babScene = scene;
        this._materialManager = materialManager;
        this._masterMeshKeys = [];
        this._evenHandler = eventHander;
        this._unloadedMaterial = new BABYLON.StandardMaterial("boxMat", this._babScene);
        this._unloadedMaterial.diffuseColor = new BABYLON.Color3(1, 0, 0);
        this._unloadedMaterial.ambientColor = new BABYLON.Color3(.5, .5, .5);
        this._unloadedMaterial.specularColor = new BABYLON.Color3(.5, .5, .5);
        this._unloadedMaterial.emissiveColor = new BABYLON.Color3(.5, .5, .5);
        this._unloadedMaterial.specularPower = 64;
        this._unloadedMaterial.alpha = .5;

        this._partiallyMaterial = new BABYLON.StandardMaterial("boxMat", this._babScene);
        this._partiallyMaterial.diffuseColor = new BABYLON.Color3(0, 0, 1);
        this._partiallyMaterial.ambientColor = new BABYLON.Color3(.5, .5, .5);
        this._partiallyMaterial.specularColor = new BABYLON.Color3(.5, .5, .5);
        this._partiallyMaterial.emissiveColor = new BABYLON.Color3(.5, .5, .5);
        this._partiallyMaterial.specularPower = 64;
        this._partiallyMaterial.alpha = .5;

        this._loadedMaterial = new BABYLON.StandardMaterial("boxMat", this._babScene);
        this._loadedMaterial.diffuseColor = new BABYLON.Color3(0, 1, 0);
        this._loadedMaterial.ambientColor = new BABYLON.Color3(.5, .5, .5);
        this._loadedMaterial.specularColor = new BABYLON.Color3(.5, .5, .5);
        this._loadedMaterial.emissiveColor = new BABYLON.Color3(.5, .5, .5);
        this._loadedMaterial.specularPower = 64;
        this._loadedMaterial.alpha = 0;

        this._loadedVisibleMaterial = new BABYLON.StandardMaterial("boxMat", this._babScene);
        this._loadedVisibleMaterial.diffuseColor = new BABYLON.Color3(0, 1, 0);
        this._loadedVisibleMaterial.ambientColor = new BABYLON.Color3(.5, .5, .5);
        this._loadedVisibleMaterial.specularColor = new BABYLON.Color3(.5, .5, .5);
        this._loadedVisibleMaterial.emissiveColor = new BABYLON.Color3(.5, .5, .5);
        this._loadedVisibleMaterial.specularPower = 64;
        this._loadedVisibleMaterial.alpha = .5;
    }

    dispose() {
        this.clearMeshes();

        if (this._babScene) {
            this._babScene.dispose();
            this._babScene = null;
        }

        this._materialManager = null;
        this._sceneContentExtents = null;
        this._masterMeshToObjectInfo = {};
        this._masterMeshObjectIndex = {};
        this._hiddenOriginalMaterials = {};
        this._masterMeshKeys = [];
        this._mergedMeshKeys = [];
        this._remappedMeshId = {};
        this._objectIdToMasterMeshIds = {};
        this._objectBlockPageToMaster = {};
        this._meshesToMerge = {};
        this._masterMeshes = {};
        this._masterPointClouds = {};
        this._masterSPS = {};
        this._cylinderInstanceMasters = {};
        this._cylinderInstanceGroups = {};
        this._evenHandler = null;
        this._hiddenAssets = {};
        this._pendingHiddenAssets = {};
        this._assetsUnhiddenCallback = null;
    }

    markedPointChanged(markedPointId: string) {
        // This will, when needed, fire a change event so that MP manager can also clear/hide the label.
    }

    getMaterialManager() {
        return this._materialManager;
    }

    private _rebuildMasterMeshObjectIndex(masterMeshId: string): { [objectId: string]: number; } | null {
        const objectInfosOfMaster = this._masterMeshToObjectInfo[masterMeshId];
        if (!objectInfosOfMaster) {
            delete this._masterMeshObjectIndex[masterMeshId];
            return null;
        }

        const objectIndex: { [objectId: string]: number; } = {};

        for (let i = 0; i < objectInfosOfMaster.length; i++) {
            const objectInfo = objectInfosOfMaster[i];
            if (!objectInfo || !objectInfo.objectId) continue;

            objectIndex[objectInfo.objectId] = i;
        }

        this._masterMeshObjectIndex[masterMeshId] = objectIndex;
        return objectIndex;
    }

    private _ensureMasterMeshObjectIndex(masterMeshId: string): { [objectId: string]: number; } | null {
        return this._masterMeshObjectIndex[masterMeshId] || this._rebuildMasterMeshObjectIndex(masterMeshId);
    }

    private _recordMasterMeshObjectIndex(masterMeshId: string, objectId: string, index: number): void {
        if (!objectId) return;

        let objectIndex = this._masterMeshObjectIndex[masterMeshId];
        if (!objectIndex) {
            objectIndex = {};
            this._masterMeshObjectIndex[masterMeshId] = objectIndex;
        }

        objectIndex[objectId] = index;
    }

    private _isCylinderInstanceMaster(masterMeshId: string): boolean {
        return this._cylinderInstanceGroups[masterMeshId] != null;
    }

    private _isCylinderInstanceBatch(masterMeshId: string): boolean {
        return this._cylinderInstanceMasters[masterMeshId] != null;
    }

    private _isCylinderInstanceBatchKey(masterMeshId: string): boolean {
        return !!masterMeshId && masterMeshId.indexOf("CylinderInstance|") === 0;
    }

    private _getCylinderInstanceMasterKey(materialKey: string, complexityLevel: number): string {
        return ["CylinderInstance", materialKey].join("|");
    }

    private _getCylinderInstanceOverflowMasterKey(materialKey: string, overflowIndex: number): string {
        return ["CylinderInstance", materialKey, `overflow_${overflowIndex}`].join("|");
    }

    private _getCylinderInstanceGroupKey(blockId: string, pageId: string, materialKey: string, complexityLevel: number): string {
        return ["CylinderInstanceGroup", blockId, pageId, materialKey].join("|");
    }

    private _getNormalizedCylinderInstanceComplexityLevel(complexityLevel: number): number {
        return MeshManager.CYLINDER_INSTANCE_COMPLEXITY_LEVEL;
    }

    private _toVector3(input: any): BABYLON.Vector3 {
        if (!input) {
            return BABYLON.Vector3.Zero();
        }

        if (input._x != null) {
            return new BABYLON.Vector3(input._x, input._y, input._z);
        }

        return new BABYLON.Vector3(input.x != null ? input.x : input.X, input.y != null ? input.y : input.Y, input.z != null ? input.z : input.Z);
    }

    private _createCylinderInstanceObjectInfo(objectId: string, minExtents: any, maxExtents: any, materialKey: string): SubMeshObjectInfo {
        const objectInfo = new SubMeshObjectInfo();
        objectInfo.objectId = objectId;
        objectInfo.vertexStartIndex = 0;
        objectInfo.vertexCount = 0;
        objectInfo.indiciesStartIndex = 0;
        objectInfo.indicesCount = 0;
        objectInfo.uvStartIndex = 0;
        objectInfo.uvCount = 0;
        objectInfo.minExtents = this._toVector3(minExtents);
        objectInfo.maxExtents = this._toVector3(maxExtents);
        objectInfo.materialOverride = materialKey;
        return objectInfo;
    }

    private _ensurePageMeshKey(blockId: string, pageId: string, masterMeshId: string): void {
        if (this._pageToMesh[blockId] == null) {
            this._pageToMesh[blockId] = {};
        }

        if (this._pageToMesh[blockId][pageId] == null) {
            this._pageToMesh[blockId][pageId] = [];
        }

        if (this._pageToMesh[blockId][pageId].indexOf(masterMeshId) === -1) {
            this._pageToMesh[blockId][pageId].push(masterMeshId);
        }
    }

    private _removePageMeshKey(blockId: string, pageId: string, masterMeshId: string): void {
        const pageMeshes = this._pageToMesh[blockId] && this._pageToMesh[blockId][pageId];
        if (!pageMeshes) {
            return;
        }

        const index = pageMeshes.indexOf(masterMeshId);
        if (index !== -1) {
            pageMeshes.splice(index, 1);
        }
    }

    private _getObjectBlockPageKey(blockId: string, pageId: string): string {
        return [blockId, pageId].join("|");
    }

    private _parseObjectBlockPageKey(pageKey: string): { blockId: string; pageId: string; } {
        const separatorIndex = pageKey.indexOf("|");
        if (separatorIndex === -1) {
            return {
                blockId: pageKey,
                pageId: ""
            };
        }

        return {
            blockId: pageKey.substring(0, separatorIndex),
            pageId: pageKey.substring(separatorIndex + 1)
        };
    }

    private _createEmptyCylinderInstanceEntries(): CylinderThinInstanceEntries {
        return {
            objectIds: [],
            pageKeys: []
        };
    }

    private _toMegabytes(bytes: number): number {
        return bytes / (1024 * 1024);
    }

    private _getStringBytes(value: string | null | undefined): number {
        return value ? value.length * MeshManager.STRING_CHARACTER_BYTES : 0;
    }

    private _getNumericArrayBytes(values: any[] | null | undefined): number {
        return values ? values.length * MeshManager.NUMERIC_ARRAY_ENTRY_BYTES : 0;
    }

    private _estimateGeometryBytes(geometry: any): number {
        if (!geometry) {
            return 0;
        }

        return this._getNumericArrayBytes(geometry.normals)
            + this._getNumericArrayBytes(geometry.indices)
            + this._getNumericArrayBytes(geometry.positions)
            + this._getNumericArrayBytes(geometry.colors);
    }

    private _estimateObjectInfoBytes(objectInfos: any[]): number {
        if (!objectInfos || objectInfos.length === 0) {
            return 0;
        }

        let bytes = 0;

        for (let i = 0; i < objectInfos.length; i++) {
            const objectInfo = objectInfos[i];
            bytes += MeshManager.MESH_OBJECT_INFO_BYTES;
            bytes += this._getStringBytes(objectInfo && objectInfo.objectId);
            bytes += this._getStringBytes(objectInfo && objectInfo.materialOverride);
        }

        return bytes;
    }

    private _estimateAppearanceOverrideBytes(appearanceOverrideData: any[]): number {
        if (!appearanceOverrideData || appearanceOverrideData.length === 0) {
            return 0;
        }

        let bytes = 0;

        for (let i = 0; i < appearanceOverrideData.length; i++) {
            const appearanceOverride = appearanceOverrideData[i];
            if (!appearanceOverride || !appearanceOverride.ListItems) {
                continue;
            }

            bytes += this._getStringBytes(appearanceOverride.OverrideObjectListID);

            for (let p = 0; p < appearanceOverride.ListItems.length; p++) {
                bytes += this._getStringBytes(appearanceOverride.ListItems[p]);
                bytes += MeshManager.APPEARANCE_OVERRIDE_ENTRY_BYTES;
            }

            bytes += appearanceOverride.ListItems.length * MeshManager.APPEARANCE_OVERRIDE_ENTRY_BYTES;
        }

        return bytes;
    }

    private _estimateMeshRetentionMemory(meshData: any, appearanceOverrideData: any[], includeAppearanceOverrides: boolean): number {
        const objectInfoCount = meshData && meshData.ObjectInfo ? meshData.ObjectInfo.length : 0;
        const idToMasterCount = meshData && meshData.IdToMaster ? meshData.IdToMaster.length : 0;
        let bytes = this._estimateGeometryBytes(meshData && meshData.Geometry);

        bytes += this._estimateObjectInfoBytes(meshData && meshData.ObjectInfo);
        bytes += objectInfoCount * MeshManager.MESH_OBJECT_TRACKING_BYTES;
        bytes += idToMasterCount * MeshManager.MESH_OBJECT_TRACKING_BYTES;

        if (includeAppearanceOverrides) {
            bytes += this._estimateAppearanceOverrideBytes(appearanceOverrideData);
        }

        return this._toMegabytes(bytes);
    }

    private _getObjectPageMasterIds(entry: string | string[] | null | undefined): string[] {
        if (!entry) {
            return [];
        }

        return typeof entry === "string" ? [entry] : entry;
    }

    private _syncObjectMasterMeshIds(objectId: string): void {
        const byPage = this._objectBlockPageToMaster[objectId];
        if (!byPage) {
            delete this._objectIdToMasterMeshIds[objectId];
            return;
        }

        const seen: { [masterMeshId: string]: boolean; } = {};
        const masterMeshIds: string[] = [];

        for (const pageKey in byPage) {
            const pageMasterIds = this._getObjectPageMasterIds(byPage[pageKey]);
            for (let i = 0; i < pageMasterIds.length; i++) {
                const masterMeshId = pageMasterIds[i];
                if (!masterMeshId || seen[masterMeshId]) {
                    continue;
                }

                seen[masterMeshId] = true;
                masterMeshIds.push(masterMeshId);
            }
        }

        if (masterMeshIds.length > 0) {
            this._objectIdToMasterMeshIds[objectId] = masterMeshIds;
        }
        else {
            delete this._objectIdToMasterMeshIds[objectId];
        }
    }

    private _removeObjectMasterForPage(objectId: string, blockId: string, pageId: string, masterMeshId: string) {
        const byPage = this._objectBlockPageToMaster[objectId];
        if (!byPage) {
            return;
        }

        const pageKey = this._getObjectBlockPageKey(blockId, pageId);
        const current = byPage[pageKey];
        if (!current) {
            return;
        }

        const currentMasterIds = this._getObjectPageMasterIds(current);
        const keptForPage: string[] = [];
        let removed = false;

        for (let i = 0; i < currentMasterIds.length; i++) {
            if (currentMasterIds[i] === masterMeshId) {
                removed = true;
                continue;
            }

            keptForPage.push(currentMasterIds[i]);
        }

        if (!removed) {
            return;
        }

        if (keptForPage.length === 0) {
            delete byPage[pageKey];
        }
        else if (keptForPage.length === 1) {
            byPage[pageKey] = keptForPage[0];
        }
        else {
            byPage[pageKey] = keptForPage;
        }

        if (Object.keys(byPage).length === 0) {
            delete this._objectBlockPageToMaster[objectId];
        }

        this._syncObjectMasterMeshIds(objectId);
    }

    private _removeObjectInfoFromMaster(masterMeshId: string, objectId: string): SubMeshObjectInfo | null {
        const objectInfos = this._masterMeshToObjectInfo[masterMeshId];
        if (!objectInfos) {
            return null;
        }

        const objectIndex = this.getObjectIndexByObjectId(masterMeshId, objectId);
        if (objectIndex < 0) {
            return null;
        }

        const removed = objectInfos.splice(objectIndex, 1)[0] || null;
        this._rebuildMasterMeshObjectIndex(masterMeshId);
        return removed;
    }

    private _getCylinderInstanceMaster(masterKey: string): CylinderInstanceMasterData | null {
        return this._cylinderInstanceMasters[masterKey] || null;
    }

    private _getCylinderInstanceGroup(masterMeshId: string): CylinderInstanceGroupData | null {
        return this._cylinderInstanceGroups[masterMeshId] || null;
    }

    private _syncCylinderInstanceMasterMaterial(masterData: CylinderInstanceMasterData): void {
        if (!masterData || !masterData.sourceMesh) {
            return;
        }

        const material = this._resolveExactCylinderMaterial(masterData.materialKey);
        if (material && masterData.sourceMesh.material !== material) {
            masterData.sourceMesh.material = material;
        }
    }

    private _createCylinderInstanceSourceMesh(masterKey: string, materialKey: string): BABYLON.Mesh | null {
        if (MeshManager.SKIP_CYLINDER_INSTANCE_MESH_CREATION) {
            return null;
        }

        const sourceMesh = BABYLON.Mesh.CreateCylinder(masterKey, 1, 1, 1, MeshManager.CYLINDER_INSTANCE_TESSELLATION, 1, this._babScene, true);
        sourceMesh.setEnabled(false);
        sourceMesh.isPickable = false;
        sourceMesh.thinInstanceEnablePicking = false;
        sourceMesh.material = this._resolveExactCylinderMaterial(materialKey);
        (sourceMesh as any).dataSource = Enumerations.PickObjectType.Object;

        return sourceMesh;
    }

    private _recreateCylinderInstanceSourceMesh(masterData: CylinderInstanceMasterData): void {
        if (!masterData || MeshManager.SKIP_CYLINDER_INSTANCE_MESH_CREATION) {
            return;
        }

        const previousSourceMesh = masterData.sourceMesh;
        const recreatedSourceMesh = this._createCylinderInstanceSourceMesh(masterData.masterMeshId, masterData.materialKey);

        if (!recreatedSourceMesh) {
            return;
        }

        masterData.sourceMesh = recreatedSourceMesh;
        this._masterMeshes[masterData.masterMeshId] = recreatedSourceMesh;
        this._meshes.push(recreatedSourceMesh);

        if (previousSourceMesh) {
            this._meshes.fastRemove(previousSourceMesh);
            previousSourceMesh.dispose(false, false);
        }
    }

    private _allocateCylinderInstanceMaster(masterKey: string, materialKey: string, complexityLevel: number): CylinderInstanceMasterData {
        const sourceMesh = this._createCylinderInstanceSourceMesh(masterKey, materialKey);

        const masterData: CylinderInstanceMasterData = {
            masterMeshId: masterKey,
            materialKey: materialKey,
            complexityLevel: complexityLevel,
            sourceMesh: sourceMesh,
            entries: this._createEmptyCylinderInstanceEntries(),
            matrixBuffer: null,
            objectEntryCounts: {},
            objectBounds: {}
        };

        this._cylinderInstanceMasters[masterKey] = masterData;

        if (sourceMesh) {
            this._masterMeshes[masterKey] = sourceMesh;
            this._meshes.push(sourceMesh);
        }

        this._masterMeshToObjectInfo[masterKey] = [];
        this._masterMeshObjectIndex[masterKey] = {};

        if (this._masterMeshKeys.indexOf(masterKey) === -1) {
            this._masterMeshKeys.push(masterKey);
        }

        return masterData;
    }

    private _getOrCreateCylinderInstanceMaster(materialKey: string, complexityLevel: number): CylinderInstanceMasterData {
        complexityLevel = this._getNormalizedCylinderInstanceComplexityLevel(complexityLevel);
        const baseKey = this._getCylinderInstanceMasterKey(materialKey, complexityLevel);
        const baseMaster = this._getCylinderInstanceMaster(baseKey);

        if (!baseMaster) {
            const masterData = this._allocateCylinderInstanceMaster(baseKey, materialKey, complexityLevel);
            this._syncCylinderInstanceMasterMaterial(masterData);
            return masterData;
        }

        if (baseMaster.entries.objectIds.length < MeshManager.CYLINDER_INSTANCE_MAX_ENTRIES) {
            this._syncCylinderInstanceMasterMaterial(baseMaster);
            return baseMaster;
        }

        // Base master is at capacity — find or create the next available overflow master.
        for (let overflowIndex = 1; ; overflowIndex++) {
            const overflowKey = this._getCylinderInstanceOverflowMasterKey(materialKey, overflowIndex);
            const overflowMaster = this._getCylinderInstanceMaster(overflowKey);

            if (!overflowMaster) {
                const masterData = this._allocateCylinderInstanceMaster(overflowKey, materialKey, complexityLevel);
                this._syncCylinderInstanceMasterMaterial(masterData);
                return masterData;
            }

            if (overflowMaster.entries.objectIds.length < MeshManager.CYLINDER_INSTANCE_MAX_ENTRIES) {
                this._syncCylinderInstanceMasterMaterial(overflowMaster);
                return overflowMaster;
            }
        }
    }

    private _getOrCreateCylinderInstanceGroup(blockId: string, pageId: string, materialKey: string, complexityLevel: number): CylinderInstanceGroupData {
        complexityLevel = this._getNormalizedCylinderInstanceComplexityLevel(complexityLevel);
        const masterMeshId = this._getCylinderInstanceGroupKey(blockId, pageId, materialKey, complexityLevel);
        let masterData = this._getCylinderInstanceGroup(masterMeshId);

        if (masterData) {
            return masterData;
        }

        const sourceMaster = this._getOrCreateCylinderInstanceMaster(materialKey, complexityLevel);

        masterData = {
            masterMeshId: masterMeshId,
            sourceMasterKey: sourceMaster.masterMeshId,
            blockId: blockId,
            pageId: pageId,
            materialKey: materialKey,
            complexityLevel: complexityLevel,
            objectIds: {}
        };

        this._cylinderInstanceGroups[masterMeshId] = masterData;
        this._masterMeshToObjectInfo[masterMeshId] = [];
        this._masterMeshObjectIndex[masterMeshId] = {};

        this._ensurePageMeshKey(blockId, pageId, masterMeshId);

        return masterData;
    }

    private _buildCylinderThinInstanceMatrix(transform: Data.GraphicData.CylinderInstanceTransform): BABYLON.Matrix {
        const scaling = this._toVector3(transform.Scaling);
        const position = this._toVector3(transform.Position);
        const axis = new BABYLON.Vector3(transform.Rotation.X, transform.Rotation.Y, transform.Rotation.Z);
        const rotation = axis.lengthSquared() > 0
            ? BABYLON.Quaternion.RotationAxis(axis.normalize(), transform.Rotation.W)
            : BABYLON.Quaternion.Identity();

        return BABYLON.Matrix.Compose(scaling, rotation, position);
    }

    private _createCylinderInstanceMatrixData(transform: Data.GraphicData.CylinderInstanceTransform): Float32Array {
        const matrix = this._buildCylinderThinInstanceMatrix(transform);
        const matrixData = new Float32Array(16);
        matrix.copyToArray(matrixData, 0);
        return matrixData;
    }

    private _copyCylinderInstanceMatrixData(masterData: CylinderInstanceMasterData, matrixIndex: number): Float32Array | null {
        if (!masterData || !masterData.matrixBuffer || matrixIndex < 0) {
            return null;
        }

        const start = matrixIndex * 16;
        if (start + 16 > masterData.matrixBuffer.length) {
            return null;
        }

        const matrixData = masterData.matrixBuffer.slice(start, start + 16);
        return matrixData;
    }

    private _setCylinderInstanceObjectBounds(masterData: CylinderInstanceMasterData, objectId: string, minExtents: BABYLON.Vector3, maxExtents: BABYLON.Vector3): void {
        if (!masterData || !objectId || !minExtents || !maxExtents) {
            return;
        }

        masterData.objectBounds[objectId] = {
            minExtents: minExtents,
            maxExtents: maxExtents
        };
    }

    private _getCylinderInstanceObjectBounds(masterData: CylinderInstanceMasterData, objectId: string): CylinderInstanceObjectBounds | null {
        if (!masterData || !objectId) {
            return null;
        }

        const bounds = masterData.objectBounds[objectId];
        if (bounds) {
            return bounds;
        }

        const objectIndex = this.getObjectIndexByObjectId(masterData.masterMeshId, objectId);
        const objectInfos = this._masterMeshToObjectInfo[masterData.masterMeshId];

        if (!objectInfos || objectIndex < 0 || !objectInfos[objectIndex]) {
            return null;
        }

        return {
            minExtents: objectInfos[objectIndex].minExtents,
            maxExtents: objectInfos[objectIndex].maxExtents
        };
    }

    private _ensureCylinderInstanceMatrixCapacity(masterData: CylinderInstanceMasterData, requiredCount: number): boolean {
        const currentCapacity = masterData.matrixBuffer ? masterData.matrixBuffer.length / 16 : 0;
        if (currentCapacity >= requiredCount) {
            return false;
        }

        const newCapacity = Math.max(requiredCount, currentCapacity > 0 ? currentCapacity * 2 : 64);
        const newMatrixBuffer = new Float32Array(newCapacity * 16);

        if (masterData.matrixBuffer) {
            newMatrixBuffer.set(masterData.matrixBuffer);
        }

        masterData.matrixBuffer = newMatrixBuffer;

        if (masterData.sourceMesh) {
            masterData.sourceMesh.thinInstanceSetBuffer("matrix", newMatrixBuffer, 16, false);
            masterData.sourceMesh.thinInstanceCount = masterData.entries.objectIds.length;
        }

        return true;
    }

    private _appendCylinderInstanceEntries(masterData: CylinderInstanceMasterData, newEntries: PendingCylinderThinInstanceEntry[]): void {
        if (!newEntries || newEntries.length === 0) {
            return;
        }

        const startIndex = masterData.entries.objectIds.length;
        const reallocated = this._ensureCylinderInstanceMatrixCapacity(masterData, startIndex + newEntries.length);
        const objectInfos = this._masterMeshToObjectInfo[masterData.masterMeshId] || [];

        for (let i = 0; i < newEntries.length; i++) {
            const entry = newEntries[i];
            const entryIndex = startIndex + i;

            masterData.matrixBuffer.set(entry.matrixData, entryIndex * 16);
            masterData.entries.objectIds.push(entry.objectId);
            masterData.entries.pageKeys.push(this._getObjectBlockPageKey(entry.blockId, entry.pageId));

            if (masterData.objectEntryCounts[entry.objectId] == null) {
                masterData.objectEntryCounts[entry.objectId] = 0;

                const bounds = this._getCylinderInstanceObjectBounds(masterData, entry.objectId);
                if (bounds) {
                    const objectInfo = this._createCylinderInstanceObjectInfo(entry.objectId, bounds.minExtents, bounds.maxExtents, masterData.materialKey);
                    objectInfos.push(objectInfo);
                    this._recordMasterMeshObjectIndex(masterData.masterMeshId, entry.objectId, objectInfos.length - 1);
                }
            }

            masterData.objectEntryCounts[entry.objectId]++;
        }

        this._masterMeshToObjectInfo[masterData.masterMeshId] = objectInfos;

        this._syncCylinderInstanceMasterMaterial(masterData);

        if (masterData.sourceMesh) {
            this._applyCylinderMasterMeshSync(masterData, reallocated, true);
        }
    }

    private _mergeCylinderInstanceMasterEntries(destinationMasterData: CylinderInstanceMasterData, sourceMasterData: CylinderInstanceMasterData): void {
        if (!destinationMasterData || !sourceMasterData) {
            return;
        }

        const sourceEntryCount = sourceMasterData.entries.objectIds.length;
        if (sourceEntryCount === 0) {
            return;
        }

        const destinationStartIndex = destinationMasterData.entries.objectIds.length;
        const reallocated = this._ensureCylinderInstanceMatrixCapacity(destinationMasterData, destinationStartIndex + sourceEntryCount);
        const destinationObjectInfos = this._masterMeshToObjectInfo[destinationMasterData.masterMeshId] || [];
        const sourceObjectInfos = this._masterMeshToObjectInfo[sourceMasterData.masterMeshId] || [];

        if (destinationMasterData.matrixBuffer && sourceMasterData.matrixBuffer) {
            destinationMasterData.matrixBuffer.set(
                sourceMasterData.matrixBuffer.subarray(0, sourceEntryCount * 16),
                destinationStartIndex * 16
            );
        }

        for (let i = 0; i < sourceEntryCount; i++) {
            destinationMasterData.entries.objectIds.push(sourceMasterData.entries.objectIds[i]);
            destinationMasterData.entries.pageKeys.push(sourceMasterData.entries.pageKeys[i]);
        }

        for (let i = 0; i < sourceObjectInfos.length; i++) {
            const sourceObjectInfo = sourceObjectInfos[i];
            if (!sourceObjectInfo || !sourceObjectInfo.objectId) {
                continue;
            }

            if (destinationMasterData.objectEntryCounts[sourceObjectInfo.objectId] == null) {
                destinationObjectInfos.push(this._createCylinderInstanceObjectInfo(
                    sourceObjectInfo.objectId,
                    sourceObjectInfo.minExtents,
                    sourceObjectInfo.maxExtents,
                    destinationMasterData.materialKey
                ));
                this._recordMasterMeshObjectIndex(destinationMasterData.masterMeshId, sourceObjectInfo.objectId, destinationObjectInfos.length - 1);
            }
        }

        for (const objectId in sourceMasterData.objectEntryCounts) {
            destinationMasterData.objectEntryCounts[objectId] = (destinationMasterData.objectEntryCounts[objectId] || 0) + sourceMasterData.objectEntryCounts[objectId];
        }

        for (const objectId in sourceMasterData.objectBounds) {
            if (destinationMasterData.objectBounds[objectId] == null) {
                destinationMasterData.objectBounds[objectId] = sourceMasterData.objectBounds[objectId];
            }
        }

        this._masterMeshToObjectInfo[destinationMasterData.masterMeshId] = destinationObjectInfos;
        this._syncCylinderInstanceMasterMaterial(destinationMasterData);

        if (destinationMasterData.sourceMesh) {
            this._applyCylinderMasterMeshSync(destinationMasterData, reallocated, true);
        }
    }

    private _syncCylinderInstanceMaster(masterData: CylinderInstanceMasterData, previousEntryIndexes?: number[]): void {
        const entryCount = masterData.entries.objectIds.length;
        const matrixBuffer = entryCount > 0 ? new Float32Array(entryCount * 16) : null;
        const previousMatrixBuffer = masterData.matrixBuffer;
        const objectEntryCounts: { [objectId: string]: number; } = {};
        const objectBounds: { [objectId: string]: CylinderInstanceObjectBounds; } = {};
        const objectInfos: SubMeshObjectInfo[] = [];
        const seenObjectIds: { [objectId: string]: boolean; } = {};

        for (let i = 0; i < entryCount; i++) {
            const objectId = masterData.entries.objectIds[i];
            const previousEntryIndex = previousEntryIndexes && previousEntryIndexes.length > i ? previousEntryIndexes[i] : i;

            if (matrixBuffer && previousMatrixBuffer && previousEntryIndex >= 0) {
                const sourceOffset = previousEntryIndex * 16;
                if (sourceOffset + 16 <= previousMatrixBuffer.length) {
                    matrixBuffer.set(previousMatrixBuffer.subarray(sourceOffset, sourceOffset + 16), i * 16);
                }
            }

            objectEntryCounts[objectId] = (objectEntryCounts[objectId] || 0) + 1;

            if (!seenObjectIds[objectId]) {
                const bounds = this._getCylinderInstanceObjectBounds(masterData, objectId);
                if (bounds) {
                    objectBounds[objectId] = bounds;
                    objectInfos.push(this._createCylinderInstanceObjectInfo(objectId, bounds.minExtents, bounds.maxExtents, masterData.materialKey));
                }
                seenObjectIds[objectId] = true;
            }
        }

        masterData.objectEntryCounts = objectEntryCounts;
        masterData.objectBounds = objectBounds;
        masterData.matrixBuffer = matrixBuffer;
        this._masterMeshToObjectInfo[masterData.masterMeshId] = objectInfos;
        this._rebuildMasterMeshObjectIndex(masterData.masterMeshId);

        this._syncCylinderInstanceMasterMaterial(masterData);

        if (masterData.sourceMesh) {
            this._applyCylinderMasterMeshSync(masterData, true, entryCount > 0);
        }
    }

    private _upsertCylinderGroupObjectInfo(groupData: CylinderInstanceGroupData, objectId: string, minExtents: BABYLON.Vector3, maxExtents: BABYLON.Vector3): void {
        const objectIndex = this.getObjectIndexByObjectId(groupData.masterMeshId, objectId);

        if (objectIndex >= 0) {
            this._masterMeshToObjectInfo[groupData.masterMeshId][objectIndex].minExtents = minExtents;
            this._masterMeshToObjectInfo[groupData.masterMeshId][objectIndex].maxExtents = maxExtents;
            this._masterMeshToObjectInfo[groupData.masterMeshId][objectIndex].materialOverride = groupData.materialKey;
            return;
        }

        this._masterMeshToObjectInfo[groupData.masterMeshId].push(this._createCylinderInstanceObjectInfo(objectId, minExtents, maxExtents, groupData.materialKey));
        this._recordMasterMeshObjectIndex(groupData.masterMeshId, objectId, this._masterMeshToObjectInfo[groupData.masterMeshId].length - 1);
    }

    private _disposeCylinderInstanceBatch(masterMeshId: string): void {
        const masterData = this._getCylinderInstanceMaster(masterMeshId);
        if (!masterData) {
            return;
        }

        if (masterData.sourceMesh) {
            masterData.sourceMesh.thinInstanceSetBuffer("matrix", null, 16, false);
        }

        masterData.matrixBuffer = null;

        if (this._masterMeshes[masterMeshId]) {
            // Cylinder instance masters use shared managed materials; disposing the mesh must not dispose them.
            this._meshes.fastRemove(this._masterMeshes[masterMeshId]);
            this._masterMeshes[masterMeshId].dispose(false, false);
        }

        delete this._cylinderInstanceMasters[masterMeshId];
        delete this._masterMeshes[masterMeshId];
        delete this._masterMeshToObjectInfo[masterMeshId];
        delete this._masterMeshObjectIndex[masterMeshId];

        const keyIndex = this._masterMeshKeys.indexOf(masterMeshId);
        if (keyIndex !== -1) {
            this._masterMeshKeys.splice(keyIndex, 1);
        }
    }

    private _releaseUnusedCylinderInstanceBatch(masterMeshId: string): void {
        const masterData = this._getCylinderInstanceMaster(masterMeshId);
        if (!masterData) {
            return;
        }

        if (MeshManager.CLEANUP_UNUSED_CYLINDER_INSTANCE_MASTERS) {
            this._disposeCylinderInstanceBatch(masterMeshId);
            return;
        }

        masterData.entries = this._createEmptyCylinderInstanceEntries();
        this._syncCylinderInstanceMaster(masterData, []);
    }

    public beginCylinderInstanceDisposalBatch(): void {
        this._cylinderInstanceDisposalBatchDepth++;
    }

    public endCylinderInstanceDisposalBatch(): void {
        if (this._cylinderInstanceDisposalBatchDepth === 0) {
            return;
        }

        this._cylinderInstanceDisposalBatchDepth--;
        if (this._cylinderInstanceDisposalBatchDepth > 0) {
            return;
        }

        const removedPageKeys = this._pendingCylinderInstancePageDisposals;
        this._pendingCylinderInstancePageDisposals = {};

        if (Object.keys(removedPageKeys).length === 0) {
            return;
        }

        // A page/material group can become associated with more than one source
        // master after repeated material migrations and overflow allocation. The
        // group keeps only one sourceMasterKey, so page teardown must use the
        // authoritative page keys stored on every master rather than trusting
        // that single pointer.
        const sourceMasterKeys = Object.keys(this._cylinderInstanceMasters);
        for (let masterIndex = 0; masterIndex < sourceMasterKeys.length; masterIndex++) {
            const sourceMasterKey = sourceMasterKeys[masterIndex];
            const sourceMaster = this._getCylinderInstanceMaster(sourceMasterKey);
            if (!sourceMaster) {
                continue;
            }

            const retainedEntries = this._createEmptyCylinderInstanceEntries();
            const retainedIndexes: number[] = [];
            const removedObjectPages: { [objectPageKey: string]: boolean; } = {};

            for (let i = 0; i < sourceMaster.entries.objectIds.length; i++) {
                const entryPageKey = sourceMaster.entries.pageKeys[i];
                if (removedPageKeys[entryPageKey]) {
                    const objectId = sourceMaster.entries.objectIds[i];
                    const objectPageKey = `${objectId}|${entryPageKey}`;
                    if (!removedObjectPages[objectPageKey]) {
                        removedObjectPages[objectPageKey] = true;
                        const pageInfo = this._parseObjectBlockPageKey(entryPageKey);
                        this._removeObjectMasterForPage(
                            objectId,
                            pageInfo.blockId,
                            pageInfo.pageId,
                            sourceMaster.masterMeshId
                        );

                        if (!this._objectIdToMasterMeshIds[objectId]
                            || this._objectIdToMasterMeshIds[objectId].length === 0) {
                            this._objectToAppearanceOverridesList[objectId] = null;
                        }
                    }
                    continue;
                }

                retainedEntries.objectIds.push(sourceMaster.entries.objectIds[i]);
                retainedEntries.pageKeys.push(entryPageKey);
                retainedIndexes.push(i);
            }

            if (retainedIndexes.length === sourceMaster.entries.objectIds.length) {
                continue;
            }

            sourceMaster.entries = retainedEntries;
            if (sourceMaster.entries.objectIds.length === 0) {
                this._releaseUnusedCylinderInstanceBatch(sourceMaster.masterMeshId);
            } else {
                this._syncCylinderInstanceMaster(sourceMaster, retainedIndexes);
            }
        }
    }

    private _disposeCylinderInstanceMaster(masterMeshId: string): void {
        const masterData = this._getCylinderInstanceGroup(masterMeshId);
        if (!masterData) {
            return;
        }

        this._removePageMeshKey(masterData.blockId, masterData.pageId, masterMeshId);

        const objectIds = Object.keys(masterData.objectIds);
        const removedPageKey = this._getObjectBlockPageKey(masterData.blockId, masterData.pageId);
        this._pendingCylinderInstancePageDisposals[removedPageKey] = true;

        if (this._cylinderInstanceDisposalBatchDepth === 0) {
            // Reuse the batched implementation even for a single page so it
            // removes stale copies from every cylinder master consistently.
            this._cylinderInstanceDisposalBatchDepth = 1;
            this.endCylinderInstanceDisposalBatch();
        }

        for (let i = 0; i < objectIds.length; i++) {
            this._removeObjectMasterForPage(objectIds[i], masterData.blockId, masterData.pageId, masterData.sourceMasterKey);
        }

        delete this._cylinderInstanceGroups[masterMeshId];
        delete this._masterMeshToObjectInfo[masterMeshId];
        delete this._masterMeshObjectIndex[masterMeshId];
    }

    // Full-clear fast path. Avoid rebuilding source masters one group at a time when the
    // caller is about to wipe all mesh-manager state anyway.
    private _disposeAllCylinderInstanceMasters(): void {
        const cylinderGroupKeys = Object.keys(this._cylinderInstanceGroups);
        const cylinderMasterKeys = Object.keys(this._cylinderInstanceMasters);
        const cylinderMasterLookup: { [masterMeshId: string]: boolean; } = {};

        for (let i = 0; i < cylinderGroupKeys.length; i++) {
            const groupMeshId = cylinderGroupKeys[i];
            const groupData = this._cylinderInstanceGroups[groupMeshId];

            if (!groupData) {
                continue;
            }

            this._removePageMeshKey(groupData.blockId, groupData.pageId, groupMeshId);

            const objectIds = Object.keys(groupData.objectIds);
            for (let objectIndex = 0; objectIndex < objectIds.length; objectIndex++) {
                const objectId = objectIds[objectIndex];

                this._removeObjectMasterForPage(objectId, groupData.blockId, groupData.pageId, groupData.sourceMasterKey);

                if (!this._objectIdToMasterMeshIds[objectId] || this._objectIdToMasterMeshIds[objectId].length === 0) {
                    this._objectToAppearanceOverridesList[objectId] = null;
                }
            }

            delete this._cylinderInstanceGroups[groupMeshId];
            delete this._masterMeshToObjectInfo[groupMeshId];
            delete this._masterMeshObjectIndex[groupMeshId];
        }

        for (let i = 0; i < cylinderMasterKeys.length; i++) {
            const masterMeshId = cylinderMasterKeys[i];
            const masterData = this._cylinderInstanceMasters[masterMeshId];
            const masterMesh = this._masterMeshes[masterMeshId] || masterData?.sourceMesh;

            cylinderMasterLookup[masterMeshId] = true;

            if (masterData?.sourceMesh) {
                masterData.sourceMesh.thinInstanceSetBuffer("matrix", null, 16, false);
                masterData.matrixBuffer = null;
            }

            if (masterMesh) {
                this._meshes.fastRemove(masterMesh);
                masterMesh.dispose(false, false);
                delete this._masterMeshes[masterMeshId];
            }

            if (this._meshesToMerge[masterMeshId] && this._meshesToMerge[masterMeshId].length > 0) {
                for (let mergeIndex = 0; mergeIndex < this._meshesToMerge[masterMeshId].length; mergeIndex++) {
                    this._meshesToMerge[masterMeshId][mergeIndex].dispose();
                    this._meshesToMerge[masterMeshId][mergeIndex] = null;
                }

                this._meshesToMerge[masterMeshId] = null;
            }

            delete this._masterMeshToObjectInfo[masterMeshId];
            delete this._masterMeshObjectIndex[masterMeshId];
        }

        if (cylinderMasterKeys.length > 0) {
            this._masterMeshKeys = this._masterMeshKeys.filter((masterMeshId: string) => !cylinderMasterLookup[masterMeshId]);
        }

        this._cylinderInstanceMasters = {};
        this._cylinderInstanceGroups = {};
    }

    public disposeAllCylinderInstanceMasters(): void {
        this._disposeAllCylinderInstanceMasters();
    }

    // Fast-path disposal used when the caller already removed page entries from the source master
    // and updated object/page tracking. Avoids expensive source-master rebuild work.
    private _disposeCylinderInstanceGroupMetadataOnly(masterMeshId: string): void {
        const masterData = this._getCylinderInstanceGroup(masterMeshId);
        if (!masterData) {
            return;
        }

        this._removePageMeshKey(masterData.blockId, masterData.pageId, masterMeshId);

        delete this._cylinderInstanceGroups[masterMeshId];
        delete this._masterMeshToObjectInfo[masterMeshId];
        delete this._masterMeshObjectIndex[masterMeshId];
    }

    private _groupSourcePageHasEntries(groupData: CylinderInstanceGroupData): boolean {
        if (!groupData) {
            return false;
        }

        const sourceMaster = this._getCylinderInstanceMaster(groupData.sourceMasterKey);
        if (!sourceMaster) {
            return false;
        }

        const pageKey = this._getObjectBlockPageKey(groupData.blockId, groupData.pageId);
        for (let i = 0; i < sourceMaster.entries.pageKeys.length; i++) {
            if (sourceMaster.entries.pageKeys[i] === pageKey) {
                return true;
            }
        }

        return false;
    }

    private _repairCylinderGroupFromSourcePage(groupData: CylinderInstanceGroupData): void {
        if (!groupData) {
            return;
        }

        const sourceMaster = this._getCylinderInstanceMaster(groupData.sourceMasterKey);
        if (!sourceMaster) {
            return;
        }

        const pageKey = this._getObjectBlockPageKey(groupData.blockId, groupData.pageId);
        const repairedObjectIds: { [objectId: string]: boolean; } = {};
        const repairedInfos: SubMeshObjectInfo[] = [];
        const seen: { [objectId: string]: boolean; } = {};

        for (let i = 0; i < sourceMaster.entries.objectIds.length; i++) {
            if (sourceMaster.entries.pageKeys[i] !== pageKey) {
                continue;
            }

            const objectId = sourceMaster.entries.objectIds[i];
            repairedObjectIds[objectId] = true;

            if (seen[objectId]) {
                continue;
            }

            seen[objectId] = true;
            const bounds = this._getCylinderInstanceObjectBounds(sourceMaster, objectId);
            if (bounds) {
                repairedInfos.push(this._createCylinderInstanceObjectInfo(objectId, bounds.minExtents, bounds.maxExtents, groupData.materialKey));
            }
        }

        groupData.objectIds = repairedObjectIds;
        this._masterMeshToObjectInfo[groupData.masterMeshId] = repairedInfos;
        this._rebuildMasterMeshObjectIndex(groupData.masterMeshId);
    }

    private _getCylinderInstanceGroupsForSourceMaster(sourceMasterKey: string): CylinderInstanceGroupData[] {
        const groups: CylinderInstanceGroupData[] = [];

        for (const groupKey in this._cylinderInstanceGroups) {
            const groupData = this._cylinderInstanceGroups[groupKey];
            if (!groupData || groupData.sourceMasterKey !== sourceMasterKey) {
                continue;
            }

            groups.push(groupData);
        }

        return groups;
    }

    private _getCylinderInstanceGroupForSourceMasterPage(sourceMasterKey: string, blockId: string, pageId: string): CylinderInstanceGroupData | null {
        if (!sourceMasterKey || !blockId || !pageId) {
            return null;
        }

        for (const groupKey in this._cylinderInstanceGroups) {
            const groupData = this._cylinderInstanceGroups[groupKey];
            if (!groupData) {
                continue;
            }

            if (groupData.sourceMasterKey === sourceMasterKey && groupData.blockId === blockId && groupData.pageId === pageId) {
                return groupData;
            }
        }

        return null;
    }

    private _retagCylinderInstanceMasterMaterialOnly(masterData: CylinderInstanceMasterData, newMaterialId: string): void {
        if (!masterData || !newMaterialId) {
            return;
        }

        masterData.materialKey = newMaterialId;

        const objectInfos = this._masterMeshToObjectInfo[masterData.masterMeshId] || [];
        for (let i = 0; i < objectInfos.length; i++) {
            objectInfos[i].materialOverride = newMaterialId;
        }

        this._syncCylinderInstanceMasterMaterial(masterData);
    }

    private _isTransientCylinderInstanceMaterial(materialId: string | null | undefined): boolean {
        if (!materialId) {
            return false;
        }

        return materialId === "HidMat"
            || materialId === "SelMatOpaque"
            || materialId.indexOf("SelMatTransp") === 0
            || materialId.indexOf("XRayMaterial") === 0;
    }

    private _retagCylinderInstanceGroupMaterialOnly(groupData: CylinderInstanceGroupData, newMaterialId: string): void {
        if (!groupData || !newMaterialId) {
            return;
        }

        groupData.materialKey = newMaterialId;

        const groupObjectInfos = this._masterMeshToObjectInfo[groupData.masterMeshId] || [];
        for (let i = 0; i < groupObjectInfos.length; i++) {
            groupObjectInfos[i].materialOverride = newMaterialId;
        }
    }

    private _rekeyCylinderInstanceGroup(
        groupData: CylinderInstanceGroupData,
        sourceMasterData: CylinderInstanceMasterData,
        destinationMasterData: CylinderInstanceMasterData,
        sourceMasterMeshId: string,
        destinationMasterMeshId: string,
        newMaterialId: string): CylinderInstanceGroupData | null {
        if (!groupData) {
            return null;
        }

        const oldGroupMasterMeshId = groupData.masterMeshId;
        const targetGroupMasterMeshId = this._getCylinderInstanceGroupKey(groupData.blockId, groupData.pageId, newMaterialId, groupData.complexityLevel);
        const targetGroup = this._getCylinderInstanceGroup(targetGroupMasterMeshId);
        const groupObjectIds = Object.keys(groupData.objectIds);

        for (let i = 0; i < groupObjectIds.length; i++) {
            const objectId = groupObjectIds[i];
            const bounds = this._getCylinderInstanceObjectBounds(sourceMasterData, objectId);

            if (bounds) {
                this._setCylinderInstanceObjectBounds(destinationMasterData, objectId, bounds.minExtents, bounds.maxExtents);
            }

            this._removeObjectMasterForPage(objectId, groupData.blockId, groupData.pageId, sourceMasterMeshId);
            this._addObjectMasterForPage(objectId, groupData.blockId, groupData.pageId, destinationMasterMeshId);
        }

        if (targetGroup && targetGroup.masterMeshId !== oldGroupMasterMeshId) {
            for (let i = 0; i < groupObjectIds.length; i++) {
                const objectId = groupObjectIds[i];
                const bounds = this._getCylinderInstanceObjectBounds(sourceMasterData, objectId);

                targetGroup.objectIds[objectId] = true;

                if (bounds) {
                    this._upsertCylinderGroupObjectInfo(targetGroup, objectId, bounds.minExtents, bounds.maxExtents);
                }
            }

            targetGroup.sourceMasterKey = destinationMasterMeshId;
            targetGroup.materialKey = newMaterialId;

            this._removePageMeshKey(groupData.blockId, groupData.pageId, oldGroupMasterMeshId);
            delete this._cylinderInstanceGroups[oldGroupMasterMeshId];
            delete this._masterMeshToObjectInfo[oldGroupMasterMeshId];
            delete this._masterMeshObjectIndex[oldGroupMasterMeshId];
            return targetGroup;
        }

        if (oldGroupMasterMeshId !== targetGroupMasterMeshId) {
            delete this._cylinderInstanceGroups[oldGroupMasterMeshId];
            this._cylinderInstanceGroups[targetGroupMasterMeshId] = groupData;

            this._masterMeshToObjectInfo[targetGroupMasterMeshId] = this._masterMeshToObjectInfo[oldGroupMasterMeshId] || [];
            delete this._masterMeshToObjectInfo[oldGroupMasterMeshId];

            if (this._masterMeshObjectIndex[oldGroupMasterMeshId]) {
                this._masterMeshObjectIndex[targetGroupMasterMeshId] = this._masterMeshObjectIndex[oldGroupMasterMeshId];
                delete this._masterMeshObjectIndex[oldGroupMasterMeshId];
            }

            this._removePageMeshKey(groupData.blockId, groupData.pageId, oldGroupMasterMeshId);
            this._ensurePageMeshKey(groupData.blockId, groupData.pageId, targetGroupMasterMeshId);
        }

        groupData.masterMeshId = targetGroupMasterMeshId;
        groupData.sourceMasterKey = destinationMasterMeshId;
        groupData.materialKey = newMaterialId;

        const groupObjectInfos = this._masterMeshToObjectInfo[targetGroupMasterMeshId] || [];
        for (let i = 0; i < groupObjectInfos.length; i++) {
            groupObjectInfos[i].materialOverride = newMaterialId;
        }

        return groupData;
    }

    private _rekeyCylinderInstanceMaster(masterData: CylinderInstanceMasterData, newMaterialId: string): void {
        if (!masterData) {
            return;
        }

        const oldMasterMeshId = masterData.masterMeshId;
        const newMasterMeshId = this._getCylinderInstanceMasterKey(newMaterialId, masterData.complexityLevel);

        if (oldMasterMeshId === newMasterMeshId) {
            masterData.materialKey = newMaterialId;
            this._syncCylinderInstanceMasterMaterial(masterData);
            return;
        }

        delete this._cylinderInstanceMasters[oldMasterMeshId];
        this._cylinderInstanceMasters[newMasterMeshId] = masterData;

        if (this._masterMeshes[oldMasterMeshId]) {
            this._masterMeshes[newMasterMeshId] = this._masterMeshes[oldMasterMeshId];
            delete this._masterMeshes[oldMasterMeshId];

            this._masterMeshes[newMasterMeshId].id = newMasterMeshId;
            this._masterMeshes[newMasterMeshId].name = newMasterMeshId;
        }

        this._masterMeshToObjectInfo[newMasterMeshId] = this._masterMeshToObjectInfo[oldMasterMeshId] || [];
        delete this._masterMeshToObjectInfo[oldMasterMeshId];

        if (this._masterMeshObjectIndex[oldMasterMeshId]) {
            this._masterMeshObjectIndex[newMasterMeshId] = this._masterMeshObjectIndex[oldMasterMeshId];
            delete this._masterMeshObjectIndex[oldMasterMeshId];
        }

        const keyIndex = this._masterMeshKeys.indexOf(oldMasterMeshId);
        if (keyIndex !== -1) {
            this._masterMeshKeys[keyIndex] = newMasterMeshId;
        }

        masterData.masterMeshId = newMasterMeshId;
        masterData.materialKey = newMaterialId;

        const objectInfos = this._masterMeshToObjectInfo[newMasterMeshId] || [];
        for (let i = 0; i < objectInfos.length; i++) {
            objectInfos[i].materialOverride = newMaterialId;
        }

        this._syncCylinderInstanceMasterMaterial(masterData);
        this._rebuildMasterMeshObjectIndex(newMasterMeshId);
    }

    private _createPendingCylinderInstanceEntriesForMaster(masterData: CylinderInstanceMasterData): PendingCylinderThinInstanceEntry[] {
        const pendingEntries: PendingCylinderThinInstanceEntry[] = [];

        if (!masterData) {
            return pendingEntries;
        }

        for (let i = 0; i < masterData.entries.objectIds.length; i++) {
            const matrixData = this._copyCylinderInstanceMatrixData(masterData, i);
            if (!matrixData) {
                continue;
            }

            const pageInfo = this._parseObjectBlockPageKey(masterData.entries.pageKeys[i]);
            pendingEntries.push({
                objectId: masterData.entries.objectIds[i],
                blockId: pageInfo.blockId,
                pageId: pageInfo.pageId,
                matrixData: matrixData
            });
        }

        return pendingEntries;
    }

    private _changeEntireCylinderInstanceMasterMaterial(masterMeshId: string, newMaterialId: string, debugObjectIds?: string[]): boolean {
        const sourceMasterData = this._getCylinderInstanceMaster(masterMeshId);
        if (!sourceMasterData || !newMaterialId || sourceMasterData.materialKey === newMaterialId) {
            return false;
        }

        const sourceMasterMeshId = sourceMasterData.masterMeshId;
        const destinationMasterMeshId = this._getCylinderInstanceMasterKey(newMaterialId, sourceMasterData.complexityLevel);
        const existingDestinationMaster = this._getCylinderInstanceMaster(destinationMasterMeshId);
        const destinationMasterData = existingDestinationMaster || sourceMasterData;
        const sourceGroups = this._getCylinderInstanceGroupsForSourceMaster(sourceMasterMeshId);
        const touchedGroups: { [masterMeshId: string]: CylinderInstanceGroupData; } = {};

        const shouldKeepMasterIds = MeshManager.CYLINDER_INSTANCE_KEEP_MASTERS_ON_WHOLE_SWAP
            && !(this._isTransientCylinderInstanceMaterial(sourceMasterData.materialKey)
                && !this._isTransientCylinderInstanceMaterial(newMaterialId));

        if (shouldKeepMasterIds) {
            for (let i = 0; i < sourceGroups.length; i++) {
                this._retagCylinderInstanceGroupMaterialOnly(sourceGroups[i], newMaterialId);
            }

            this._retagCylinderInstanceMasterMaterialOnly(sourceMasterData, newMaterialId);

            return true;
        }

        for (let i = 0; i < sourceGroups.length; i++) {
            const touchedGroup = this._rekeyCylinderInstanceGroup(
                sourceGroups[i],
                sourceMasterData,
                destinationMasterData,
                sourceMasterMeshId,
                destinationMasterMeshId,
                newMaterialId
            );

            if (touchedGroup) {
                touchedGroups[touchedGroup.masterMeshId] = touchedGroup;
            }
        }

        if (!existingDestinationMaster) {
            this._rekeyCylinderInstanceMaster(sourceMasterData, newMaterialId);

            const touchedGroupKeys = Object.keys(touchedGroups);
            for (let i = 0; i < touchedGroupKeys.length; i++) {
                this._repairCylinderGroupFromSourcePage(touchedGroups[touchedGroupKeys[i]]);
            }

            return true;
        }

        this._mergeCylinderInstanceMasterEntries(existingDestinationMaster, sourceMasterData);
        this._releaseUnusedCylinderInstanceBatch(sourceMasterMeshId);

        const touchedGroupKeys = Object.keys(touchedGroups);
        for (let i = 0; i < touchedGroupKeys.length; i++) {
            this._repairCylinderGroupFromSourcePage(touchedGroups[touchedGroupKeys[i]]);
        }

        return true;
    }

    private _isWholeCylinderInstanceMasterChange(masterData: CylinderInstanceMasterData, objectIds: string[]): boolean {
        if (!masterData || !objectIds || objectIds.length === 0) {
            return false;
        }

        const trackedLookup: { [objectId: string]: boolean; } = {};
        for (let i = 0; i < masterData.entries.objectIds.length; i++) {
            const objectId = masterData.entries.objectIds[i];
            if (objectId) {
                trackedLookup[objectId] = true;
            }
        }

        const trackedObjectIds = Object.keys(trackedLookup);
        if (trackedObjectIds.length === 0 || objectIds.length < trackedObjectIds.length) {
            return false;
        }

        const seen: { [objectId: string]: boolean; } = {};
        let matchedCount = 0;

        for (let i = 0; i < objectIds.length; i++) {
            const objectId = objectIds[i];
            if (!objectId || seen[objectId] || !trackedLookup[objectId]) {
                continue;
            }

            seen[objectId] = true;
            matchedCount++;

            if (matchedCount === trackedObjectIds.length) {
                return true;
            }
        }

        return false;
    }

    private _estimateCylinderInstanceMemory(
        matrixBytes: number,
        instanceCount: number,
        masterObjectCount: number,
        groupObjectCount: number,
        masterCount: number,
        groupCount: number): number {
        const entryBytes = instanceCount * MeshManager.CYLINDER_INSTANCE_ENTRY_BYTES;
        const masterObjectBytes = masterObjectCount * MeshManager.CYLINDER_INSTANCE_MASTER_OBJECT_BYTES;
        const groupObjectBytes = groupObjectCount * MeshManager.CYLINDER_INSTANCE_GROUP_OBJECT_BYTES;
        const masterBytes = masterCount * MeshManager.CYLINDER_INSTANCE_MASTER_BYTES;
        const groupBytes = groupCount * MeshManager.CYLINDER_INSTANCE_GROUP_BYTES;

        return this._toMegabytes(matrixBytes + entryBytes + masterObjectBytes + groupObjectBytes + masterBytes + groupBytes);
    }

    private _changeCylinderInstanceObjectsMaterial(masterMeshId: string, objectIds: string[], newMaterialId: string): void {
        const masterData = this._getCylinderInstanceMaster(masterMeshId);
        if (!masterData || !objectIds || objectIds.length === 0) {
            return;
        }

        if (masterData.materialKey === newMaterialId) {
            const objectIdLookup: { [objectId: string]: boolean; } = {};
            let needsTrackingRepair = false;

            for (let i = 0; i < objectIds.length; i++) {
                const objectId = objectIds[i];
                objectIdLookup[objectId] = true;

                if (!masterData.objectEntryCounts[objectId]
                    || !this._objectIdToMasterMeshIds[objectId]
                    || this._objectIdToMasterMeshIds[objectId].indexOf(masterMeshId) === -1) {
                    needsTrackingRepair = true;
                }
            }

            if (needsTrackingRepair) {
                this._syncCylinderInstanceMaster(masterData);

                for (let i = 0; i < masterData.entries.objectIds.length; i++) {
                    const objectId = masterData.entries.objectIds[i];
                    if (!objectIdLookup[objectId]) {
                        continue;
                    }

                    const pageInfo = this._parseObjectBlockPageKey(masterData.entries.pageKeys[i]);
                    this._addObjectMasterForPage(objectId, pageInfo.blockId, pageInfo.pageId, masterMeshId);
                }
            }

            const objectInfos = this._masterMeshToObjectInfo[masterMeshId] || [];
            for (let i = 0; i < objectIds.length; i++) {
                const objectIndex = this.getObjectIndexByObjectId(masterMeshId, objectIds[i]);
                if (objectIndex >= 0) {
                    objectInfos[objectIndex].materialOverride = newMaterialId;
                }
            }

            return;
        }

        // Not using the change all branch here. It is untested, but might be better for
        // performance.
        /*
        var isWholeMasterChange = this._isWholeCylinderInstanceMasterChange(masterData, objectIds);

        isWholeMasterChange = false;
        if (isWholeMasterChange) {

            this._changeEntireCylinderInstanceMasterMaterial(masterMeshId, newMaterialId, objectIds);
            return;
        }
        */
        const objectIdLookup: { [objectId: string]: boolean; } = {};
        const destinationMaster = this._getOrCreateCylinderInstanceMaster(newMaterialId, masterData.complexityLevel);
        // Dedup key is (objectId, pageKey) — an object can span multiple pages so each page needs its own tracking update.
        const movedPageObjectKeys: { [key: string]: boolean; } = {};
        // Per-objectId dedup for master-level operations (bounds) that should happen only once regardless of page count.
        const movedObjectIds: { [objectId: string]: boolean; } = {};
        const retainedEntries = this._createEmptyCylinderInstanceEntries();
        const retainedIndexes: number[] = [];
        const pendingEntriesByMaster: { [masterMeshId: string]: PendingCylinderThinInstanceEntry[]; } = {};
        const touchedMasters: { [masterMeshId: string]: CylinderInstanceMasterData; } = {};
        // Track which objectIds were removed from each source group, to batch-rebuild their objectInfos once.
        const removedFromGroup: { [groupMeshId: string]: { [objectId: string]: boolean; }; } = {};
        const touchedGroups: { [masterMeshId: string]: CylinderInstanceGroupData; } = {};

        for (let i = 0; i < objectIds.length; i++) {
            objectIdLookup[objectIds[i]] = true;
        }

        // Build a fast lookup: pageKey -> group, so we don't scan all groups per entry.
        const sourceGroupByPageKey: { [pageKey: string]: CylinderInstanceGroupData | null; } = {};
        const destinationGroupByPageKey: { [pageKey: string]: CylinderInstanceGroupData; } = {};
        const pageInfoByPageKey: { [pageKey: string]: { blockId: string; pageId: string; }; } = {};

        for (let i = 0; i < masterData.entries.objectIds.length; i++) {
            const objectId = masterData.entries.objectIds[i];
            const pageKey = masterData.entries.pageKeys[i];

            if (!objectIdLookup[objectId]) {
                retainedEntries.objectIds.push(objectId);
                retainedEntries.pageKeys.push(pageKey);
                retainedIndexes.push(i);
                continue;
            }

            let pageInfo = pageInfoByPageKey[pageKey];
            if (!pageInfo) {
                pageInfo = this._parseObjectBlockPageKey(pageKey);
                pageInfoByPageKey[pageKey] = pageInfo;
            }

            // Resolve source group once per (master, page) combination using a local cache.
            if (!(pageKey in sourceGroupByPageKey)) {
                let sourceGroup = this._getCylinderInstanceGroup(
                    this._getCylinderInstanceGroupKey(pageInfo.blockId, pageInfo.pageId, masterData.materialKey, masterData.complexityLevel));

                if (!sourceGroup) {
                    const matchingSourceGroup = this._getCylinderInstanceGroupForSourceMasterPage(
                        masterData.masterMeshId,
                        pageInfo.blockId,
                        pageInfo.pageId);
                    if (matchingSourceGroup) {
                        sourceGroup = matchingSourceGroup;
                    }
                }

                if (sourceGroup && sourceGroup.sourceMasterKey !== masterData.masterMeshId) {
                    sourceGroup.sourceMasterKey = masterData.masterMeshId;
                }

                sourceGroupByPageKey[pageKey] = sourceGroup;
            }
            const sourceGroup = sourceGroupByPageKey[pageKey];
            if (sourceGroup) {
                touchedGroups[sourceGroup.masterMeshId] = sourceGroup;
            }

            let destinationGroup = destinationGroupByPageKey[pageKey];
            if (!destinationGroup) {
                destinationGroup = this._getOrCreateCylinderInstanceGroup(pageInfo.blockId, pageInfo.pageId, newMaterialId, masterData.complexityLevel);
                if (destinationGroup.sourceMasterKey !== destinationMaster.masterMeshId) {
                    destinationGroup.sourceMasterKey = destinationMaster.masterMeshId;
                }
                destinationGroupByPageKey[pageKey] = destinationGroup;
            }
            const sourceOffset = i * 16;
            const matrixData = masterData.matrixBuffer && sourceOffset + 16 <= masterData.matrixBuffer.length
                ? masterData.matrixBuffer.subarray(sourceOffset, sourceOffset + 16)
                : null;

            const movedPageObjectKey = objectId + "|" + pageKey;
            if (!movedPageObjectKeys[movedPageObjectKey]) {
                movedPageObjectKeys[movedPageObjectKey] = true;

                // Per-page operations: update page-level tracking and destination group membership.
                destinationGroup.objectIds[objectId] = true;

                if (sourceGroup) {
                    delete sourceGroup.objectIds[objectId];
                    // Defer objectInfo removal — batch-rebuild the source group index at the end.
                    if (!removedFromGroup[sourceGroup.masterMeshId]) {
                        removedFromGroup[sourceGroup.masterMeshId] = {};
                    }
                    removedFromGroup[sourceGroup.masterMeshId][objectId] = true;
                }

                this._removeObjectMasterForPage(objectId, pageInfo.blockId, pageInfo.pageId, masterData.masterMeshId);

                this._addObjectMasterForPage(objectId, pageInfo.blockId, pageInfo.pageId, destinationMaster.masterMeshId);

                const destinationGroupBounds = this._getCylinderInstanceObjectBounds(masterData, objectId);
                if (destinationGroupBounds) {
                    this._upsertCylinderGroupObjectInfo(
                        destinationGroup,
                        objectId,
                        destinationGroupBounds.minExtents,
                        destinationGroupBounds.maxExtents
                    );
                }
            }

            // Per-objectId operations: bounds and destination group objectInfo — only needs to happen once.
            if (!movedObjectIds[objectId]) {
                movedObjectIds[objectId] = true;

                const bounds = this._getCylinderInstanceObjectBounds(masterData, objectId);
                if (bounds) {
                    this._setCylinderInstanceObjectBounds(destinationMaster, objectId, bounds.minExtents, bounds.maxExtents);
                }
            }

            if (matrixData) {
                if (!pendingEntriesByMaster[destinationMaster.masterMeshId]) {
                    pendingEntriesByMaster[destinationMaster.masterMeshId] = [];
                }

                pendingEntriesByMaster[destinationMaster.masterMeshId].push({
                    objectId: objectId,
                    blockId: pageInfo.blockId,
                    pageId: pageInfo.pageId,
                    matrixData: matrixData
                });
            }

            touchedMasters[destinationMaster.masterMeshId] = destinationMaster;
        }
        // Batch-remove objectInfos from each source group and rebuild its index once.
        for (const groupMeshId in removedFromGroup) {
            const removedIds = removedFromGroup[groupMeshId];
            const groupObjectInfos = this._masterMeshToObjectInfo[groupMeshId];
            if (groupObjectInfos) {
                const filtered = groupObjectInfos.filter(info => !removedIds[info.objectId]);
                this._masterMeshToObjectInfo[groupMeshId] = filtered;
                this._rebuildMasterMeshObjectIndex(groupMeshId);
            }
        }

        masterData.entries = retainedEntries;

        this._syncCylinderInstanceMaster(masterData, retainedIndexes);

        if (masterData.entries.objectIds.length === 0) {
            this._releaseUnusedCylinderInstanceBatch(masterMeshId);
        }

        const touchedKeys = Object.keys(touchedMasters);
        for (let i = 0; i < touchedKeys.length; i++) {
            const masterMeshId = touchedKeys[i];
            this._appendCylinderInstanceEntries(touchedMasters[masterMeshId], pendingEntriesByMaster[masterMeshId] || []);
        }

        const touchedGroupKeys = Object.keys(touchedGroups);
        for (let i = 0; i < touchedGroupKeys.length; i++) {
            this._repairCylinderGroupFromSourcePage(touchedGroups[touchedGroupKeys[i]]);
        }
    }
    clearMesh(masterMeshId: string) {

        if (this._isCylinderInstanceMaster(masterMeshId)) {
            this._disposeCylinderInstanceMaster(masterMeshId);
            return;
        }

        var objectInfos = this._masterMeshToObjectInfo[masterMeshId];

        if (objectInfos) {

            for (var x = 0; x < objectInfos.length; x++) {
                const objectId = objectInfos[x].objectId;
                // special-case: treat as pageId="0", blockId="0"
                this._removeObjectMastersForPage(objectId, "0", "0");
            }
        }

        delete this._masterMeshToObjectInfo[masterMeshId];
        delete this._masterMeshObjectIndex[masterMeshId];
        if (this._meshesToMerge[masterMeshId] && this._meshesToMerge[masterMeshId].length > 0) {

            for (var x = 0; x < this._meshesToMerge[masterMeshId].length; x++) {
                this._meshesToMerge[masterMeshId][x].dispose();
                this._meshesToMerge[masterMeshId][x] = null;
            }

            delete this._meshesToMerge[masterMeshId];
        }

        delete this._masterMeshToObjectInfo[masterMeshId];
        var key = this._masterMeshKeys.indexOf(masterMeshId);
        if (key >= 0) {
            this._masterMeshKeys.splice(key, 1);
        }

        this._babScene.removeMesh(this._babScene.getMeshByID(masterMeshId));

        Object.keys(this._masterMeshToObjectInfo).splice(Object.keys(this._masterMeshToObjectInfo).indexOf(masterMeshId), 1);
        Object.keys(this._masterMeshes).splice(Object.keys(this._masterMeshes).indexOf(masterMeshId), 1);

        if (this._masterMeshes[masterMeshId]) {
            this._masterMeshes[masterMeshId].dispose();
            delete this._masterMeshes[masterMeshId];
        }
    }

    public getPointClouds() {

        return Object.values(this._masterPointClouds);
    }

    getOverrideMaterialOfObject(objectId: string): string[] {

        // This now can point to n master keys
        var masterKeys = this._getCurrentObjectMasterKeys(objectId);
        var retVal: string[] = [];

        if (masterKeys == null || masterKeys.length === 0)
            return;

        // We make the assumption that all master mesh info parts of the same object id will "point" to th same material override.
        // Therefore, we only need to check the first one!
        for (var z = 0; z < masterKeys.length; z++) {
            var masterKey = masterKeys[z];
            const masterMesh = this._masterMeshes[masterKey];
            const cylinderMaster = this._cylinderInstanceMasters[masterKey];

            if (cylinderMaster) {
                retVal.push(cylinderMaster.materialKey || masterKey);
                continue;
            }

            const fallbackMaterialId = (masterMesh && masterMesh.material)
                ? ((masterMesh.material.id || masterMesh.material.name) as string)
                : cylinderMaster?.materialKey || null;

            var indexOfObject = this.getObjectIndexByObjectId(masterKey, objectId);
            var objectInfosOfMaster = this._masterMeshToObjectInfo[masterKey];

            if (indexOfObject >= 0) {
                if (objectInfosOfMaster[indexOfObject].materialOverride != "" && objectInfosOfMaster[indexOfObject].materialOverride != null) {
                    retVal.push(objectInfosOfMaster[indexOfObject].materialOverride);
                }
                else {
                    retVal.push(fallbackMaterialId || masterKey);
                }
            }
            else {
                retVal.push(fallbackMaterialId || masterKey);
            }
        }

        return retVal;
    }
    getAllMeshes(): VersionedArray<BABYLON.AbstractMesh> {
        return this._meshes;
    }
    getMasterKeysOfObject(objectId: string): string[] {
        return this._getCurrentObjectMasterKeys(objectId);
    }

    changeAllMaterialToNewMaterial(masterMeshId: string, newMaterialId: string) {
        var objectInfosOfMaster = this._masterMeshToObjectInfo[masterMeshId];
        var objectIdsToChange: string[] = [];

        for (var i = 0; i < objectInfosOfMaster.length; i++) {
            objectIdsToChange.push(objectInfosOfMaster[i].objectId);
        }

        this.changeGraphicObjectsMaterial(masterMeshId, objectIdsToChange, newMaterialId, false);
    }

    setObjectsToMaintainHidden(objectIds: any[][]) {

        for (var x = 0; x < objectIds.length; x++) {
            var ids = objectIds[x];

            for (var y = 0; y < ids.length; y++) {

                if (this._hiddenAssets[ids[y].objectId]) {

                    this._pendingHiddenAssets[ids[y].objectId] = true;
                }
            }
        }
    }

    maintainHidden(meshId: any) {

        if (this._pendingHiddenAssets[meshId]) {

            this.hideObjects([meshId], false, false);
            delete this._pendingHiddenAssets[meshId];
        }
    }

    getHiddenAssetIds(): string[] {

        return Object.keys(this._hiddenAssets);
    }

    getLoadedAssetIds(): string[] {
        return Object.keys(this._objectIdToMasterMeshIds);
    }

    setAssetsUnhiddenCallback(callback: (assetIds: string[]) => void): void {
        this._assetsUnhiddenCallback = callback;
    }

    unhideAllAssets(fireEvents: boolean) {
        this.unhideObjects(Object.keys(this._hiddenAssets), fireEvents);
    }

    setPointShadeType(shadeMode: Enumerations.PointShadeMode) {
        switch (shadeMode) {
            case Enumerations.PointShadeMode.SPLAT:
                {
                    this.enablePointCollisionMaterial(false);
                    this.enablePointXRayMaterial(false);
                    break;
                }
            case Enumerations.PointShadeMode.XRAY:
                {
                    this.enablePointCollisionMaterial(false);
                    this.enablePointXRayMaterial(true);
                    break;
                }
            case Enumerations.PointShadeMode.COLLISION:
                {
                    this.enablePointCollisionMaterial(true);
                    this.enablePointXRayMaterial(false);
                    break;
                }
            default:
                break;
        }

        this._babScene.render();
    }
    enablePointCollisionMaterial(enabled: boolean) {
        this._materialManager.setPointCloudMeshOcclusionEnabled(enabled);
    }
    enablePointXRayMaterial(enabled: boolean) {
        if (this._pointXrayEnabled === enabled) return;

        this._pointXrayEnabled = enabled;

        for (const key in this._masterPointClouds) {
            const mesh = this._masterPointClouds[key];
            if (!mesh || mesh.isDisposed()) continue;

            const resolution = mesh.metadata?.voxelMM;
            if (!resolution) continue;

            let newMat: BABYLON.Material;

            if (enabled) {
                newMat = this._materialManager.getPointCloudMaterial();
            } else {

                newMat = this._materialManager.getPointCloudSplatMaterialForResolutionMM(resolution);
                this._materialManager.assignPointCloudMaterial(mesh, resolution);
            }

            mesh.material = newMat;
        }
    }
    unhideObjects(assets: string[], fireEvents: boolean, fireBlockEvents: boolean = true) {

        if (fireBlockEvents) {
            this._evenHandler.fireEvent(Enumerations.EventType.STARTBLOCKINGOPERATION);
        }

        var objectsToUnselect: { [realMaterial: string]: any; } = {};
        var revertMaterialKeys: any = {};
        const unhiddenAssetIds: string[] = [];

        // Need to collect which meshes will be affected by the unselect, and which assets correspond to each mesh. Furthermore, we need to get the
        // material to which each asset will be reverted. The results are organized based on mesh id to be unselected, and then organized again
        // based on the material to which to revert to.
        for (var i = 0; i < assets.length; i++) {
            if (this._hiddenAssets[assets[i]]) {

                unhiddenAssetIds.push(assets[i]);

                var masterMeshIds = this._hiddenAssets[assets[i]];

                for (var z = 0; z < masterMeshIds.length; z++) {

                    var realMaterial = this._hiddenOriginalMaterials[assets[i]][z];

                    // Hiding a cylinder-instance object moves its entries into
                    // the master keyed by the hidden material, so the master
                    // captured at hide time may no longer hold the object.
                    // Revert from the master that currently does.
                    var masterMeshId = this._resolveCurrentHiddenMasterForObject(
                        assets[i],
                        masterMeshIds[z],
                        realMaterial
                    );

                    if (!objectsToUnselect[masterMeshId]) {
                        objectsToUnselect[masterMeshId] = new Array();
                    }

                    // Assets should be selected based on the result of the mesh manager's change of material.

                    if (!objectsToUnselect[masterMeshId][realMaterial]) {
                        objectsToUnselect[masterMeshId][realMaterial] = new Array();
                    }

                    objectsToUnselect[masterMeshId][realMaterial].push(assets[i]);
                }

                delete this._hiddenOriginalMaterials[assets[i]];
                delete this._hiddenAssets[assets[i]];
            }

            delete this._pendingHiddenAssets[assets[i]];
        }

        for (var key in objectsToUnselect) {

            var newMaterials = objectsToUnselect[key];

            // This COULD be done with fewer calls to change materials if this method allowed for a collection of new materials to be provided. This would
            // then require a map to be made between object ids and new material ids. This may stand as a "TO DO".
            for (var newMaterial in newMaterials) {
                this.changeGraphicObjectsMaterial(key, objectsToUnselect[key][newMaterial], newMaterial, false);
            }
        }

        if (unhiddenAssetIds.length > 0 && this._assetsUnhiddenCallback) {
            this._assetsUnhiddenCallback(unhiddenAssetIds);
        }

        if (fireBlockEvents) {
            this._evenHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
        }
    }

    hideObjects(assets: string[], fireEvents: boolean, fireBlockEvents: boolean = true) {

        if (fireBlockEvents) {
            this._evenHandler.fireEvent(Enumerations.EventType.STARTBLOCKINGOPERATION);
        }

        // We need to organize the meshes that will be affected by the selection, and which asset ids belong to each affected mesh...
        var objectsToHide: { [realMaterial: string]: any; } = {};

        var currentMaterialsToChange: string[] = [];

        {
            for (var i = 0; i < assets.length; i++) {
                if (!this._hiddenAssets[assets[i]] || this._hiddenAssets[assets[i]].length == 0 || this._pendingHiddenAssets[assets[i]]) {

                    if (this._pendingHiddenAssets[assets[i]]) {
                        delete this._hiddenAssets[assets[i]];
                    }

                    var masterMeshIds = this.getMasterKeysOfObject(assets[i]);
                    var revertMaterial = this.getOverrideMaterialOfObject(assets[i]);


                    if (revertMaterial == null || masterMeshIds == null) {
                        this._pendingHiddenAssets[assets[i]] = true;
                        continue;
                    }

                    this._hiddenOriginalMaterials[assets[i]] = revertMaterial;

                    for (var z = 0; z < masterMeshIds.length; z++) {

                        var masterMeshId = masterMeshIds[z];

                        var selectionMaterialName = this._materialManager.getHiddenMaterialName(revertMaterial[z]);

                        if (masterMeshId == null) {

                            continue;
                        }

                        if (!objectsToHide[masterMeshId]) {
                            objectsToHide[masterMeshId] = new Array();
                        }

                        if (!objectsToHide[masterMeshId][selectionMaterialName]) {
                            objectsToHide[masterMeshId][selectionMaterialName] = new Array();
                        }

                        objectsToHide[masterMeshId][selectionMaterialName].push(assets[i]);

                        if (this._hiddenAssets[assets[i]] == null) {
                            this._hiddenAssets[assets[i]] = [];
                        }

                        this._hiddenAssets[assets[i]].push(masterMeshId);
                    }

                    //this._numSelected++;
                }
            }

            for (var key in objectsToHide) {

                var newMaterials = objectsToHide[key];

                // This COULD be done with fewer calls to change materials if this method allowed for a collection of new materials to be provided. This would
                // then require a map to be made between object ids and new material ids. This may stand as a "TO DO".
                for (var newMaterial in newMaterials) {
                    // The new material is likely only the selection material, but it IS possible that we have multiple selection materials with different alphas.
                    this.changeGraphicObjectsMaterial(key, objectsToHide[key][newMaterial], newMaterial, false);
                }
            }
        }

        if (fireBlockEvents) {
            this._evenHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
        }
    }


    // BE ADVISED, this is really a turn xray on/off. If you want this to work on any set of assets for any color, use the asset set manager
    // to do that. This might as well be called "change xray mode".
    changeAppearanceOverrides(
        namedLists: any[],
        activateDefaultList: boolean,
        activateXRay: boolean,
        overrideManager: Interfaces.Interfaces.IMaterialOverrideManager
    ) {
        const objectKeys = Object.keys(this._objectToAppearanceOverridesList || {});
        if (!objectKeys.length) {
            return;
        }

        const om = overrideManager;
        const matMgr = this._materialManager;

        const xrayMatName = "XRayMaterial";

        if (!om) return;

        const canBatch = typeof om.beginBatch === "function" && typeof om.endBatch === "function";

        if (canBatch) om.beginBatch();
        try {
            for (let i = 0; i < objectKeys.length; i++) {
                const objectKey = objectKeys[i];

                // Ensure base information is seeded (master meshes + base materials)
                if (!om.ensureBaseInfo(objectKey)) {
                    continue;
                }

                if (activateXRay) {
                    const baseMaterials = om.getBaseMaterials(objectKey);
                    const masterMeshIds = om.getMasterMeshIds(objectKey);

                    if (!baseMaterials || !masterMeshIds || baseMaterials.length === 0) {
                        continue;
                    }

                    const overrideMaterials = new Array(masterMeshIds.length).fill(xrayMatName);

                    om.setOverrideForSource(
                        objectKey,
                        MeshManager.XRAY_LAYER_SOURCE,
                        MeshManager.XRAY_LAYER_PRIORITY,
                        overrideMaterials
                    );
                } else {
                    om.clearOverrideForSource(objectKey, MeshManager.XRAY_LAYER_SOURCE);
                }
            }
        } finally {
            if (canBatch) om.endBatch();
        }
    }


    isAssetHidden(assetId: string): boolean {
        return (this._hiddenAssets[assetId] != null && this._hiddenAssets[assetId].length > 0);
    }

    // Cylinder-instance hides move an object's thin instances into the master
    // keyed by the hidden material. Given the master captured at hide time and
    // the material to revert to, return the master that currently holds the
    // object's entries so the revert move starts from the right place.
    private _resolveCurrentHiddenMasterForObject(
        objectId: string,
        capturedMasterMeshId: string,
        revertMaterialId: string
    ): string {
        // The source batch can be disposed after its last entries move to the
        // hidden-material batch. Recognize its stable key even when it is no
        // longer present in _cylinderInstanceMasters.
        if (!this._isCylinderInstanceBatchKey(capturedMasterMeshId)) {
            return capturedMasterMeshId;
        }

        const capturedMaster = this._getCylinderInstanceMaster(capturedMasterMeshId);
        if (capturedMaster && capturedMaster.objectEntryCounts[objectId]) {
            return capturedMasterMeshId;
        }

        const hiddenMaterialName = this._materialManager.getHiddenMaterialName(revertMaterialId);
        for (const masterKey in this._cylinderInstanceMasters) {
            const masterData = this._cylinderInstanceMasters[masterKey];
            if (masterData &&
                masterData.materialKey === hiddenMaterialName &&
                masterData.objectEntryCounts[objectId]) {
                return masterKey;
            }
        }

        return capturedMasterMeshId;
    }

    changeGLBObjectsMaterial(masterMeshId: string, objectIds: string[], newMaterialId: string, enableNotifications: boolean = true, baseMaterial: boolean = false) {

        if (enableNotifications) {
            this._evenHandler.fireEvent(Enumerations.EventType.STARTBLOCKINGOPERATION);
        }

        if (this._masterMeshes[masterMeshId] == null) {
            return;
        }

        var mesh = this._masterMeshes[masterMeshId];
        var newMaterial = this._materialManager.getMaterial(newMaterialId);

        if (newMaterial.name.toLocaleLowerCase().startsWith("sel") || newMaterial.name.indexOf("XRayMaterial") !== -1) {
            mesh.useVertexColors = false;
            mesh.hasVertexAlpha = false;
        }
        else if (mesh.isVerticesDataPresent(BABYLON.VertexBuffer.ColorKind)) {
            mesh.useVertexColors = true;
            //mesh.hasVertexAlpha = true;
        }
        this._ensureNormalsForNodeMaterial(mesh, newMaterial);
        mesh.material = newMaterial;
    }

    changeSmartMeshObjectsMaterial(masterMeshId: string, objectIds: string[], newMaterialId: string, enableNotifications: boolean = true, baseMaterial: boolean = false) {

        if (enableNotifications) {
            this._evenHandler.fireEvent(Enumerations.EventType.STARTBLOCKINGOPERATION);
        }

        if (this._masterMeshes[masterMeshId] == null) {
            return;
        }

        var mesh = this._masterMeshes[masterMeshId];
        var newMaterial = this._materialManager.getMaterial(newMaterialId);

        if (newMaterial.name.toLocaleLowerCase().startsWith("sel") || newMaterial.name.indexOf("XRayMaterial") !== -1) {
            mesh.useVertexColors = false;
            mesh.hasVertexAlpha = false;
        }
        else if (mesh.isVerticesDataPresent(BABYLON.VertexBuffer.ColorKind)) {
            mesh.useVertexColors = true;
        }
        this._ensureNormalsForNodeMaterial(mesh, newMaterial);
        mesh.material = newMaterial;
    }

    private _isWholeMasterMeshChange(masterMeshId: string, objectIds: string[], objectInfosOfMaster: SubMeshObjectInfo[]): boolean {
        if (!objectIds || !objectInfosOfMaster || objectInfosOfMaster.length === 0) {
            return false;
        }

        if (objectIds.length < objectInfosOfMaster.length) {
            return false;
        }

        const objectIndex = this._ensureMasterMeshObjectIndex(masterMeshId);
        if (!objectIndex) {
            return false;
        }

        const seenIds: { [objectId: string]: boolean } = {};
        let seenCount = 0;

        for (let i = 0; i < objectIds.length; i++) {
            const objectId = objectIds[i];

            if (!objectId || seenIds[objectId] || objectIndex[objectId] == null) {
                continue;
            }

            seenIds[objectId] = true;
            seenCount++;

            if (seenCount === objectInfosOfMaster.length) {
                return true;
            }
        }

        return false;
    }

    private _getMaterialColor(material: any): BABYLON.Color3 {
        if (material?.diffuseColor) {
            return material.diffuseColor;
        }

        if (material?.albedoColor) {
            return material.albedoColor;
        }

        return BABYLON.Color3.White();
    }

    private _ensureNormalsForNodeMaterial(mesh: BABYLON.Mesh, material: BABYLON.Material): void {
        if (!(material instanceof BABYLON.NodeMaterial)
            || mesh.isVerticesDataPresent(BABYLON.VertexBuffer.NormalKind)) {
            return;
        }

        const positions = mesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
        const indices = mesh.getIndices();
        if (!positions || positions.length === 0 || !indices || indices.length === 0) {
            return;
        }

        const normals = new Float32Array(positions.length);
        BABYLON.VertexData.ComputeNormals(positions, indices, normals);
        mesh.setVerticesData(BABYLON.VertexBuffer.NormalKind, normals, false);
    }

    private _getMaterialAlpha(material: any): number {
        const alpha = material?.alpha;
        return typeof alpha === "number" ? alpha : 1;
    }

    private _canUseUnbuiltWholeMeshMaterialPath(masterMeshId: string, objectIds: string[], newMaterialId: string, objectInfosOfMaster: SubMeshObjectInfo[]): boolean {
        const sps = this._masterSPS[masterMeshId] as any;
        if (!sps || sps.particlesBuilt) {
            return false;
        }

        return this._isWholeMasterMeshChange(masterMeshId, objectIds, objectInfosOfMaster);
    }

    private _changeUnbuiltEntireMasterMeshMaterial(masterMeshId: string, objectInfosOfMaster: SubMeshObjectInfo[], newMaterialId: string, newMaterial: BABYLON.Material): boolean {
        const sps = this._masterSPS[masterMeshId] as any;
        const mesh = (this._masterMeshes[masterMeshId] as any) ?? sps?.mesh;

        if (!sps || sps.particlesBuilt || !mesh) {
            return false;
        }

        for (let i = 0; i < objectInfosOfMaster.length; i++) {
            objectInfosOfMaster[i].materialOverride = newMaterialId;
        }

        if (newMaterial.name.toLocaleLowerCase().startsWith("sel") || newMaterial.name.indexOf("XRayMaterial") !== -1) {
            mesh.useVertexColors = false;
            mesh.hasVertexAlpha = false;
        }
        else if (mesh.isVerticesDataPresent(BABYLON.VertexBuffer.ColorKind)) {
            mesh.useVertexColors = true;
        }

        this._ensureNormalsForNodeMaterial(mesh, newMaterial);
        mesh.material = newMaterial;
        sps.mesh.material = newMaterial;
        return true;
    }

    private _canUseBuiltWholeMeshMaterialPath(masterMeshId: string, objectIds: string[], objectInfosOfMaster: SubMeshObjectInfo[]): boolean {
        const sps = this._masterSPS[masterMeshId] as any;
        if (!sps || !sps.particlesBuilt) {
            return false;
        }

        return this._isWholeMasterMeshChange(masterMeshId, objectIds, objectInfosOfMaster);
    }

    private _changeEntireMasterMeshMaterial(masterMeshId: string, objectInfosOfMaster: SubMeshObjectInfo[], newMaterialId: string, newMaterial: BABYLON.Material): boolean {
        const sps = this._masterSPS[masterMeshId] as any;
        if (!sps || !sps.particlesBuilt) {
            return false;
        }

        const revertPerColor = newMaterialId == "VertexColorMaterial";
        const materialColor = this._getMaterialColor(newMaterial);
        const materialAlpha = this._getMaterialAlpha(newMaterial);
        for (let i = 0; i < objectInfosOfMaster.length; i++) {
            objectInfosOfMaster[i].materialOverride = newMaterialId;
        }

        for (let i = 0; i < sps.nbParticles; i++) {
            const particle = sps.particles[i] as any;

            if (newMaterial.name == "HidMat") {
                if (particle.isVisible) {
                    particle.isVisible = false;
                    sps.computeParticleVertex = true;
                }
            }
            else if (!particle.isVisible) {
                particle.isVisible = true;
                sps.computeParticleVertex = true;
            }

            if (!revertPerColor) {
                particle.material = newMaterial;
                particle.color.a = materialAlpha;
                particle.color.r = materialColor.r;
                particle.color.g = materialColor.g;
                particle.color.b = materialColor.b;
            }
            else {
                particle.material = this._materialManager.getColorVertexMaterial();
            }
        }

        sps.markMaterialTopologyDirty();
        sps.markParticleColorsDirty();
        if (sps.computeParticleVertex) {
            sps.markParticleVerticesDirty();
        }

        sps.runParticleUpdate(function (particle: BABYLON.SolidParticle): BABYLON.SolidParticle {
            return particle;
        });
        return true;
    }

    changeGraphicObjectsMaterial(masterMeshId: string, objectIds: string[], newMaterialId: string, enableNotifications: boolean = true, baseMaterial: boolean = false) {

        // Comments about alpha:
        // The particle sets th alpha BUT this is never used, since we never turn thee hasVertexAlpha flag on. Instead, since we
        // are using multimaterials, the alpha is set on that material, which rendrs the alpha on the verts. If both the material and the particle
        // has alspha, and the hasVertexAlpha flag is set on, then it mixes the alpha values. Setting the alpha on the material
        // instead of the particle seems to be correct so that we can have mixed alpha particle systems, and not have backface issues.
        if (enableNotifications) {
            this._evenHandler.fireEvent(Enumerations.EventType.STARTBLOCKINGOPERATION);
        }

        if (this._isCylinderInstanceBatch(masterMeshId)) {
            this._changeCylinderInstanceObjectsMaterial(masterMeshId, objectIds, newMaterialId);

            if (enableNotifications) {
                this._evenHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
            }

            return;
        }

        if (this._masterMeshes[masterMeshId] == null) {
            return;
        }

        if ((this._masterMeshes[masterMeshId] as any).dataSource == Enumerations.PickObjectType.GLBObject) {
            this.changeGLBObjectsMaterial(masterMeshId, objectIds, newMaterialId, enableNotifications, baseMaterial);
            return;
        }

        if ((this._masterMeshes[masterMeshId] as any).dataSource == Enumerations.PickObjectType.Mesh) {
            this.changeSmartMeshObjectsMaterial(masterMeshId, objectIds, newMaterialId, enableNotifications, baseMaterial);
            return;
        }

        if ((this._masterMeshes[masterMeshId] as any).dataSource == Enumerations.PickObjectType.PagedMesh && !this._masterSPS[masterMeshId]) {
            this.changeSmartMeshObjectsMaterial(masterMeshId, objectIds, newMaterialId, enableNotifications, baseMaterial);
            return;
        }

        var newMaterial: BABYLON.Material;
        var objectInfosOfMaster = this._masterMeshToObjectInfo[masterMeshId];

        var currentMaterial = (this._masterSPS[masterMeshId] as any)._hasVertexColor
            ? this._materialManager.getColorVertexMaterial()
            : ((this._masterSPS[masterMeshId].mesh?.material as BABYLON.Material) ?? this._materialManager.getMaterial(masterMeshId));

        if (baseMaterial && (this._masterSPS[masterMeshId] as any)._hasVertexColor)
            newMaterialId = "VertexColorMaterial";

        // The wrapper provides a null value when the default is to be used.
        if (newMaterialId == null) {

            var defaultMaterialAlpha = 1;
            var defaultWireframe = false;

            newMaterial = this._materialManager.getDefaultMaterial(defaultMaterialAlpha, defaultWireframe);
            newMaterialId = newMaterial.name;

        }
        else {
            newMaterial = this._materialManager.getMaterial(newMaterialId);
        }

        if (!newMaterial) {
            console.log("Material for graphic object cannot be found. Id: " + newMaterialId);
            return;
        }

        if (objectIds == null)
            return;

        if (this._canUseUnbuiltWholeMeshMaterialPath(masterMeshId, objectIds, newMaterialId, objectInfosOfMaster)) {
            this._changeUnbuiltEntireMasterMeshMaterial(masterMeshId, objectInfosOfMaster, newMaterialId, newMaterial);

            if (enableNotifications) {
                this._evenHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
            }

            return;
        }

        if (this._canUseBuiltWholeMeshMaterialPath(masterMeshId, objectIds, objectInfosOfMaster)) {
            this._changeEntireMasterMeshMaterial(masterMeshId, objectInfosOfMaster, newMaterialId, newMaterial);

            if (enableNotifications) {
                this._evenHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
            }

            return;
        }


        var objectsToChange = new Array<number>();

        for (var objInd = 0; objInd < objectIds.length; objInd++) {
            var objectId = objectIds[objInd];

            var indexOfObject = this.getObjectIndexByObjectId(masterMeshId, objectId);
            if (indexOfObject >= 0) {
                objectsToChange.push(indexOfObject);
                objectInfosOfMaster[indexOfObject].materialOverride = newMaterialId;
            }
        }

        var that = this;
        var sps = this._masterSPS[masterMeshId] as any;

        if (!sps.particlesBuilt) {
            var m = this._masterSPS[masterMeshId].mesh;
            var material = m.material as any;

            this._masterSPS[masterMeshId].initParticles = function () {
                const tintColor = that._getMaterialColor(material);
                const tintAlpha = that._getMaterialAlpha(material);

                if (tintAlpha === 0) {
                    console.warn("SPS initParticles resolved transparent material", {
                        masterMeshId: masterMeshId,
                        materialId: material?.id,
                        materialName: material?.name,
                        particleCount: sps.nbParticles,
                    });
                }

                for (var i = 0; i < sps.nbParticles; i++) {
                    {
                        sps.particles[i].color.a = tintAlpha;
                        sps.particles[i].color.r = tintColor.r;
                        sps.particles[i].color.g = tintColor.g;
                        sps.particles[i].color.b = tintColor.b;
                        sps.particles[i].material = material;
                    }

                    sps.particles[i].position.x = 0;
                    sps.particles[i].position.y = 0;
                    sps.particles[i].position.z = 0;
                }
            };

            this._masterSPS[masterMeshId].addShape(m, 1);

            var builtMesh = this._masterSPS[masterMeshId].buildMesh();

            if (sps.shapeData != null) {

                var particles: BABYLON.SolidParticle[] = this.processParticles(builtMesh, sps.shapeData);
                sps.particles = particles;
                sps.nbParticles = particles.length;

                sps.shapeData = null;
            }

            // We do NOT use the material with RGBA from the material manager, but instead create an empty placeholder. This is
            // done because the material properties are already burnt into the particles when we build the mesh. If we again
            // set the RGB values via a material, we get incorrect results. From the doc:
            // "The colors are the Vertex colors, the color related to the vertices themselves. This means that, if you also use a colored material, the vertex colors and the material colors will mix nicely."
            // https://doc.babylonjs.com/overviews/Solid_Particle_System#colors-and-uvs
            var nm = new BABYLON.StandardMaterial("base", this._babScene);
            const currentMaterialAny = currentMaterial as any;
            nm.wireframe = !!currentMaterialAny.wireframe;
            nm.specularPower = currentMaterialAny.specularPower != null ? currentMaterialAny.specularPower : nm.specularPower;
            if (currentMaterialAny.specularColor) {
                nm.specularColor = currentMaterialAny.specularColor;
            }
            if (currentMaterialAny.emissiveColor) {
                nm.emissiveColor = currentMaterialAny.emissiveColor;
            }
            if (currentMaterialAny.ambientColor) {
                nm.ambientColor = currentMaterialAny.ambientColor;
            }
            nm.diffuseColor = this._getMaterialColor(currentMaterial);
            nm.alpha = this._getMaterialAlpha(currentMaterial);

            sps.setOwnedMeshMaterial(nm);

            sps.initParticles();
            sps.updateParticle = null;

            builtMesh.isPickable = false;

            this._masterMeshes[masterMeshId] = builtMesh;

            sps.particlesBuilt = true;
            sps.initParticles = null;
        }

        var revertPerColor: boolean = newMaterialId == "VertexColorMaterial";

        var toChange = new Uint8Array(sps.nbParticles);
        for (var i = 0; i < objectsToChange.length; i++) {
            const idx = objectsToChange[i];
            if (idx >= 0 && idx < toChange.length) {
                toChange[idx] = 1;
            }
        }

        this._masterSPS[masterMeshId].markMaterialTopologyDirty();
        this._masterSPS[masterMeshId].markParticleColorsDirty();

        this._masterSPS[masterMeshId].runParticleUpdate(function (p: BABYLON.SolidParticle): BABYLON.SolidParticle {
            const tintColor = that._getMaterialColor(newMaterial);
            const tintAlpha = that._getMaterialAlpha(newMaterial);

            // O(1) check instead of indexOf
            if (toChange[p.idx]) {
                if (newMaterial.name == "HidMat") {
                    if (p.isVisible) {
                        p.isVisible = false;
                        that._masterSPS[masterMeshId].computeParticleVertex = true;
                        that._masterSPS[masterMeshId].markParticleVerticesDirty();
                    }
                }
                else {
                    if (!p.isVisible) {
                        p.isVisible = true;
                        that._masterSPS[masterMeshId].computeParticleVertex = true;
                        that._masterSPS[masterMeshId].markParticleVerticesDirty();
                    }
                }

                if (!revertPerColor) {
                    (p as any).material = newMaterial;

                    p.color.a = tintAlpha;
                    p.color.r = tintColor.r;
                    p.color.g = tintColor.g;
                    p.color.b = tintColor.b;
                }
                else {
                    (p as any).material = that._materialManager.getColorVertexMaterial();
                }
            }

            return p;
        });

        if (enableNotifications) {
            this._evenHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
        }
    }

    hideGraphicObjects(masterMeshId: string, objectIds: string[]) {

        //var objectInfosOfMaster = this._masterMeshToObjectInfo[masterMeshId];

        //var positions = this._masterMeshes[masterMeshId].getVertexBuffer(BABYLON.VertexBuffer.PositionKind).getData();
        //var normals = this._masterMeshes[masterMeshId].getVertexBuffer(BABYLON.VertexBuffer.NormalKind).getData();

        //var uvsBuffer = this._masterMeshes[masterMeshId].getVertexBuffer(BABYLON.VertexBuffer.UVKind);
        //var uvs = uvsBuffer ? this._masterMeshes[masterMeshId].getVertexBuffer(BABYLON.VertexBuffer.UVKind).getData() : null;
        //var indices = this._masterMeshes[masterMeshId].getIndices();

        //for (var objInd = 0; objInd < objectIds.length; objInd++) {
        //    var objectId = objectIds[objInd];
        //    //var objectId = this.getObjectIdByFace(masterMeshId, faceId);
        //    var indexOfObject = this.getObjectIndexByObjectId(masterMeshId, objectId.toString());
        //    var objectInfoOfObject = objectInfosOfMaster[indexOfObject];

        //    var startInd = objectInfoOfObject.indiciesStartIndex;
        //    var endInd = objectInfoOfObject.indiciesEndIndex;

        //    var startPos = objectInfoOfObject.vertexStartIndex * 3;
        //    var endPos = objectInfoOfObject.vertexEndIndex * 3;

        //    var startUv = objectInfoOfObject.uvStartIndex;
        //    var endUv = objectInfoOfObject.uvEndIndex;
        //    var numUvsRemoved = endUv - startUv;

        //    var numIndicesRemoved = endInd - startInd;
        //    var selectedIndicies = indices.splice(startInd, numIndicesRemoved + 1);

        //    var numPositionsRemoved = endPos - startPos + 3;

        //    //var pickedId = objectInfosOfMaster[i].objectId;
        //    objectInfosOfMaster.splice(indexOfObject, 1);

        //    var selectedPositions = positions.splice(startPos, numPositionsRemoved);
        //    var selectedNormals = normals.splice(startPos, numPositionsRemoved);
        //    var selectedUvs = uvs ? uvs.splice(startUv, numUvsRemoved + 1) : null;

        //    for (var z = startInd; z < indices.length; z++) {
        //        indices[z] -= (numPositionsRemoved) / 3;
        //    }

        //    for (var z = indexOfObject; z < objectInfosOfMaster.length; z++) {

        //        objectInfosOfMaster[z].indiciesStartIndex -= numIndicesRemoved + 1;
        //        objectInfosOfMaster[z].indiciesEndIndex -= numIndicesRemoved + 1;

        //        objectInfosOfMaster[z].vertexStartIndex -= numPositionsRemoved / 3;
        //        objectInfosOfMaster[z].vertexEndIndex -= numPositionsRemoved / 3;

        //        if (uvs) {
        //            objectInfosOfMaster[z].uvStartIndex -= numUvsRemoved + 1;
        //            objectInfosOfMaster[z].uvEndIndex -= numUvsRemoved + 1;
        //        }
        //    }

        //    this._objectIdToMasterMeshId[objectId] = null;
        //}

        //if (positions.length == 0 || indices.length == 0) {
        //    this.clearMesh(masterMeshId);
        //}
        //else {
        //    //this._masterMeshes[masterMeshId].markAsDirty("");
        //    this._masterMeshes[masterMeshId].setVerticesData(BABYLON.VertexBuffer.PositionKind, positions);
        //    this._masterMeshes[masterMeshId].setVerticesData(BABYLON.VertexBuffer.NormalKind, normals);
        //    this._masterMeshes[masterMeshId].setVerticesData(BABYLON.VertexBuffer.UVKind, uvs);
        //    this._masterMeshes[masterMeshId].setIndices(indices);
        //}
    }

    getExtentsOfObject(id: string): BABYLON.Vector3[] {

        var retVal: BABYLON.Vector3[] = [];
        var masterMeshIds = this._objectIdToMasterMeshIds[id];

        if (masterMeshIds != null && masterMeshIds.length > 0) {
            var masterMeshId = masterMeshIds[0];
            const cylinderMaster = this._cylinderInstanceMasters[masterMeshId];

            if (cylinderMaster && cylinderMaster.objectBounds[id]) {
                retVal.push(cylinderMaster.objectBounds[id].minExtents);
                retVal.push(cylinderMaster.objectBounds[id].maxExtents);
                return retVal;
            }

            var index = this.getObjectIndexByObjectId(masterMeshId, id);

            if (index > -1) {
                var objectInfo = this._masterMeshToObjectInfo[masterMeshId][index];

                retVal.push(objectInfo.minExtents);
                retVal.push(objectInfo.maxExtents);

                return retVal;
            }
            else {
                return null;
            }
        }

        return null;
    }

    getObjectIndexByObjectId(masterMeshId: string, objectId: string): number {
        const objectIndex = this._ensureMasterMeshObjectIndex(masterMeshId);
        if (objectIndex && objectIndex[objectId] != null) {
            return objectIndex[objectId];
        }

        return -1;
    }

    private _findObjectInfoIndexByFace(masterMeshId: string, faceId: number): number {
        const objectInfosOfMaster = this._masterMeshToObjectInfo[masterMeshId];
        if (!objectInfosOfMaster || objectInfosOfMaster.length === 0 || faceId == null || faceId < 0) {
            return -1;
        }

        for (let i = 0; i < objectInfosOfMaster.length; i++) {
            const objectInfo = objectInfosOfMaster[i];
            const faceStart = (objectInfo?.indiciesStartIndex ?? 0) / 3;
            const faceCount = (objectInfo?.indicesCount ?? 0) / 3;
            if (faceId >= faceStart && faceId < faceStart + faceCount) {
                return i;
            }
        }

        return -1;
    }

    getObjectIdByFace(masterMeshId: string, faceId: number): string {
        const foundIndex = this._findObjectInfoIndexByFace(masterMeshId, faceId);
        if (foundIndex >= 0) {
            return this._masterMeshToObjectInfo[masterMeshId][foundIndex].objectId;
        }

        return "-1";

    }

    getObjectIdByThinInstance(masterMeshId: string, thinInstanceIndex: number): string {
        const masterData = this._getCylinderInstanceMaster(masterMeshId);
        if (!masterData || thinInstanceIndex < 0 || thinInstanceIndex >= masterData.entries.objectIds.length) {
            return "-1";
        }

        return masterData.entries.objectIds[thinInstanceIndex];
    }

    public isObjectHiddenByMaterial(masterMeshId: string, objectId: string): boolean {
        if (!masterMeshId || !objectId || objectId === "-1") {
            return false;
        }

        const cylinderMaster = this._getCylinderInstanceMaster(masterMeshId);
        if (cylinderMaster?.materialKey === "HidMat") {
            return true;
        }

        const objectIndex = this.getObjectIndexByObjectId(masterMeshId, objectId);
        return objectIndex >= 0
            && this._masterMeshToObjectInfo[masterMeshId]?.[objectIndex]?.materialOverride === "HidMat";
    }

    private _getRayBoxIntersectionDistance(ray: BABYLON.Ray, min: BABYLON.Vector3, max: BABYLON.Vector3): number | null {
        let tMin = Number.NEGATIVE_INFINITY;
        let tMax = Number.POSITIVE_INFINITY;

        const updateRange = (originValue: number, directionValue: number, minValue: number, maxValue: number): boolean => {
            if (Math.abs(directionValue) < 1e-8) {
                return originValue >= minValue && originValue <= maxValue;
            }

            let axisMin = (minValue - originValue) / directionValue;
            let axisMax = (maxValue - originValue) / directionValue;

            if (axisMin > axisMax) {
                const swap = axisMin;
                axisMin = axisMax;
                axisMax = swap;
            }

            tMin = Math.max(tMin, axisMin);
            tMax = Math.min(tMax, axisMax);

            return tMax >= tMin;
        };

        if (!updateRange(ray.origin.x, ray.direction.x, min.x, max.x)
            || !updateRange(ray.origin.y, ray.direction.y, min.y, max.y)
            || !updateRange(ray.origin.z, ray.direction.z, min.z, max.z)) {
            return null;
        }

        if (tMax < 0) {
            return null;
        }

        return tMin >= 0 ? tMin : tMax;
    }

    private _pickCylinderObjectExact(masterData: CylinderInstanceMasterData, objectId: string, worldRay: BABYLON.Ray): Interfaces.Interfaces.ICylinderPickResult | null {
        if (!masterData || !masterData.sourceMesh || !masterData.matrixBuffer) {
            return null;
        }

        const sourceMesh = masterData.sourceMesh;
        const positions = sourceMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
        const indices = sourceMesh.getIndices();
        if (!positions || !indices || indices.length < 3) {
            return null;
        }

        let bestHit: Interfaces.Interfaces.ICylinderPickResult | null = null;

        for (let entryIndex = 0; entryIndex < masterData.entries.objectIds.length; entryIndex++) {
            if (masterData.entries.objectIds[entryIndex] !== objectId) {
                continue;
            }

            const matrix = BABYLON.Matrix.FromArray(masterData.matrixBuffer, entryIndex * 16);
            const inverseMatrix = new BABYLON.Matrix();
            matrix.invertToRef(inverseMatrix);

            const localRay = BABYLON.Ray.Transform(worldRay, inverseMatrix);
            let bestLocalDistance = Number.POSITIVE_INFINITY;

            for (let index = 0; index < indices.length; index += 3) {
                const p0 = BABYLON.Vector3.FromArray(positions, indices[index] * 3);
                const p1 = BABYLON.Vector3.FromArray(positions, indices[index + 1] * 3);
                const p2 = BABYLON.Vector3.FromArray(positions, indices[index + 2] * 3);

                const hit = localRay.intersectsTriangle(p0, p1, p2);
                if (!hit || hit.distance < 1e-6) {
                    continue;
                }

                const normal = BABYLON.Vector3.Cross(p1.subtract(p0), p2.subtract(p0)).normalize();
                if (BABYLON.Vector3.Dot(localRay.direction, normal) > 0) {
                    continue;
                }

                if (hit.distance < bestLocalDistance) {
                    bestLocalDistance = hit.distance;
                }
            }

            if (!isFinite(bestLocalDistance)) {
                continue;
            }

            const localHitPoint = localRay.origin.add(localRay.direction.scale(bestLocalDistance));
            const worldHitPoint = BABYLON.Vector3.TransformCoordinates(localHitPoint, matrix);
            const distance = BABYLON.Vector3.Distance(worldRay.origin, worldHitPoint);

            if (!bestHit || distance < bestHit.distance) {
                bestHit = {
                    objectId: objectId,
                    distance: distance,
                    pickedPoint: worldHitPoint,
                    pickedMesh: sourceMesh
                };
            }
        }

        return bestHit;
    }

    public pickCylinderInstance(ray: BABYLON.Ray, predicate?: (mesh: BABYLON.AbstractMesh) => boolean): Interfaces.Interfaces.ICylinderPickResult | null {
        let bestHit: Interfaces.Interfaces.ICylinderPickResult | null = null;

        for (const masterMeshId in this._cylinderInstanceMasters) {
            const masterData = this._cylinderInstanceMasters[masterMeshId];
            const sourceMesh = masterData?.sourceMesh;
            if (!masterData
                || masterData.materialKey === "HidMat"
                || !sourceMesh
                || !sourceMesh.isEnabled()
                || !sourceMesh.isVisible
                || !sourceMesh.isPickable) {
                continue;
            }

            if (predicate && !predicate(sourceMesh)) {
                continue;
            }

            const objectInfos = this._masterMeshToObjectInfo[masterMeshId] || [];
            for (let objectIndex = 0; objectIndex < objectInfos.length; objectIndex++) {
                const objectInfo = objectInfos[objectIndex];
                if (!objectInfo || !objectInfo.objectId || !objectInfo.minExtents || !objectInfo.maxExtents) {
                    continue;
                }

                const objectBounds = new BABYLON.BoundingBox(objectInfo.minExtents, objectInfo.maxExtents);
                if (!ray.intersectsBox(objectBounds)) {
                    continue;
                }

                const exactHit = this._pickCylinderObjectExact(masterData, objectInfo.objectId, ray);
                if (exactHit && (!bestHit || exactHit.distance < bestHit.distance)) {
                    bestHit = exactHit;
                }
            }
        }

        return bestHit;
    }

    getObjectIndexByFace(masterMeshId: string, faceId: number): number {
        return this._findObjectInfoIndexByFace(masterMeshId, faceId);
    }

    clearMeshes() {
        this._disposeAllCylinderInstanceMasters();

        // Dispose SPS systems
        const spsMasterKeys = Object.keys(this._masterSPS);
        for (let i = 0; i < spsMasterKeys.length; i++) {
            if (this._masterSPS[spsMasterKeys[i]]) {
                this._masterSPS[spsMasterKeys[i]].dispose();
                this._masterSPS[spsMasterKeys[i]] = null;
            }
        }

        for (var i = 0; i < this._masterMeshKeys.length; i++) {
            this._masterMeshes[this._masterMeshKeys[i]].dispose();
            this._masterMeshes[this._masterMeshKeys[i]] = null;

            if (this._meshesToMerge[this._masterMeshKeys[i]] != null && this._meshesToMerge[this._masterMeshKeys[i]].length > 0) {

                for (var x = 0; x < this._meshesToMerge[this._masterMeshKeys[i]].length; x++) {
                    this._meshesToMerge[this._masterMeshKeys[i]][x].dispose();
                    this._meshesToMerge[this._masterMeshKeys[i]][x] = null;
                }
                this._meshesToMerge[this._masterMeshKeys[i]] = null;
            }
        }
        this.clearVertexIndexes();
        this._objectIdToMasterMeshIds = {};
        this._masterMeshToObjectInfo = {};
        this._masterMeshObjectIndex = {};
        this._masterMeshKeys = [];
        this._cylinderInstanceMasters = {};
        this._cylinderInstanceGroups = {};
        this._objectBlockPageToMaster = {};
        this._hiddenAssets = {};
        this._pendingHiddenAssets = {};
    }

    getIntersectedIds(boundingPlanes: BABYLON.Plane[]): string[] {

        var boundedIds: string[] = [];
        for (var i = 0; i < this._masterMeshKeys.length; i++) {
            var objectInfos = this._masterMeshToObjectInfo[this._masterMeshKeys[i]];

            for (var x = 0; x < objectInfos.length; x++) {
                var objectBox = new BABYLON.BoundingBox(objectInfos[x].minExtents, objectInfos[x].maxExtents);

                if (BABYLON.BoundingBox.IsInFrustum(objectBox.vectors, boundingPlanes)) {

                    boundedIds.push(objectInfos[x].objectId);
                }
            }
        }

        return boundedIds;
    }

    getIntersectedBounds(boundingPlanes: BABYLON.Plane[]): BABYLON.BoundingBox {
        var intersectedBox: BABYLON.BoundingBox;

        for (var i = 0; i < this._masterMeshKeys.length; i++) {
            var objectInfos = this._masterMeshToObjectInfo[this._masterMeshKeys[i]];

            for (var x = 0; x < objectInfos.length; x++) {
                var objectBox = new BABYLON.BoundingBox(objectInfos[x].minExtents.clone(), objectInfos[x].maxExtents.clone());


                if (BABYLON.BoundingBox.IsInFrustum(objectBox.vectors, boundingPlanes)) {
                    if (intersectedBox) {
                        intersectedBox.minimum.x = Math.min(objectInfos[x].minExtents.x, intersectedBox.minimum.x);
                        intersectedBox.minimum.y = Math.min(objectInfos[x].minExtents.y, intersectedBox.minimum.y);
                        intersectedBox.minimum.z = Math.min(objectInfos[x].minExtents.z, intersectedBox.minimum.z);

                        intersectedBox.maximum.x = Math.max(objectInfos[x].maxExtents.x, intersectedBox.maximum.x);
                        intersectedBox.maximum.y = Math.max(objectInfos[x].maxExtents.y, intersectedBox.maximum.y);
                        intersectedBox.maximum.z = Math.max(objectInfos[x].maxExtents.z, intersectedBox.maximum.z);
                    }
                    else {
                        intersectedBox = new BABYLON.BoundingBox(objectInfos[x].minExtents.clone(), objectInfos[x].maxExtents.clone());
                    }
                }
            }
        }

        return intersectedBox;
    }

    getIntersectedDistance(boundingPlanes: BABYLON.Plane[], cameraPosition: BABYLON.Vector3): number {

        var minimumDistance = 0;

        var boundingBoxPoints: BABYLON.Vector3[] = [];
        for (var a = 0; a < this._masterMeshKeys.length; a++) {
            var objectInfos = this._masterMeshToObjectInfo[this._masterMeshKeys[a]];

            for (var x = 0; x < objectInfos.length; x++) {
                var objectBox = new BABYLON.BoundingBox(objectInfos[x].minExtents.clone(), objectInfos[x].maxExtents.clone());

                if (BABYLON.BoundingBox.IsInFrustum(objectBox.vectors, boundingPlanes)) {

                    var c1 = new BABYLON.Vector3(objectBox.maximum.x, objectBox.maximum.y, objectBox.maximum.z);
                    var c2 = new BABYLON.Vector3(objectBox.maximum.x, objectBox.maximum.y, objectBox.minimum.z);
                    var c3 = new BABYLON.Vector3(objectBox.maximum.x, objectBox.minimum.y, objectBox.maximum.z);
                    var c4 = new BABYLON.Vector3(objectBox.maximum.x, objectBox.minimum.y, objectBox.minimum.z);

                    var c5 = new BABYLON.Vector3(objectBox.minimum.x, objectBox.maximum.y, objectBox.maximum.z);
                    var c6 = new BABYLON.Vector3(objectBox.minimum.x, objectBox.maximum.y, objectBox.minimum.z);
                    var c7 = new BABYLON.Vector3(objectBox.minimum.x, objectBox.minimum.y, objectBox.maximum.z);
                    var c8 = new BABYLON.Vector3(objectBox.minimum.x, objectBox.minimum.y, objectBox.minimum.z);

                    boundingBoxPoints.length = 0;
                    boundingBoxPoints.push(c1);
                    boundingBoxPoints.push(c2);
                    boundingBoxPoints.push(c3);
                    boundingBoxPoints.push(c4);
                    boundingBoxPoints.push(c5);
                    boundingBoxPoints.push(c6);
                    boundingBoxPoints.push(c7);
                    boundingBoxPoints.push(c8);


                    for (var i = 0; i < 8; i++) {
                        for (var p = 0; p < 6; p++) {
                            if (boundingBoxPoints[i] != null) {

                                var inFrustumCheck = new BABYLON.BoundingBox(boundingBoxPoints[i], boundingBoxPoints[i]);

                                if (boundingPlanes[p].dotCoordinate(boundingBoxPoints[i]) < 0) {
                                    // Bounding extent is outside of frustum, so project it ONTO the frustum plane.
                                    var distanceToProject = boundingPlanes[p].signedDistanceTo(boundingBoxPoints[i]);
                                    boundingBoxPoints[i] = null;

                                    break;
                                }
                            }
                        }
                    }

                    if (minimumDistance == 0) {

                        for (var i = 0; i < 8; i++) {

                            if (boundingBoxPoints[i] != null) {
                                if (minimumDistance == 0) {
                                    minimumDistance = cameraPosition.subtract(boundingBoxPoints[i]).length();
                                }
                                else {
                                    minimumDistance = Math.min(minimumDistance, cameraPosition.subtract(boundingBoxPoints[i]).length());
                                }
                            }
                        }
                    }
                    else {

                        for (var i = 0; i < 8; i++) {
                            if (boundingBoxPoints[i] != null) {
                                minimumDistance = Math.min(minimumDistance, cameraPosition.subtract(boundingBoxPoints[i]).length());
                            }
                        }
                    }
                }
            }
        }

        return minimumDistance;
    }

    addGraphicObject(objectId: string, minExtents: Data.GraphicData.Vector3D, maxExtents: Data.GraphicData.Vector3D, masterMeshId: string, meshes: BABYLON.Mesh[]) {

        if (meshes.length > 0) {
            var material = this._materialManager.getMaterial(masterMeshId);

            masterMeshId = material.id;

            var subMeshId = masterMeshId + "_1";

            if (this._mergedMeshKeys.indexOf(subMeshId) != -1) {

                // This base id has already been merged. Therefore, let us append a number on the end of the ID, and
                // create a new mesh so we do not get meshes that are too large.
                masterMeshId = masterMeshId + "_" + this._remappedMeshId[masterMeshId].toString();
            }
            else {
                masterMeshId = subMeshId;
            }

            for (var i = 0; i < meshes.length; i++) {
                var mesh = meshes[i];

                mesh.id = objectId;
                mesh.material = material;
                masterMeshId = this.addToMeshes(masterMeshId, mesh);
            }

            var minEx = new BABYLON.Vector3(minExtents.X / 1000, minExtents.Y / 1000, minExtents.Z / 1000);
            var maxEx = new BABYLON.Vector3(maxExtents.X / 1000, maxExtents.Y / 1000, maxExtents.Z / 1000);
            this.updateMeshExtents(new BABYLON.BoundingInfo(minEx, maxEx));
            this.updateMeshObjectInformation(masterMeshId, minEx, maxEx, objectId, meshes);

            if (this._objectIdToMasterMeshIds[objectId] == null) {
                this._objectIdToMasterMeshIds[objectId] = [];
            }

            this._addObjectMasterForPage(objectId, "0", "0", masterMeshId);

            if (this._meshesToMerge[masterMeshId].length > this.MERGE_MESH_THRESHOLD) {
                this.mergeMeshes(true);
            }
        }
    }
    mergeInstancedMeshes(forceMerge: boolean) {
        return;
        //if (forceMerge) {
        //    // Materials are the "keys" in the meshes to merge, so for all the materials that have been added, merge on all the waiting meshes.
        //    for (var i = 0; i < this._masterMeshKeys.length; i++) {
        //        if ((forceMerge && this._meshesToMerge[this._masterMeshKeys[i]].length > 0) || this._meshesToMerge[this._masterMeshKeys[i]].length > this.MERGE_MESH_THRESHOLD) {

        //            this.updateMeshVertexIndeces(this._masterMeshKeys[i], this._meshesToMerge[this._masterMeshKeys[i]]);
        //            if (this._mergedMeshes[this._masterMeshKeys[i]] != null) {
        //                this._meshesToMerge[this._masterMeshKeys[i]].push(this._mergedMeshes[this._masterMeshKeys[i]]);
        //            }

        //            var mergedMesh = BABYLON.Mesh.MergeMeshes(this._meshesToMerge[this._masterMeshKeys[i]], true, true);
        //            mergedMesh.id = this._masterMeshKeys[i];

        //            this._mergedMeshes[this._masterMeshKeys[i]] = mergedMesh;
        //            this._meshesToMerge[this._masterMeshKeys[i]] = [];
        //        }
        //    }
        //}
    }

    setDisplayAppearance(materialKey: string) {
        var material = this._materialManager.getMaterial(materialKey);

        for (var i = 0; i < this._masterMeshKeys.length; i++) {
            var meshToChange = this._masterMeshes[this._masterMeshKeys[i]];
        }
    }

    mergeMeshes(forceMerge: boolean) {
        if (forceMerge) {
            // Materials are the "keys" in the meshes to merge, so for all the materials that have been added, merge on all the waiting meshes.
            for (var i = 0; i < this._masterMeshKeys.length; i++) {
                if (this._meshesToMerge[this._masterMeshKeys[i]]) {

                    if ((forceMerge && this._meshesToMerge[this._masterMeshKeys[i]].length > 0) || this._meshesToMerge[this._masterMeshKeys[i]].length > this.MERGE_MESH_THRESHOLD) {

                        if (this._meshesToMerge[this._masterMeshKeys[i]].length > 0) {

                            if (this._masterMeshes[this._masterMeshKeys[i]] != null) {
                                this._meshesToMerge[this._masterMeshKeys[i]].unshift(this._masterMeshes[this._masterMeshKeys[i]]);
                            }

                            var mergedMesh = BABYLON.Mesh.MergeMeshes(this._meshesToMerge[this._masterMeshKeys[i]], true, true);

                            mergedMesh.id = this._masterMeshKeys[i];
                            mergedMesh.material = this._materialManager.getMaterial(this._masterMeshKeys[i]);
                            mergedMesh.name = this._materialManager.getMaterial(this._masterMeshKeys[i]).name;
                            mergedMesh.isPickable = false;

                            this._mergedMeshKeys.push(this._masterMeshKeys[i]);

                            var mergeMeshKey = this._masterMeshKeys[i];

                            var keyParts = mergeMeshKey.split("_");
                            var baseKey = keyParts[0];

                            var subMeshId = Number(keyParts[1]);

                            this._remappedMeshId[baseKey] = subMeshId + 1;

                            this._masterMeshes[this._masterMeshKeys[i]] = mergedMesh;
                            this._meshesToMerge[this._masterMeshKeys[i]] = [];

                            this.insertMeshIntoParticleSystem(this._masterMeshKeys[i], mergedMesh, null);
                        }
                        else {
                            var singleMesh = this._meshesToMerge[this._masterMeshKeys[i]][0];
                            singleMesh.id = this._masterMeshKeys[i];
                            singleMesh.material = this._materialManager.getMaterial(this._masterMeshKeys[i]);
                            singleMesh.name = this._materialManager.getMaterial(this._masterMeshKeys[i]).name;
                            singleMesh.isPickable = false;

                            this._mergedMeshKeys.push(this._masterMeshKeys[i]);
                            this._masterMeshes[this._masterMeshKeys[i]] = singleMesh.clone(singleMesh.name);
                            this._meshesToMerge[this._masterMeshKeys[i]] = []
                        }
                    }
                }
            }
        }
    }


    insertMeshIntoParticleSystem(id: string, mesh: BABYLON.Mesh, vertexColors: any, subMeshData: any = null) {

        // We set the SPS to not be pickable since our current picking code manages the picked index itself.
        var sps = new CustomSolidParticleSystem(id, this._babScene, { isPickable: false, vertexColors: vertexColors });

        var material = <BABYLON.StandardMaterial>mesh.material;

        mesh.isPickable = false;
        var s = sps.setMesh(mesh, subMeshData);

        sps.computeParticleRotation = false;

        sps.mesh.freezeWorldMatrix();       // prevents from re-computing the World Matrix each frame
        sps.mesh.freezeNormals();

        this._masterSPS[id] = sps as any;
    }

    setMaterialEdgeMode(mode: Enumerations.MaterialEdgeMode) {
        var wireframe = mode == Enumerations.MaterialEdgeMode.WIREFRAME;

        for (var i = 0; i < this._masterMeshKeys.length; i++) {

            this._masterMeshes[this._masterMeshKeys[i]].material.wireframe = wireframe;
        }
    }

    createOctantBBox(minPoint: BABYLON.Vector3, maxPoint: BABYLON.Vector3, blockId: number, numPages: number) {

        var box = BABYLON.Mesh.CreateBox(blockId.toString(), 1, this._babScene);

        box.position = maxPoint.subtract(maxPoint.subtract(minPoint).scale(.5));

        box.scaling = new BABYLON.Vector3(maxPoint.x - minPoint.x, maxPoint.y - minPoint.y, maxPoint.z - minPoint.z);
        box.material = this._unloadedMaterial;

        this._octreeMeshes[blockId] = box;
        this._octreePageCount[blockId] = numPages;
        this._octreeLoadedPageCount[blockId] = 0;
        this._octreeVisiblePageCount[blockId] = 0;
    }

    clearBoxPageCounts() {

        var keys = Object.keys(this._octreeVisiblePageCount);

        for (var x = 0; x < keys.length; x++) {

            this._octreeVisiblePageCount[keys[x]] = 0;
        }
    }

    setBoxPageVisible(blockId: string, count: number) {

        this._octreeVisiblePageCount[blockId] += count;

        this.updateBoxMaterial(blockId);
    }

    setBoxLoaded(blockId: string, count: number) {
        this._octreeLoadedPageCount[blockId] += count;

        this.updateBoxMaterial(blockId);
    }

    updateBoxMaterial(blockId: string) {

        if (this._octreeLoadedPageCount[blockId] == this._octreePageCount[blockId]) {

            this._octreeMeshes[blockId].material = this._loadedMaterial;
        }
        else if (this._octreeLoadedPageCount[blockId] == this._octreeVisiblePageCount[blockId]) {
            this._octreeMeshes[blockId].material = this._loadedVisibleMaterial;
        }
        else if (this._octreeLoadedPageCount[blockId] == 0) {

            this._octreeMeshes[blockId].material = this._unloadedMaterial;
        }
        else if (this._octreeLoadedPageCount[blockId] < this._octreePageCount[blockId]) {

            this._octreeMeshes[blockId].material = this._partiallyMaterial;
        }
    }

    public setPickableMeshes(pickable: boolean) {

        for (var i = 0; i < this._masterMeshKeys.length; i++) {
            const masterMeshId = this._masterMeshKeys[i];
            const mesh = this._masterMeshes[masterMeshId];

            if (!mesh) {
                continue;
            }

            if (mesh.material == null || mesh.material.id != "MeasurementMaterial") {
                if (this._isCylinderInstanceBatch(masterMeshId)) {
                    mesh.isPickable = pickable;
                    mesh.thinInstanceEnablePicking = false;
                    continue;
                }

                mesh.isPickable = pickable;
            }
        }
    }

    public setSpsMeshesEnabled(enabled: boolean): void {
        const spsKeys = Object.keys(this._masterSPS);

        for (let i = 0; i < spsKeys.length; i++) {
            const sps = this._masterSPS[spsKeys[i]] as any;
            const mesh = sps?.mesh as BABYLON.Mesh;

            if (!mesh || mesh.isDisposed()) {
                continue;
            }

            mesh.setEnabled(enabled);
        }
    }

    /**
     * Apply or remove XRay material to Region meshes (GLB/mesh). Point clouds are left unchanged.
     * @param enable If true, switch region meshes to XRay; if false, restore their original materials
     */
    public setRegionXRayMode(enable: boolean): void {
        this._regionXRayActive = !!enable;
        const xrayMat = this._materialManager.getMaterial("XRayMaterial");
        var regionKeys: string[] = [];
        for (var b in this._pageToRegion) {
            if (!this._pageToRegion.hasOwnProperty(b)) continue;
            var pages = this._pageToRegion[b];
            for (var p in pages) {
                if (!pages.hasOwnProperty(p)) continue;
                var arr = pages[p];
                for (var i = 0; i < arr.length; i++) {
                    if (regionKeys.indexOf(arr[i]) === -1) regionKeys.push(arr[i]);
                }
            }
        }
        // If no region meshes are tracked yet, just set the flag and exit; new region meshes will honor _regionXRayActive on creation.
        if (regionKeys.length === 0) return;

        for (var r = 0; r < regionKeys.length; r++) {
            var key = regionKeys[r];
            const mesh = this._masterMeshes[key];
            if (!mesh) continue;
            // skip point clouds managed separately
            if (this._masterPointClouds[key]) continue;

            if (enable) {
                // store original material id if not already stored
                if (!this._regionOriginalMaterial[key]) {
                    const mat = mesh.material as BABYLON.StandardMaterial;
                    if (mat && mat.id) this._regionOriginalMaterial[key] = mat.id;
                }
                mesh.useVertexColors = false;
                mesh.hasVertexAlpha = false;
                mesh.material = xrayMat;
            } else {
                const originalId = this._regionOriginalMaterial[key];
                if (originalId) {
                    const blockId = this._regionMaterialBlock[key];
                    // GLB materials are looked up with prefix when available
                    const restored = (originalId.indexOf("GLB|") === 0)
                        ? this._materialManager.getGLBMaterial(originalId, blockId)
                        : this._materialManager.getMaterial(originalId);
                    if (restored) {
                        mesh.material = restored;
                        // restore vertex color flags if applicable
                        if (mesh.isVerticesDataPresent(BABYLON.VertexBuffer.ColorKind)) {
                            mesh.useVertexColors = true;
                            mesh.hasVertexAlpha = true;
                        }
                    }
                }
            }
        }
    }


    public getMemoryConsumptionForData(meshDatas: any, blockId: string, pageId: string): number {
        // Asset page memory is accounted for by DisplayManager using metadata.
        return 0;
    }

    public processMeshDataMaterialMap(baseMaterialMap: any) {

        var baseMaterialKeys = Object.keys(baseMaterialMap);
        for (var i = 0; i < baseMaterialKeys.length; i++) {

            var key = baseMaterialKeys[i];
            var objects = baseMaterialMap[baseMaterialKeys[i]];

            for (var x = 0; x < objects.length; x++) {

                if (this._objectToAppearanceOverridesList[objects[x]] == null) {
                    this._objectToAppearanceOverridesList[objects[x]] = [];
                }

                this._objectToAppearanceOverridesList[objects[x]].push(key);
            }
        }
    }

    public processMeshData(meshData: any, baseMaterialMap: any, appearanceOverrideData: any, blockId: string, pageId: string): number {
        const includeAppearanceOverrides = this._pageToMesh[blockId] == null || this._pageToMesh[blockId][pageId] == null || this._pageToMesh[blockId][pageId].length == 0;

        // Only load the appearance override data for a block/page ONCE!
        if (includeAppearanceOverrides) {
            for (var i = 0; i < appearanceOverrideData.length; i++) {

                var appearanceOverride = appearanceOverrideData[i];

                for (var p = 0; p < appearanceOverride.ListItems.length; p++) {

                    if (this._objectToAppearanceOverridesList[appearanceOverride.ListItems[p]] == null) {
                        this._objectToAppearanceOverridesList[appearanceOverride.ListItems[p]] = [];
                    }

                    this._objectToAppearanceOverridesList[appearanceOverride.ListItems[p]].push(appearanceOverride.OverrideObjectListID);
                }
            }
        }

        for (var i = 0; i < meshData.ObjectInfo.length; i++) {

            var info = meshData.ObjectInfo[i];

            if (info.minExtents._x) {
                info.minExtents = new BABYLON.Vector3(info.minExtents._x, info.minExtents._y, info.minExtents._z);
                info.maxExtents = new BABYLON.Vector3(info.maxExtents._x, info.maxExtents._y, info.maxExtents._z);
            }
            else {
                info.minExtents = new BABYLON.Vector3(info.minExtents.x, info.minExtents.y, info.minExtents.z);
                info.maxExtents = new BABYLON.Vector3(info.maxExtents.x, info.maxExtents.y, info.maxExtents.z);
            }

            this.updateMeshExtents(new BABYLON.BoundingInfo(info.minExtents, info.maxExtents));
        }

        if (this._masterMeshKeys.indexOf(meshData.Key) == -1) {
            this._masterMeshKeys.push(meshData.Key);
        }

        this._masterMeshToObjectInfo[meshData.Key] = meshData.ObjectInfo;
        this._rebuildMasterMeshObjectIndex(meshData.Key);

        if (meshData.Geometry.colors.length > 0 && appearanceOverrideData.length == 0) {
            var objectInfosOfMaster = this._masterMeshToObjectInfo[meshData.Key];

            for (var x = 0; x < objectInfosOfMaster.length; x++) {
                objectInfosOfMaster[x].materialOverride = "VertexColorMaterial";
            }
        }

        for (var x = 0; x < meshData.IdToMaster.length; x++) {
            this._addObjectMasterForPage(meshData.IdToMaster[x], blockId, pageId, meshData.Key);
        }

        this.parseMeshData(meshData.Key, meshData.MaterialKey, meshData, appearanceOverrideData, blockId, pageId);

        this.meshesDirty = true;

        this.getMaterialManager().refreshPointCloudMeshOccluders();
        return 0;
    }
    public processInstanceData(instanceData: Data.GraphicData.AssetInstancePayload, blockId: string, pageId: string): number {
        return this.processInstancePayload(instanceData ? [instanceData] : null, blockId, pageId).memoryLength;
    }

    public processInstancePayload(instancePayload: Data.GraphicData.AssetInstancePayload[], blockId: string, pageId: string): { memoryLength: number; objectIds: string[]; } {

        if (!instancePayload || instancePayload.length === 0) {
            return { memoryLength: 0, objectIds: [] };
        }

        const objectMasters: { [masterMeshId: string]: CylinderInstanceMasterData; } = {};
        const newEntriesByMaster: { [masterMeshId: string]: PendingCylinderThinInstanceEntry[]; } = {};
        const processedObjectIds: string[] = [];
        const seenObjectIds: { [objectId: string]: boolean; } = {};
        let instanceCount = 0;

        for (let payloadIndex = 0; payloadIndex < instancePayload.length; payloadIndex++) {
            const instanceData = instancePayload[payloadIndex];

            if (!instanceData || !instanceData.Instances || instanceData.Instances.length === 0) {
                continue;
            }

            const objectId = instanceData.ObjectId;
            const materialKey = instanceData.MaterialKey;
            const rawMinExtents = this._toVector3(instanceData.MinExtents).scale(1 / 1000);
            const rawMaxExtents = this._toVector3(instanceData.MaxExtents).scale(1 / 1000);
            const minExtents = new BABYLON.Vector3(rawMinExtents.x, rawMinExtents.z, rawMinExtents.y);
            const maxExtents = new BABYLON.Vector3(rawMaxExtents.x, rawMaxExtents.z, rawMaxExtents.y);
            const firstTransform = instanceData.Instances[0];
            const complexityLevel = this._getNormalizedCylinderInstanceComplexityLevel(firstTransform.ComplexityLevel);
            const groupKey = this._getCylinderInstanceGroupKey(blockId, pageId, materialKey, complexityLevel);
            const existingGroup = this._getCylinderInstanceGroup(groupKey);
            const groupData = existingGroup || this._getOrCreateCylinderInstanceGroup(blockId, pageId, materialKey, complexityLevel);
            const masterMeshId = groupData.sourceMasterKey;
            let masterData = objectMasters[masterMeshId];

            if (!masterData) {
                const existingMaster = this._getCylinderInstanceMaster(masterMeshId);
                masterData = existingMaster || this._getOrCreateCylinderInstanceMaster(materialKey, complexityLevel);
                objectMasters[masterMeshId] = masterData;
                newEntriesByMaster[masterMeshId] = [];
            }

            this._setCylinderInstanceObjectBounds(masterData, objectId, minExtents, maxExtents);

            if (!groupData.objectIds[objectId]) {
                groupData.objectIds[objectId] = true;
                this._upsertCylinderGroupObjectInfo(groupData, objectId, minExtents, maxExtents);
                this._addObjectMasterForPage(objectId, blockId, pageId, masterMeshId);
            }

            if (!seenObjectIds[objectId]) {
                seenObjectIds[objectId] = true;
                processedObjectIds.push(objectId);
            }

            for (let i = 0; i < instanceData.Instances.length; i++) {
                const transform = instanceData.Instances[i];
                const entry = {
                    objectId: objectId,
                    blockId: blockId,
                    pageId: pageId,
                    matrixData: this._createCylinderInstanceMatrixData(transform)
                };

                newEntriesByMaster[masterMeshId].push(entry);
                instanceCount++;
            }
        }

        const masterKeys = Object.keys(objectMasters);
        for (let i = 0; i < masterKeys.length; i++) {
            this._appendCylinderInstanceEntries(objectMasters[masterKeys[i]], newEntriesByMaster[masterKeys[i]]);
        }

        if (instanceCount > 0) {
            this.meshesDirty = true;
        }

        return {
            memoryLength: 0,
            objectIds: processedObjectIds
        };
    }
    public processRegionMaterials(materialData: any, blockId: string) {
        this._materialManager.processMaterialData(materialData, blockId);
    }
    public processRegionMeshData(meshData: any, dataType: string, blockId: string, pageId: string, resolution: number, lodLevel?: number): number {

        //console.log(this._babScene.meshes.length);
        var memoryConsumptionSize = this._estimateMeshRetentionMemory(meshData, [], false);


        for (var i = 0; i < meshData.ObjectInfo.length; i++) {

            var info = meshData.ObjectInfo[i];

            if (info.minExtents._x) {
                info.minExtents = new BABYLON.Vector3(info.minExtents._x, info.minExtents._y, info.minExtents._z);
                info.maxExtents = new BABYLON.Vector3(info.maxExtents._x, info.maxExtents._y, info.maxExtents._z);
            }
            else {
                info.minExtents = new BABYLON.Vector3(info.minExtents.x, info.minExtents.y, info.minExtents.z);
                info.maxExtents = new BABYLON.Vector3(info.maxExtents.x, info.maxExtents.y, info.maxExtents.z);
            }

            this.updateMeshExtents(new BABYLON.BoundingInfo(info.minExtents, info.maxExtents));
        }

        this._masterMeshToObjectInfo[meshData.Key] = meshData.ObjectInfo;
        this._rebuildMasterMeshObjectIndex(meshData.Key);

        const hasVertexColors = meshData.Geometry.colors.length > 0;
        const shouldUseVertexColorMaterial = hasVertexColors || meshData.MaterialKey == null || meshData.MaterialKey === "";
        const scopedRegionMaterialKey = shouldUseVertexColorMaterial
            ? null
            : "GLB|" + meshData.MaterialKey;

        if (shouldUseVertexColorMaterial) {
            var objectInfosOfMaster = this._masterMeshToObjectInfo[meshData.Key];

            for (var x = 0; x < objectInfosOfMaster.length; x++) {

                if (dataType == "Mesh") {
                    objectInfosOfMaster[x].materialOverride = "VertexColorBackfaceMaterial";
                }
                else {
                    objectInfosOfMaster[x].materialOverride = "VertexColorMaterial";
                }
            }
        }
        else if (scopedRegionMaterialKey) {
            const objectInfosOfMaster = this._masterMeshToObjectInfo[meshData.Key];

            for (let x = 0; x < objectInfosOfMaster.length; x++) {
                objectInfosOfMaster[x].materialOverride = scopedRegionMaterialKey;
            }
        }

        for (var x = 0; x < meshData.IdToMaster.length; x++) {
            this._addObjectMasterForPage(meshData.IdToMaster[x], blockId, pageId, meshData.Key);
        }

        this.parseRegionMeshData(meshData.Key, dataType, meshData, blockId, pageId, resolution, lodLevel);

        this.meshesDirty = true;
        this.getMaterialManager().refreshPointCloudMeshOccluders();
        return memoryConsumptionSize;
    }

    private _sanitizeObjectInfoRanges(masterMeshId: string, objectInfos: any[], totalIndices: number, totalVertices: number): void {
        if (!Array.isArray(objectInfos) || objectInfos.length === 0) {
            return;
        }

        let fallbackIndexStart = 0;
        let fallbackVertexStart = 0;

        for (let i = 0; i < objectInfos.length; i++) {
            const info = objectInfos[i];
            if (!info) {
                continue;
            }

            const requestedIndexStart = typeof info.indiciesStartIndex === "number"
                ? info.indiciesStartIndex
                : fallbackIndexStart;
            const requestedIndexCount = typeof info.indicesCount === "number"
                ? info.indicesCount
                : 0;
            const requestedVertexStart = typeof info.vertexStartIndex === "number"
                ? info.vertexStartIndex
                : fallbackVertexStart;
            const requestedVertexCount = typeof info.vertexCount === "number"
                ? info.vertexCount
                : 0;

            const nextInfo = i + 1 < objectInfos.length ? objectInfos[i + 1] : null;
            const nextRequestedIndexStart = typeof nextInfo?.indiciesStartIndex === "number"
                ? nextInfo.indiciesStartIndex
                : null;
            const nextRequestedVertexStart = typeof nextInfo?.vertexStartIndex === "number"
                ? nextInfo.vertexStartIndex
                : null;

            const safeIndexStart = Math.max(0, requestedIndexStart);
            const safeVertexStart = Math.max(0, requestedVertexStart);
            const remainingIndices = Math.max(totalIndices - safeIndexStart, 0);
            const remainingVertices = Math.max(totalVertices - safeVertexStart, 0);
            const boundedIndexCount = Math.min(Math.max(requestedIndexCount, 0), remainingIndices);
            const boundedVertexCount = Math.min(Math.max(requestedVertexCount, 0), remainingVertices);
            const safeIndexCount = nextRequestedIndexStart != null && nextRequestedIndexStart >= safeIndexStart
                ? Math.min(boundedIndexCount, Math.max(nextRequestedIndexStart - safeIndexStart, 0))
                : boundedIndexCount;
            const safeVertexCount = nextRequestedVertexStart != null && nextRequestedVertexStart >= safeVertexStart
                ? Math.min(boundedVertexCount, Math.max(nextRequestedVertexStart - safeVertexStart, 0))
                : boundedVertexCount;
            const hasSevereRangeIssue = requestedIndexStart < 0
                || requestedVertexStart < 0
                || requestedIndexCount < 0
                || requestedVertexCount < 0
                || (nextRequestedIndexStart != null && nextRequestedIndexStart < safeIndexStart)
                || (nextRequestedVertexStart != null && nextRequestedVertexStart < safeVertexStart);

            if (hasSevereRangeIssue) {
                console.warn("Region mesh object info exceeded decoded geometry bounds", {
                    masterMeshId: masterMeshId,
                    objectId: info.objectId,
                    objectIndex: i,
                    requestedIndexStart: requestedIndexStart,
                    requestedIndexCount: requestedIndexCount,
                    safeIndexStart: safeIndexStart,
                    safeIndexCount: safeIndexCount,
                    requestedVertexStart: requestedVertexStart,
                    requestedVertexCount: requestedVertexCount,
                    safeVertexStart: safeVertexStart,
                    safeVertexCount: safeVertexCount,
                    totalIndices: totalIndices,
                    totalVertices: totalVertices,
                });
            }

            info.indiciesStartIndex = safeIndexStart;
            info.indicesCount = safeIndexCount;
            info.vertexStartIndex = safeVertexStart;
            info.vertexCount = safeVertexCount;

            fallbackIndexStart = safeIndexStart + safeIndexCount;
            fallbackVertexStart = safeVertexStart + safeVertexCount;
        }
    }

    private _addObjectMasterForPage(objectId: string, blockId: string, pageId: string, masterMeshId: string) {
        // global
        if (!this._objectIdToMasterMeshIds[objectId]) this._objectIdToMasterMeshIds[objectId] = [];
        const arr = this._objectIdToMasterMeshIds[objectId];
        if (arr.indexOf(masterMeshId) === -1) arr.push(masterMeshId);

        // scoped
        if (!this._objectBlockPageToMaster[objectId]) this._objectBlockPageToMaster[objectId] = {};
        const byPage = this._objectBlockPageToMaster[objectId];
        const pageKey = this._getObjectBlockPageKey(blockId, pageId);
        const current = byPage[pageKey];

        if (!current) {
            byPage[pageKey] = masterMeshId;
            return;
        }

        if (typeof current === "string") {
            if (current !== masterMeshId) {
                byPage[pageKey] = [current, masterMeshId];
            }
            return;
        }

        if (current.indexOf(masterMeshId) === -1) {
            current.push(masterMeshId);
        }
    }

    private _removeObjectMastersForPage(objectId: string, blockId: string, pageId: string) {
        const byPage = this._objectBlockPageToMaster[objectId];
        if (!byPage) return;
        const pageKey = this._getObjectBlockPageKey(blockId, pageId);
        const pageEntry = byPage[pageKey];
        const masterIds = this._getObjectPageMasterIds(pageEntry);
        if (masterIds.length === 0) return;

        // delete scoped entry
        delete byPage[pageKey];
        if (Object.keys(byPage).length === 0) delete this._objectBlockPageToMaster[objectId];

        this._syncObjectMasterMeshIds(objectId);
    }

    private _getActualCylinderInstanceMasterKeysOfObject(objectId: string): string[] {
        const result: string[] = [];
        const seen: { [masterMeshId: string]: boolean; } = {};
        const cylinderMasterKeys = Object.keys(this._cylinderInstanceMasters);

        for (let i = 0; i < cylinderMasterKeys.length; i++) {
            const masterMeshId = cylinderMasterKeys[i];
            const masterData = this._cylinderInstanceMasters[masterMeshId];

            if (!masterData || !masterData.objectEntryCounts[objectId]) {
                continue;
            }

            if (seen[masterMeshId]) {
                continue;
            }

            seen[masterMeshId] = true;
            result.push(masterMeshId);
        }

        result.sort();
        return result;
    }

    private _getCurrentObjectMasterKeys(objectId: string): string[] {
        const result: string[] = [];
        const seen: { [masterMeshId: string]: boolean; } = {};
        const byBlock = this._objectBlockPageToMaster[objectId];

        if (byBlock) {
            for (const pageKey in byBlock) {
                const masterIds = this._getObjectPageMasterIds(byBlock[pageKey]);

                for (let i = 0; i < masterIds.length; i++) {
                    const masterMeshId = masterIds[i];
                    if (!masterMeshId || seen[masterMeshId]) {
                        continue;
                    }

                    if (!this._masterMeshes[masterMeshId] && !this._cylinderInstanceMasters[masterMeshId]) {
                        continue;
                    }

                    seen[masterMeshId] = true;
                    result.push(masterMeshId);
                }
            }
        }

        if (result.length === 0) {
            const cylinderMasters = this._getActualCylinderInstanceMasterKeysOfObject(objectId);
            for (let i = 0; i < cylinderMasters.length; i++) {
                const masterMeshId = cylinderMasters[i];
                if (seen[masterMeshId]) {
                    continue;
                }

                seen[masterMeshId] = true;
                result.push(masterMeshId);
            }
        }

        if (result.length === 0) {
            const global = this._objectIdToMasterMeshIds[objectId] || [];
            for (let i = 0; i < global.length; i++) {
                const masterMeshId = global[i];
                if (!masterMeshId || seen[masterMeshId]) {
                    continue;
                }

                if (!this._masterMeshes[masterMeshId] && !this._cylinderInstanceMasters[masterMeshId]) {
                    continue;
                }

                seen[masterMeshId] = true;
                result.push(masterMeshId);
            }
        }

        result.sort();

        if (result.length > 0) {
            this._objectIdToMasterMeshIds[objectId] = result.slice(0);
        }
        else {
            delete this._objectIdToMasterMeshIds[objectId];
        }

        return result;
    }

    private parseRegionMeshData(
        key: string,
        dataType: string,
        parsedData: any,
        blockId: string,
        pageId: string,
        resolution: number,
        lodLevel?: number
    ) {
        const geom = parsedData.Geometry;
        const positions = geom.positions as Float32Array;
        const normals = geom.normals as Float32Array;
        const indices = geom.indices as Uint32Array;
        const colors = geom.colors as Float32Array | Uint8Array | null;
        const colorInfo = geom.colorInfo || { componentType: "FLOAT32", normalized: false, components: 4 };
        const hasVertexColors = colors != null && colors.length > 0;
        const normalizedDataType = (dataType || "").toString().toLowerCase();
        const isPagedMesh = (normalizedDataType === "mesh" || normalizedDataType === "pagedglb" || normalizedDataType === "pagedfbx")
            && (parsedData.PickObjectType || "").toLowerCase() === Enumerations.PickObjectType.PagedMesh;

        this._sanitizeObjectInfoRanges(key, parsedData.ObjectInfo, indices ? indices.length : 0, positions ? positions.length / 3 : 0);

        // set color buffer (byte or float)
        const setColorBuffer = (mesh: BABYLON.Mesh) => {
            if (!colors || colors.length === 0) return;

            // sanity: vertex counts match
            const vtxCount = (positions.length / 3) | 0;
            if (((colors.length / 4) | 0) !== vtxCount) {
                console.warn("Color/position count mismatch", { vtxCount, colorLen: colors.length });
                return;
            }

            if (colorInfo.componentType === "UNSIGNED_BYTE" && colorInfo.normalized) {
                const vb = new BABYLON.VertexBuffer(
                    this._babScene.getEngine(),
                    colors as Uint8Array,
                    BABYLON.VertexBuffer.ColorKind,
        /* updatable */ false,
        /* postpone  */ false,
        /* stride    */ 0,                                  // tightly packed
        /* instanced */ false,
        /* offset    */ 0,
        /* size      */ 4,                                  // RGBA components
                    BABYLON.VertexBuffer.UNSIGNED_BYTE,
        /* normalized*/ true,
        /* useBytes  */ true
                );
                mesh.setVerticesBuffer(vb);
            } else {
                mesh.setVerticesData(BABYLON.VertexBuffer.ColorKind, colors as Float32Array, /*updatable*/ false);
            }
            mesh.useVertexColors = true;
        };

        if (positions && positions.length > 0 && indices && indices.length > 0) {

            if (this._masterMeshKeys.indexOf(key) == -1) {
                this._masterMeshKeys.push(key);
            }
            // TRIANGLES
            const mesh = new BABYLON.Mesh(key, this._babScene);

            // positions via VertexData, non-updatable
            const vd = new BABYLON.VertexData();
            vd.set(positions, BABYLON.VertexBuffer.PositionKind);
            if (normals && normals.length) vd.set(normals, BABYLON.VertexBuffer.NormalKind);
            vd.indices = indices;
            vd.applyToMesh(mesh, /*updatable*/ false);

            setColorBuffer(mesh);

            this._meshes.push(mesh);
            this._masterMeshes[key] = mesh;

            if (!this._pageToRegion[blockId]) this._pageToRegion[blockId] = {};
            if (!this._pageToRegion[blockId][pageId]) this._pageToRegion[blockId][pageId] = [];
            this._pageToRegion[blockId][pageId].push(key);


            const assignedMat = (hasVertexColors || parsedData.MaterialKey == null || parsedData.MaterialKey === "")
                ? this._materialManager.getColorVertexBackfaceMaterial()
                : this._materialManager.getGLBMaterial("GLB|" + parsedData.MaterialKey, blockId);


            // remember original for XRay toggling (store the id to restore later)
            if (assignedMat && assignedMat.id) {
                this._regionOriginalMaterial[key] = assignedMat.id;
            }
            if (this._regionXRayActive) {
                const xr = this._materialManager.getMaterial("XRayMaterial");
                mesh.useVertexColors = false;
                mesh.hasVertexAlpha = false;
                mesh.material = xr;
            } else {
                mesh.material = assignedMat;
            }
            this._regionMaterialBlock[key] = blockId;

            (mesh as any).dataSource = isPagedMesh
                ? Enumerations.PickObjectType.PagedMesh
                : (normalizedDataType === "mesh" ? Enumerations.PickObjectType.Mesh : Enumerations.PickObjectType.GLBObject);

            if (isPagedMesh) {
                this.insertMeshIntoParticleSystem(key, mesh, colors, parsedData.ObjectInfo);
            }
        }
        else if (positions && positions.length > 0) {

            // POINT CLOUD
            const pointMesh = new BABYLON.Mesh("PointCloud" + "_" + blockId + "_" + pageId + "_" + key, this._babScene);

            // positions non-updatable
            pointMesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, positions, /*updatable*/ false);
            setColorBuffer(pointMesh);

            // mark unindexed for points
            pointMesh.isUnIndexed = true;

            const pointMaterial = !this._pointXrayEnabled ? this._materialManager.getPointCloudSplatMaterialForResolutionMM(resolution) : this._materialManager.getPointCloudMaterial();
            pointMesh.material = pointMaterial;
            pointMesh.isPickable = false;
            if (!this._pageToRegion[blockId]) this._pageToRegion[blockId] = {};
            if (!this._pageToRegion[blockId][pageId]) this._pageToRegion[blockId][pageId] = [];

            const bucket = this._pageToRegion[blockId];
            const arr = bucket[pageId] as string[];

            if (arr.indexOf(key) !== -1) {
                console.warn("Duplicate _pageToRegion entry", { blockId, pageId, key, stack: new Error().stack });
                return;
            }

            if (!this._pointXrayEnabled)
                this._materialManager.assignPointCloudMaterial(pointMesh, resolution);

            pointMesh.metadata = pointMesh.metadata || {};
            pointMesh.metadata.voxelMM = resolution;      // e.g., 5, 10, 50
            pointMesh.metadata.spacingWorld = resolution / 1000;

            this._pageToRegion[blockId][pageId].push(key);

            this._masterPointClouds[key] = pointMesh;
            // Track block for completeness; point clouds use shader, may ignore XRay
            this._regionMaterialBlock[key] = blockId;

            // This is specifically used so that our alpha material gets the shader code initialized (once). If this isnt done, then
            // switching to the alpha material can cause a flicker, or black points. Note this intialization is really only done
            // once in material manager.
            this._materialManager.warmPointMaterial(pointMesh);
        }

        parsedData.Geometry = null;
        parsedData = null;
    }

    private parseRegionMeshDataOld(key: string, parsedData: any, blockId: string, pageId: string, pointSize: number) {

        var index: number;
        var cache: number;

        var geometries = parsedData.Geometry;

        if (geometries.positions.length > 0 && (geometries.indices.length > 0)) {
            var mesh = new BABYLON.Mesh(key, this._babScene);

            mesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, geometries.positions);
            mesh.setIndices(geometries.indices);
            mesh.setVerticesData(BABYLON.VertexBuffer.NormalKind, geometries.normals);

            this._masterMeshes[key] = mesh;

            if (this._pageToRegion[blockId] == null) {
                this._pageToRegion[blockId] = {};
            }

            if (this._pageToRegion[blockId][pageId] == null) {
                this._pageToRegion[blockId][pageId] = [];
            }

            this._pageToRegion[blockId][pageId].push(key);

            mesh.material = parsedData.MaterialKey == null || parsedData.MaterialKey == "" ? this._materialManager.getColorVertexMaterial() : this._materialManager.getGLBMaterial("GLB|" + parsedData.MaterialKey, blockId);

            if (geometries.colors != null && geometries.colors.length > 0) {
                mesh.setVerticesData(BABYLON.VertexBuffer.ColorKind, geometries.colors);
                mesh.useVertexColors = true;
            }

            (mesh as any).dataSource = Enumerations.PickObjectType.GLBObject;
            //this.insertMeshIntoParticleSystem(key, mesh, geometries.colors, parsedData.ObjectInfo);
        }
        else if (geometries.positions.length > 0) {

            // Create a new empty mesh
            var pointMesh = new BABYLON.Mesh("pointCloud", this._babScene);
            var vertexData = new BABYLON.VertexData();

            vertexData.set(geometries.positions, BABYLON.VertexBuffer.PositionKind);
            vertexData.set(geometries.colors, BABYLON.VertexBuffer.ColorKind);
            vertexData.set(geometries.normals, BABYLON.VertexBuffer.NormalKind);
            vertexData.applyToMesh(pointMesh, true);

            var pointMaterial = this._materialManager.getPointCloudMaterial();

            pointMesh.useVertexColors = true;

            // Assign the shader material
            pointMesh.material = pointMaterial;
            pointMesh.refreshBoundingInfo();

            if (this._pageToRegion[blockId] == null) {
                this._pageToRegion[blockId] = {};
            }

            if (this._pageToRegion[blockId][pageId] == null) {
                this._pageToRegion[blockId][pageId] = [];
            }

            this._pageToRegion[blockId][pageId].push(key);

            this._masterPointClouds[key] = pointMesh;
        }

        parsedData.Geometry = null;
        parsedData = null;
    }
    private parseMeshData(key: string, materialKey: string, parsedData: any, appearanceOverrideData: any, blockId: string, pageId: string) {

        var index: number;
        var cache: number;

        var geometries = parsedData.Geometry;

        var material = this._materialManager.getMaterial(materialKey);

        var mesh = new BABYLON.Mesh(key, this._babScene);

        mesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, geometries.positions);
        mesh.setVerticesData(BABYLON.VertexBuffer.NormalKind, geometries.normals);

        mesh.setIndices(geometries.indices);

        this._masterMeshes[key] = mesh;
        this._meshes.push(mesh);
        if (this._pageToMesh[blockId] == null) {
            this._pageToMesh[blockId] = {};
        }

        if (this._pageToMesh[blockId][pageId] == null) {
            this._pageToMesh[blockId][pageId] = [];
        }

        this._pageToMesh[blockId][pageId].push(key);

        if (geometries.colors.length > 0) {
            mesh.material = this._materialManager.getColorVertexMaterial();
            mesh.setVerticesData(BABYLON.VertexBuffer.ColorKind, geometries.colors);
        }
        else {
            mesh.material = material;
        }

        (mesh as any).dataSource = Enumerations.PickObjectType.Object;

        this.insertMeshIntoParticleSystem(key, mesh, geometries.colors, parsedData.ObjectInfo);
    }
    public containsPageData(blockId: string, pageId: string): boolean {

        return (this._pageToMesh[blockId] != null && this._pageToMesh[blockId][pageId] != null && this._pageToMesh[blockId][pageId].length > 0)
    }
    getAssetCount(blockId: string, pageId: string): number {

        var numObjects = 0;
        if (this._pageToMesh[blockId] != null && this._pageToMesh[blockId][pageId] != null) {
            var keys = this._pageToMesh[blockId][pageId];

            for (var x = 0; x < keys.length; x++) {
                var info = this._masterMeshToObjectInfo[keys[x]];

                if (info == null) {
                    continue;
                }

                numObjects += info.length;
            }
        }

        return numObjects;
    }

    public getObjectIdsByBlockPage(blockId: string, pageId: string): any[] {

        var retVal: any[] = [];

        if (this._pageToMesh[blockId] != null && this._pageToMesh[blockId][pageId] != null) {
            var keys = this._pageToMesh[blockId][pageId];

            for (var x = 0; x < keys.length; x++) {
                var info = this._masterMeshToObjectInfo[keys[x]];

                if (info == null) {
                    continue;
                }

                retVal.push(info);
            }
        }

        return retVal;
    }

    public getLoadedAssetPages(): { blockId: string; pageId: string; }[] {
        return this._getLoadedPages(this._pageToMesh);
    }

    public getLoadedRegionPages(): { blockId: string; pageId: string; }[] {
        return this._getLoadedPages(this._pageToRegion);
    }

    private _getLoadedPages(
        pagesByBlock: { [blockId: string]: { [pageId: string]: string[]; }; }
    ): { blockId: string; pageId: string; }[] {
        const loadedPages: { blockId: string; pageId: string; }[] = [];
        for (const blockId in pagesByBlock) {
            const pages = pagesByBlock[blockId];
            for (const pageId in pages) {
                if (pages[pageId]?.length) {
                    loadedPages.push({ blockId, pageId });
                }
            }
        }
        return loadedPages;
    }

    public getRegionObjectIdsByBlockPage(blockId: string, pageId: string): any[] {

        var retVal: any[] = [];

        if (this._pageToRegion[blockId] != null && this._pageToRegion[blockId][pageId] != null) {
            var keys = this._pageToRegion[blockId][pageId];

            for (var x = 0; x < keys.length; x++) {
                var info = this._masterMeshToObjectInfo[keys[x]];

                if (info == null) {
                    continue;
                }

                retVal.push(info);
            }
        }

        return retVal;
    }
    public disposePageData(blockId: string, pageId: string): number {

        var deletedGeometryLength = 0;
        var deletedKeyLength = 0;
        const flushCylinderDisposal = this._cylinderInstanceDisposalBatchDepth === 0;
        if (flushCylinderDisposal) {
            this.beginCylinderInstanceDisposalBatch();
        }

        // Queue the page independently of its group metadata. This also cleans
        // instances orphaned by an earlier stale sourceMasterKey/group mapping.
        this._pendingCylinderInstancePageDisposals[this._getObjectBlockPageKey(blockId, pageId)] = true;

        if (this._pageToMesh[blockId] != null && this._pageToMesh[blockId][pageId] != null) {
            var keys = this._pageToMesh[blockId][pageId].slice();

            for (var x = 0; x < keys.length; x++) {
                if (this._isCylinderInstanceMaster(keys[x])) {
                    const objectInfos = this._masterMeshToObjectInfo[keys[x]] || [];

                    for (let y = 0; y < objectInfos.length; y++) {
                        const objectId = objectInfos[y].objectId;
                        this._removeObjectMasterForPage(objectId, blockId, pageId, keys[x]);

                        if (!this._objectIdToMasterMeshIds[objectId] || this._objectIdToMasterMeshIds[objectId].length === 0) {
                            this._objectToAppearanceOverridesList[objectId] = null;
                        }
                    }

                    this._disposeCylinderInstanceMaster(keys[x]);
                    deletedKeyLength += (keys[x].length / 1024) / 1024;
                    continue;
                }

                var mesh = this._masterMeshes[keys[x]];
                var info = this._masterMeshToObjectInfo[keys[x]] || [];

                if (mesh) {
                    deletedGeometryLength += mesh.getTotalIndices();
                    deletedGeometryLength += mesh.getTotalVertices() * 3 * 2;
                }

                if (mesh) {
                    if (mesh.geometry) {
                        mesh.geometry.dispose();
                    }

                    mesh.dispose(false, false);


                }

                this._masterMeshes[keys[x]] = null;
                //delete this._masterMeshes[keys[x]];
                var index = this._masterMeshKeys.indexOf(keys[x]);

                if (index !== -1) {
                    this._masterMeshKeys.splice(index, 1);
                }

                if (this._masterSPS[keys[x]]) {
                    this._masterSPS[keys[x]].dispose();
                    this._masterSPS[keys[x]] = null;
                }

                for (var y = 0; y < info.length; y++) {
                    const objectId = info[y].objectId;
                    // clear per-page contribution only
                    this._removeObjectMastersForPage(objectId, blockId, pageId);

                    // if you also want to clear appearance list only when fully removed:
                    // (optional) only delete override list when object no longer exists in any master mesh
                    if (!this._objectIdToMasterMeshIds[objectId] || this._objectIdToMasterMeshIds[objectId].length === 0) {
                        this._objectToAppearanceOverridesList[objectId] = null;
                    }
                }


                this._masterMeshToObjectInfo[keys[x]] = null;
                delete this._masterMeshObjectIndex[keys[x]];
                deletedKeyLength += (keys[x].length / 1024) / 1024;
            }

            this._pageToMesh[blockId][pageId] = [];

        }

        if (flushCylinderDisposal) {
            this.endCylinderInstanceDisposalBatch();
        }

        return (((deletedGeometryLength * 32) / 8) / 1024) / 1024 + deletedKeyLength;
    }

    public hasPointCloudData(): boolean {
        const scene = (this as any)._babScene as BABYLON.Scene | undefined;
        if (!scene || !scene.meshes) return false;

        for (const m of scene.meshes) {
            if (!m || m.isDisposed() || !m.name) continue;

            // match "PointCloud…" (case-insensitive)
            if (m.name.length >= 10 && m.name.toLowerCase().startsWith("pointcloud")) {
                // If you want to ensure it's an actual drawable mesh (not an empty container),
                // keep this guard; otherwise you can return true immediately.
                const hasVerts = typeof (m as any).getTotalVertices === "function"
                    ? (m as any).getTotalVertices() > 0
                    : true; // InstancedMesh may not expose getTotalVertices

                if (hasVerts) return true;
            }
        }
        return false;
    }
    public disposeRegionData(blockId: string, pageId: string): number {

        var deletedGeometryLength = 0;
        var deletedKeyLength = 0;

        if (this._pageToRegion[blockId] != null && this._pageToRegion[blockId][pageId] != null) {
            var keys = this._pageToRegion[blockId][pageId];

            for (var x = 0; x < keys.length; x++) {
                var mesh = this._masterMeshes[keys[x]];

                if (mesh != null) {
                    var geoLength = 0;

                    this._babScene.removeMesh(mesh);
                    mesh.dispose();
                    mesh = null;
                    this._masterMeshes[keys[x]] = null;

                    var index = this._masterMeshKeys.indexOf(keys[x]);

                    this._masterMeshKeys.splice(index, 1);

                    if (this._masterSPS[keys[x]] != null) {
                        this._masterSPS[keys[x]].dispose();
                        this._masterSPS[keys[x]] = null;
                    }

                    var info = this._masterMeshToObjectInfo[keys[x]];

                    if (info != null) {
                        for (var y = 0; y < info.length; y++) {
                            this._objectToAppearanceOverridesList[info[y].objectId] = null;
                            this._objectIdToMasterMeshIds[info[y].objectId] = null;

                            deletedKeyLength += (info[y].objectId.length / 1024) / 1024;
                        }
                    }

                    this._masterMeshToObjectInfo[keys[x]] = null;
                    delete this._masterMeshObjectIndex[keys[x]];
                    delete this._regionOriginalMaterial[keys[x]];
                    delete this._regionMaterialBlock[keys[x]];
                    //deletedKeyLength += (keys[x].length / 1024) / 1024;
                }

                var pointCloud = this._masterPointClouds[keys[x]];

                if (pointCloud != null) {
                    var geoLength = 0;
                    //deletedGeometryLength += mesh.getTotalIndices();
                    //deletedGeometryLength += mesh.getTotalVertices() * 3 * 2;
                    this._babScene.removeMesh(pointCloud);


                    //this._babScene.removeMaterial(pointCloud.mesh.material);

                    if (pointCloud != null) {
                        //pointCloud.mesh.material.dispose();
                        pointCloud.dispose();
                        //pointCloud.mesh.material = null;
                        //pointCloud.mesh = null;
                    }

                    pointCloud = null;

                    //delete this._masterPointClouds[keys[x]];
                    delete this._masterPointClouds[keys[x]];
                    delete this._regionMaterialBlock[keys[x]];

                    //this._masterPointClouds[keys[x]] = null;
                }
            }
            delete this._pageToRegion[blockId][pageId];
            //this._pageToMesh[blockId][pageId] = [];

        }

        //return (((deletedGeometryLength * 32) / 8) / 1024) / 1024 + deletedKeyLength;
        return 0;
    }

    public getBoundingInfo(id: string): BABYLON.BoundingInfo {

        return this._masterMeshes[id].getBoundingInfo();
    }

    private processParticles(mesh: BABYLON.Mesh, objectData: any): BABYLON.SolidParticle[] {

        var particles: BABYLON.SolidParticle[] = new Array<BABYLON.SolidParticle>(objectData.length);
        var f: number = 0;
        var fallbackIndexStart = 0;

        var meshPos = mesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
        var facetPos: number[] = [];      // submesh positions
        var facetUV: number[] = [];       // submesh UV
        for (var x = 0; x < meshPos.length; x += 3) {

            facetPos.push(meshPos[x]);
            facetPos.push(meshPos[x + 1]);
            facetPos.push(meshPos[x + 2]);
        }

        var shape: BABYLON.Vector3[] = this._posToShape(facetPos);
        var shapeUV: number[] = this._uvsToShapeUV(facetUV);

        var vertexCount = 0;
        for (var o = 0; o < objectData.length; o++) {

            var size = objectData[o].indicesCount;
            var vertCount = objectData[o].vertexCount;
            var sourceIndexStart = typeof objectData[o].indiciesStartIndex === "number"
                ? objectData[o].indiciesStartIndex
                : fallbackIndexStart;
            f = o;


            // reset temp arrays
            facetPos.length = 0;
            facetUV.length = 0;

            // create a model shape for each single particle
            var idx: number = o;

            var modelShape = new CustomModelShape(o, shape, size, vertCount, shapeUV, null, null);

            vertexCount += size;
            var particle = new BABYLON.SolidParticle(idx, idx, objectData[o].vertexCount * 3, vertCount, modelShape as any, o, 0, this as any);
            (particle as any)._sourceIndexStart = sourceIndexStart;
            (particle as any)._sourceIndexCount = size;

            particles[o] = particle;
            fallbackIndexStart = sourceIndexStart + size;

            idx++;
        }

        return particles;
    }

    private _posToShape(positions: any): BABYLON.Vector3[] {
        var shape: any[] = [];
        for (var i = 0; i < positions.length; i += 3) {
            shape.push(new BABYLON.Vector3(positions[i], positions[i + 1], positions[i + 2]));
        }
        return shape;
    }

    // returns a shapeUV array from a Vector4 uvs
    private _uvsToShapeUV(uvs: any): number[] {
        var shapeUV: any[] = [];
        if (uvs) {
            for (var i = 0; i < uvs.length; i++)
                shapeUV.push(uvs[i]);
        }
        return shapeUV;
    }

    private updateMeshObjectInformation(masterMeshId: string, minExtents: BABYLON.Vector3, maxExtents: BABYLON.Vector3, objectId: string, meshes: BABYLON.Mesh[]) {

        var totalVerts = 0;
        var totalInd = 0;
        var totalUvs = 0;

        var vertexArray = this._masterMeshToObjectInfo[masterMeshId];
        if (vertexArray == null) {
            vertexArray = [];
            this._masterMeshToObjectInfo[masterMeshId] = vertexArray;
            this._masterMeshObjectIndex[masterMeshId] = {};
        }

        if (this._masterMeshKeys.indexOf(masterMeshId) == -1) {
            this._masterMeshKeys.push(masterMeshId);
        }

        var tailIndices = vertexArray.length > 0 ? vertexArray[vertexArray.length - 1].indiciesStartIndex + vertexArray[vertexArray.length - 1].indicesCount : 0;
        var tailVertexIndex = vertexArray.length > 0 ? vertexArray[vertexArray.length - 1].vertexStartIndex + vertexArray[vertexArray.length - 1].vertexCount : 0;
        var tailUvIndex = vertexArray.length > 0 ? vertexArray[vertexArray.length - 1].uvStartIndex + vertexArray[vertexArray.length - 1].uvCount : 0;

        var insertIndices = 0;
        var insertVertexIndex = 0;
        var insertUvIndex = 0;

        for (var i = 0; i < meshes.length; i++) {
            var totalFaces = meshes[i].getTotalIndices() / 3;

            var uvBuffer = meshes[i].getVertexBuffer(BABYLON.VertexBuffer.UVKind);
            var uvs = uvBuffer ? meshes[i].getVertexBuffer(BABYLON.VertexBuffer.UVKind).getData() : null;


            insertIndices += meshes[i].getTotalIndices();
            insertVertexIndex += meshes[i].getTotalVertices();
            insertUvIndex += uvs ? meshes[i].getVertexBuffer(BABYLON.VertexBuffer.UVKind).getSize() : 0;
        }

        var faceInfo = new SubMeshObjectInfo();

        faceInfo.objectId = objectId;
        faceInfo.indiciesStartIndex = tailIndices;
        faceInfo.vertexStartIndex = tailVertexIndex;
        faceInfo.uvStartIndex = tailUvIndex;
        faceInfo.indicesCount = insertIndices;
        faceInfo.vertexCount = insertVertexIndex;
        faceInfo.uvCount = insertUvIndex;
        faceInfo.minExtents = minExtents;
        faceInfo.maxExtents = maxExtents;

        vertexArray.push(faceInfo);
        this._recordMasterMeshObjectIndex(masterMeshId, objectId, vertexArray.length - 1);
    }

    private clearVertexIndexes() {
        for (var i = 0; i < this._masterMeshKeys.length; i++) {
            this._masterMeshToObjectInfo[this._masterMeshKeys[i]] = [];
            this._masterMeshObjectIndex[this._masterMeshKeys[i]] = {};
        }

        this._masterMeshKeys = [];
    }
    private addToInstancedMeshes(meshId: string, mesh: BABYLON.InstancedMesh) {
        // Removed because of gutted memory.
    }

    private addToMeshes(meshId: string, mesh: BABYLON.Mesh): string {

        var meshArray = this._meshesToMerge[meshId];
        if (meshArray == null) {
            meshArray = [];
            this._meshesToMerge[meshId] = meshArray;
        }


        if (this._masterMeshKeys.indexOf(meshId) == -1) {
            this._masterMeshKeys.push(meshId);
        }

        meshArray.push(mesh);

        return meshId;
    }

    public updateMeshExtents(boundingInfo: BABYLON.BoundingInfo) {
        if (this._sceneContentExtents == null)
            this._sceneContentExtents = new BABYLON.BoundingBox(new BABYLON.Vector3(boundingInfo.minimum.x - 1, boundingInfo.minimum.y - 1, boundingInfo.minimum.z - 1), new BABYLON.Vector3(boundingInfo.maximum.x + 1, boundingInfo.maximum.y + 1, boundingInfo.maximum.z + 1));
        else {

            if (this._sceneContentExtents.minimum.x > boundingInfo.minimum.x) {
                this._sceneContentExtents.minimum.x = boundingInfo.minimum.x;
            }
            if (this._sceneContentExtents.minimum.y > boundingInfo.minimum.y) {
                this._sceneContentExtents.minimum.y = boundingInfo.minimum.y;
            }
            if (this._sceneContentExtents.minimum.z > boundingInfo.minimum.z) {
                this._sceneContentExtents.minimum.z = boundingInfo.minimum.z;
            }

            if (this._sceneContentExtents.maximum.x < boundingInfo.maximum.x) {
                this._sceneContentExtents.maximum.x = boundingInfo.maximum.x;
            }
            if (this._sceneContentExtents.maximum.y < boundingInfo.maximum.y) {
                this._sceneContentExtents.maximum.y = boundingInfo.maximum.y;
            }
            if (this._sceneContentExtents.maximum.z < boundingInfo.maximum.z) {
                this._sceneContentExtents.maximum.z = boundingInfo.maximum.z;
            }
        }


    }

    private degToRad(deg: number): number {
        return (deg * Math.PI) / 180;
    }
}

// Class used for to keep track of object info with respect to the master meshes.
class SubMeshObjectInfo {
    public vertexStartIndex: number;
    public vertexCount: number;
    public indiciesStartIndex: number;
    public indicesCount: number;
    public uvStartIndex: number;
    public uvCount: number;
    public objectId: string;
    public minExtents: BABYLON.Vector3;
    public maxExtents: BABYLON.Vector3;
    public materialOverride: string;
}

interface CylinderInstanceGroupData {
    masterMeshId: string;
    sourceMasterKey: string;
    blockId: string;
    pageId: string;
    materialKey: string;
    complexityLevel: number;
    objectIds: { [objectId: string]: boolean; };
}

interface CylinderThinInstanceEntries {
    objectIds: string[];
    pageKeys: string[];
}

interface PendingCylinderThinInstanceEntry {
    objectId: string;
    blockId: string;
    pageId: string;
    matrixData: Float32Array;
}

interface CylinderInstanceObjectBounds {
    minExtents: BABYLON.Vector3;
    maxExtents: BABYLON.Vector3;
}

interface CylinderInstanceMasterData {
    masterMeshId: string;
    materialKey: string;
    complexityLevel: number;
    sourceMesh: BABYLON.Mesh | null;
    entries: CylinderThinInstanceEntries;
    matrixBuffer: Float32Array | null;
    objectEntryCounts: { [objectId: string]: number; };
    objectBounds: { [objectId: string]: CylinderInstanceObjectBounds; };
}

class CustomModelShape {
    /**
         * The shape id
         * @hidden
         */
    public shapeID: number;
    /**
     * flat array of model positions (internal use)
     * @hidden
     */
    public _shape: BABYLON.Vector3[];
    /**
     * flat array of model UVs (internal use)
     * @hidden
     */
    public _shapeUV: number[];
    /**
     * length of the shape in the model indices array (internal use)
     * @hidden
     */
    public _indicesLength: number = 0;
    public _vertexLength: number = 0;

    /**_vertexLength
     * Custom position function (internal use)
     * @hidden
     */
    public _positionFunction: BABYLON.Nullable<(particle: BABYLON.SolidParticle, i: number, s: number) => void>;
    /**
     * Custom vertex function (internal use)
     * @hidden
     */
    public _vertexFunction: BABYLON.Nullable<(particle: BABYLON.SolidParticle, vertex: BABYLON.Vector3, i: number) => void>;

    /**
     * Creates a ModelShape object. This is an internal simplified reference to a mesh used as for a model to replicate particles from by the SPS.
     * SPS internal tool, don't use it manually.
     * @hidden
     */
    constructor(id: number, shape: BABYLON.Vector3[], indicesLength: number, vertexLength: number, shapeUV: number[],
        posFunction: BABYLON.Nullable<(particle: BABYLON.SolidParticle, i: number, s: number) => void>, vtxFunction: BABYLON.Nullable<(particle: BABYLON.SolidParticle, vertex: BABYLON.Vector3, i: number) => void>) {
        this.shapeID = id;
        this._shape = shape;
        this._indicesLength = indicesLength;
        this._vertexLength = vertexLength;
        this._shapeUV = shapeUV;
        this._positionFunction = posFunction;
        this._vertexFunction = vtxFunction;
    }
}

const depthSortFunction = (p1: BABYLON.DepthSortedParticle, p2: BABYLON.DepthSortedParticle) => p2.sqDistance - p1.sqDistance;

class CustomSolidParticleSystem {
    private static readonly _NOOP_UPDATE_PARTICLE = function (particle: BABYLON.SolidParticle): BABYLON.SolidParticle {
        return particle;
    };

    private static readonly _NOOP_AFTER_UPDATE_PARTICLES = function (): void {
    };

    // public members
    /**
    *  The SPS array of Solid Particle objects. Just access each particle as with any classic array.
    *  Example : var p = SPS.particles[i];
    */
    public particles: BABYLON.SolidParticle[] = new Array<BABYLON.SolidParticle>();
    /**
    * The SPS total number of particles. Read only. Use SPS.counter instead if you need to set your own value.
    */
    public nbParticles: number = 0;
    /**
    * If the particles must ever face the camera (default false). Useful for planar particles.
    */
    public billboard: boolean = false;
    /**
    * This a counter ofr your own usage. It's not set by any SPS functions.
    */
    public counter: number = 0;
    /**
    * The SPS name. This name is also given to the underlying mesh.
    */
    public name: string;
    public shapeData: any;
    /**
    * The SPS mesh. It's a standard BJS Mesh, so all the methods from the Mesh class are avalaible.
    */
    public mesh: BABYLON.Mesh;
    /**
    * This empty object is intended to store some SPS specific or temporary values in order to lower the Garbage Collector activity.
    * Please read : http://doc.babylonjs.com/overviews/Solid_Particle_System#garbage-collector-concerns
    */
    public vars: any = {};
    /**
    * This array is populated when the SPS is set as 'pickable'.
    * Each key of this array is a `faceId` value that you can get from a pickResult object.
    * Each element of this array is an object `{idx: int, faceId: int}`.
    * `idx` is the picked particle index in the `SPS.particles` array
    * `faceId` is the picked face index counted within this particle.
    * Please read : http://doc.babylonjs.com/overviews/Solid_Particle_System#pickable-particles
    */
    public pickedParticles: { idx: number; faceId: number }[];

    public recomputeNormals: boolean = true;

    /**
     * If the particle intersection must be computed only with the bounding sphere (no bounding box computation, so faster). (Internal use only)
     * @hidden
     */
    public _bSphereOnly: boolean = false;
    /**
     * A number to multiply the boundind sphere radius by in order to reduce it for instance. (Internal use only)
     * @hidden
     */
    public _bSphereRadiusFactor: number = 1.0;

    // private members
    private _scene: BABYLON.Scene;
    private _camInvertedPosition: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _minBbox: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _maxBbox: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _vertex: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _depthSortFunction: (p1: BABYLON.DepthSortedParticle, p2: BABYLON.DepthSortedParticle) => number =
        function (p1, p2) {
            return (p2.sqDistance - p1.sqDistance);
        };
    private _pivotBackTranslation: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _scaledPivot: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _particleHasParent: boolean = false;
    private _parent: BABYLON.SolidParticle;

    private _positions: number[] = new Array<number>();
    private _indices: number[] = new Array<number>();
    private _normals: number[] = new Array<number>();
    private _colors: number[] = new Array<number>();
    private _uvs: number[] = new Array<number>();
    private _colors32: Float32Array;
    private _origColors: Float32Array;
    private _uvs32: Float32Array;
    private _index: number = 0;  // indices index
    private _updatable: boolean = true;
    private _pickable: boolean = false;
    private _isVisibilityBoxLocked = false;
    private _alwaysVisible: boolean = false;
    private _shapeCounter: number = 0;
    private _copy: BABYLON.SolidParticle = new BABYLON.SolidParticle(0, 0, 0, 0, null, 0, 0, this as any);
    private _shape: any;
    private _shapeUV: number[];
    private _color: BABYLON.Color4 = new BABYLON.Color4(0, 0, 0, 0);
    private _computeParticleColor: boolean = false;
    private _hasVertexColor: boolean = false;
    private _computeParticleTexture: boolean = false;
    private _computeParticleRotation: boolean = false;
    private _containsMixedAlpha: boolean = false;
    private _computeParticleVertex: boolean = false;
    private _computeBoundingBox: boolean = false;
    private _cam_axisZ: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _cam_axisY: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _cam_axisX: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _axisX: BABYLON.Vector3 = BABYLON.Axis.X;
    private _axisY: BABYLON.Vector3 = BABYLON.Axis.Y;
    private _axisZ: BABYLON.Vector3 = BABYLON.Axis.Z;
    private _camera: BABYLON.TargetCamera;
    private _particle: BABYLON.SolidParticle;
    private _camDir: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _rotMatrix: BABYLON.Matrix = new BABYLON.Matrix();
    private _invertMatrix: BABYLON.Matrix = new BABYLON.Matrix();
    private _rotated: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _quaternion: BABYLON.Quaternion = new BABYLON.Quaternion();
    private _normal: BABYLON.Vector3 = BABYLON.Vector3.Zero();
    private _yaw: number = 0.0;
    private _pitch: number = 0.0;
    private _roll: number = 0.0;
    private _halfroll: number = 0.0;
    private _halfpitch: number = 0.0;
    private _halfyaw: number = 0.0;
    private _sinRoll: number = 0.0;
    private _cosRoll: number = 0.0;
    private _sinPitch: number = 0.0;
    private _cosPitch: number = 0.0;
    private _sinYaw: number = 0.0;
    private _cosYaw: number = 0.0;
    private _w: number = 0.0;
    private _minimum: BABYLON.Vector3 = BABYLON.TmpVectors.Vector3[0];
    private _maximum: BABYLON.Vector3 = BABYLON.TmpVectors.Vector3[1];
    private _multiMat: BABYLON.MultiMaterial;
    // Materials resolved by MaterialManager are shared across SPS meshes. Only
    // locally-created placeholders/fallbacks belong to this SPS and may be disposed here.
    private _ownedAssignmentMaterials: BABYLON.Material[] = [];

    private _indices32: BABYLON.IndicesArray;           // used as depth sorted array if depth sort enabled, else used as typed indices
    private _positions32: Float32Array;         // updated positions for the VBO
    private _normals32: Float32Array;           // updated normals for the VBO
    private _fixedNormal32: Float32Array;       // initial normal reference
    private _depthSort: boolean = false;
    private _depthSortParticles: boolean = true;
    private _mustUnrotateFixedNormals = false;
    private _particlesIntersect: boolean = false;
    private _needs32Bits: boolean = false;
    private _particleMaterials: BABYLON.Material[] = [];
    private _materialRunsSignature: string = null;
    private _dirtyMaterialTopology: boolean = true;
    private _dirtyParticleColors: boolean = false;
    private _dirtyParticleVertices: boolean = false;
    private _dirtyParticleTextures: boolean = false;
    public particlesBuilt: boolean = false;
    public depthSortedParticles: BABYLON.DepthSortedParticle[];

    /**
    * Creates a SPS (Solid Particle System) object.
    * `name` (String) is the SPS name, this will be the underlying mesh name.
    * `scene` (Scene) is the scene in which the SPS is added.
    * `updatable` (default true) : if the SPS must be updatable or immutable.
    * `isPickable` (default false) : if the solid particles must be pickable.
    */
    constructor(name: string, scene: BABYLON.Scene, options?: { vertexColors?: any; updatable?: boolean; isPickable?: boolean }) {

        this.name = name;
        this._scene = scene;
        this._camera = <BABYLON.TargetCamera>scene.activeCamera;
        this._pickable = options ? options.isPickable : false;
        if (options && options.updatable) {
            this._updatable = options.updatable;
        } else {
            this._updatable = true;
        }
        if (this._pickable) {
            this.pickedParticles = [];
        }
        if (this._depthSort) {
            this.depthSortedParticles = [];
        }
        if (options.vertexColors != null && options.vertexColors.length > 0) {
            this._origColors = options.vertexColors;
            this._hasVertexColor = true;
            this.computeParticleColor = true;
        }
    }

    public runParticleUpdate(updateParticle: (particle: BABYLON.SolidParticle) => BABYLON.SolidParticle, afterUpdateParticles?: () => void, start: number = 0, end: number = this.nbParticles - 1, update: boolean = true): void {
        const previousUpdateParticle = (this as any).updateParticle;
        const previousAfterUpdateParticles = (this as any).afterUpdateParticles;

        this.updateParticle = updateParticle || CustomSolidParticleSystem._NOOP_UPDATE_PARTICLE;
        this.afterUpdateParticles = afterUpdateParticles || CustomSolidParticleSystem._NOOP_AFTER_UPDATE_PARTICLES;

        try {
            this.setParticles(start, end, update);
        }
        finally {
            if (previousUpdateParticle && previousUpdateParticle !== CustomSolidParticleSystem._NOOP_UPDATE_PARTICLE) {
                this.updateParticle = previousUpdateParticle;
            }
            else {
                delete (this as any).updateParticle;
            }

            if (previousAfterUpdateParticles && previousAfterUpdateParticles !== CustomSolidParticleSystem._NOOP_AFTER_UPDATE_PARTICLES) {
                this.afterUpdateParticles = previousAfterUpdateParticles;
            }
            else {
                delete (this as any).afterUpdateParticles;
            }
        }
    }

    public markMaterialTopologyDirty(): void {
        this._dirtyMaterialTopology = true;
    }

    public markParticleColorsDirty(): void {
        this._dirtyParticleColors = true;
    }

    public markParticleVerticesDirty(): void {
        this._dirtyParticleVertices = true;
    }

    public markParticleTexturesDirty(): void {
        this._dirtyParticleTextures = true;
    }

    public clearDirtyFlags(): void {
        this._dirtyMaterialTopology = false;
        this._dirtyParticleColors = false;
        this._dirtyParticleVertices = false;
        this._dirtyParticleTextures = false;
    }

    public invalidateMaterialTopologyCache(): void {
        this._materialRunsSignature = null;
        this._dirtyMaterialTopology = true;
    }

    public setOwnedMeshMaterial(material: BABYLON.Material): void {
        this.mesh.material = material;
        if (material && this._ownedAssignmentMaterials.indexOf(material) === -1) {
            this._ownedAssignmentMaterials.push(material);
        }
    }

    private _createOwnedFallbackMaterial(name: string, source?: BABYLON.Material): BABYLON.StandardMaterial {
        const fallback = new BABYLON.StandardMaterial(name, this.mesh._scene);
        this._copyMaterialToStandard(fallback, source);
        this._ownedAssignmentMaterials.push(fallback);
        return fallback;
    }

    private _resolveMaterialColor(material: any): BABYLON.Color3 {
        if (material?.diffuseColor) {
            return material.diffuseColor;
        }

        if (material?.albedoColor) {
            return material.albedoColor;
        }

        if (material?.baseColor) {
            return material.baseColor;
        }

        return BABYLON.Color3.White();
    }

    private _copyMaterialToStandard(target: BABYLON.StandardMaterial, source: any): void {
        if (!target || !source) {
            return;
        }

        target.wireframe = !!source.wireframe;

        if (typeof source.specularPower === "number") {
            target.specularPower = source.specularPower;
        }

        if (source.specularColor) {
            target.specularColor = source.specularColor;
        }

        if (source.emissiveColor) {
            target.emissiveColor = source.emissiveColor;
        }

        if (source.ambientColor) {
            target.ambientColor = source.ambientColor;
        }

        target.diffuseColor = this._resolveMaterialColor(source);
        target.alpha = typeof source.alpha === "number" ? source.alpha : 1;
    }

    private _materialSignature(material: BABYLON.Material): string {
        if (!material) {
            return "null";
        }

        if (!(material instanceof BABYLON.StandardMaterial)) {
            return [
                material.getClassName(),
                material.id,
                material.uniqueId,
                material.wireframe ? 1 : 0,
                material.alpha
            ].join("|");
        }

        return [
            material.name,
            material.wireframe ? 1 : 0,
            material.specularPower,
            material.diffuseColor?.r ?? 0,
            material.diffuseColor?.g ?? 0,
            material.diffuseColor?.b ?? 0,
            material.specularColor?.r ?? 0,
            material.specularColor?.g ?? 0,
            material.specularColor?.b ?? 0,
            material.ambientColor?.r ?? 0,
            material.ambientColor?.g ?? 0,
            material.ambientColor?.b ?? 0,
            material.emissiveColor?.r ?? 0,
            material.emissiveColor?.g ?? 0,
            material.emissiveColor?.b ?? 0,
            material.alpha
        ].join("|");
    }

    private _hasSameMaterialSignature(left: BABYLON.Material, right: BABYLON.Material): boolean {
        return this._materialSignature(left) === this._materialSignature(right);
    }

    private _buildMaterialRunsSignature(): string {
        if (!this.particles || this.particles.length === 0) {
            return "";
        }

        let signature = "";
        let currentSignature = this._materialSignature((this.particles[0] as any).material);
        let runLength = 1;

        for (let particleIndex = 1; particleIndex < this.nbParticles; particleIndex++) {
            const nextSignature = this._materialSignature((this.particles[particleIndex] as any).material);
            if (nextSignature === currentSignature) {
                runLength++;
                continue;
            }

            signature += currentSignature + ":" + runLength + ";";
            currentSignature = nextSignature;
            runLength = 1;
        }

        return signature + currentSignature + ":" + runLength + ";";
    }

    private _hasUniformMaterialSignature(): boolean {
        if (!this.particles || this.nbParticles === 0) {
            return false;
        }

        const firstSignature = this._materialSignature((this.particles[0] as any).material);
        for (let particleIndex = 1; particleIndex < this.nbParticles; particleIndex++) {
            if (this._materialSignature((this.particles[particleIndex] as any).material) !== firstSignature) {
                return false;
            }
        }

        return true;
    }

    private _disposeMaterialAssignments(): void {
        this.mesh.subMeshes = [];
        this.mesh.material = null;

        if (this._multiMat) {
            // The child materials are borrowed from MaterialManager's shared pool.
            this._multiMat.dispose(false, false, false);
            this._multiMat = null;
        }

        for (let materialIndex = 0; materialIndex < this._ownedAssignmentMaterials.length; materialIndex++) {
            this._ownedAssignmentMaterials[materialIndex].dispose();
        }
        this._ownedAssignmentMaterials = [];
    }

    private _applyMaterialAssignments(): void {
        const currentRunsSignature = this._buildMaterialRunsSignature();
        if (!this._dirtyMaterialTopology && this._materialRunsSignature === currentRunsSignature) {
            return;
        }

        this._disposeMaterialAssignments();

        if (this._hasUniformMaterialSignature()) {
            const uniformMaterial = (this.particles[0] as any).material;
            const resolvedUniformMaterial = uniformMaterial
                || this._createOwnedFallbackMaterial(this.mesh.id + "_RBase");

            new BABYLON.SubMesh(0, 0, this.mesh.getTotalVertices(), 0, this.mesh.getTotalIndices(), this.mesh);
            this.mesh.material = resolvedUniformMaterial;
            this.mesh.hasVertexAlpha = false;
            this._materialRunsSignature = currentRunsSignature;
            this._dirtyMaterialTopology = false;
            return;
        }

        if (this.containsMultiMaterials()) {
            this._multiMat = new BABYLON.MultiMaterial(this.mesh.id + "MULTI", this.mesh._scene);
            const subMats: BABYLON.Material[] = [];
            const totalIndices = this.mesh.getTotalIndices();
            let matInd = 0;
            let runStart = -1;
            let runCount = 0;
            let runEndExclusive = -1;
            let runMaterial: BABYLON.Material = null;

            const emitSubMesh = (material: BABYLON.Material, indexStart: number, indexCount: number, particleIndex: number): void => {
                if (indexStart < 0 || indexCount <= 0) {
                    console.warn("SPS material topology produced an invalid submesh range", {
                        meshId: this.mesh.id,
                        materialIndex: matInd,
                        materialName: material?.name,
                        indexStart: indexStart,
                        requestedIndexCount: indexCount,
                        totalIndices: totalIndices,
                        particleIndex: particleIndex,
                    });
                    return;
                }

                const remainingIndices = Math.max(totalIndices - indexStart, 0);
                const safeIndexCount = Math.min(indexCount, remainingIndices);

                if (safeIndexCount <= 0) {
                    console.warn("SPS material topology produced an invalid submesh range", {
                        meshId: this.mesh.id,
                        materialIndex: matInd,
                        materialName: material?.name,
                        indexStart: indexStart,
                        requestedIndexCount: indexCount,
                        totalIndices: totalIndices,
                        particleIndex: particleIndex,
                    });
                    return;
                }

                if (material != null) {
                    subMats.push(material);
                }
                else {
                    subMats.push(this._createOwnedFallbackMaterial("PVC"));
                }

                new BABYLON.SubMesh(matInd, 0, this.mesh.getTotalVertices(), indexStart, safeIndexCount, this.mesh);
                matInd++;
            };

            for (let particleIndex = 0; particleIndex < this.nbParticles; particleIndex++) {
                const part = this.particles[particleIndex];
                const material = (part as any).material as BABYLON.Material;
                const partIndexStart = typeof (part as any)._sourceIndexStart === "number"
                    ? (part as any)._sourceIndexStart
                    : runEndExclusive >= 0 ? runEndExclusive : 0;
                const partIndexCount = typeof (part as any)._sourceIndexCount === "number"
                    ? (part as any)._sourceIndexCount
                    : (part._model as any)._indicesLength;
                const sameMaterialAsRun = runMaterial != null
                    && this._hasSameMaterialSignature(runMaterial, material)
                    && ((runMaterial?.name === "VertexColorMaterial") === (material?.name === "VertexColorMaterial"));
                const continuesCurrentRun = runStart >= 0
                    && sameMaterialAsRun
                    && partIndexStart === runEndExclusive;

                if (runStart >= 0 && !continuesCurrentRun) {
                    emitSubMesh(runMaterial, runStart, runCount, particleIndex - 1);
                    runStart = -1;
                    runCount = 0;
                    runEndExclusive = -1;
                    runMaterial = null;
                }

                if (runStart < 0) {
                    runStart = partIndexStart;
                    runCount = partIndexCount;
                    runEndExclusive = partIndexStart + partIndexCount;
                    runMaterial = material;
                    continue;
                }

                runCount += partIndexCount;
                runEndExclusive = partIndexStart + partIndexCount;
            }

            if (runStart >= 0) {
                emitSubMesh(runMaterial, runStart, runCount, this.nbParticles - 1);
            }

            let consumedIndices = 0;
            for (let subMeshIndex = 0; subMeshIndex < this.mesh.subMeshes.length; subMeshIndex++) {
                consumedIndices += this.mesh.subMeshes[subMeshIndex].indexCount;
            }

            if (consumedIndices !== totalIndices) {
                console.warn("SPS material topology did not consume the full index buffer", {
                    meshId: this.mesh.id,
                    consumedIndices: consumedIndices,
                    totalIndices: totalIndices,
                    particleCount: this.nbParticles,
                });
            }

            this.mesh.hasVertexAlpha = false;
            this._multiMat.subMaterials = subMats;
            this.mesh.material = this._multiMat;
        }
        else {
            const currentMaterial = (this.particles[0] as any).material;
            const singleMaterial = currentMaterial
                || this._createOwnedFallbackMaterial(this.mesh.id + "_RBase");

            new BABYLON.SubMesh(0, 0, this.mesh.getTotalVertices(), 0, this.mesh.getTotalIndices(), this.mesh);
            this.mesh.material = singleMaterial;
        }

        this._materialRunsSignature = currentRunsSignature;
        this._dirtyMaterialTopology = false;
    }

    public containsMultiMaterials(): boolean {

        var material = (this.particles[0] as any).material as BABYLON.Material;
        var materialSignature = this._materialSignature(material);

        for (var n = 0; n < this.nbParticles; n++) {
            var partMaterial = (this.particles[n] as any).material as BABYLON.Material;

            if (this._materialSignature(partMaterial) !== materialSignature) {
                return true;
            }
        }

        return false;

    }
    /**
    * Builds the SPS underlying mesh. Returns a standard Mesh.
    * If no model shape was added to the SPS, the returned mesh is just a single triangular plane.
    */
    public setMesh(m: BABYLON.Mesh, shapeData: any): BABYLON.Mesh {

        //if (this.nbParticles === 0) {
        //    var triangle = BABYLON.MeshBuilder.CreateDisc("", { radius: 1, tessellation: 3 }, this._scene);
        //    this.addShape(triangle, 1);
        //    triangle.dispose();
        //}
        //this._positions32 = new Float32Array(this._positions);
        //this._uvs32 = new Float32Array(this._uvs);
        //this._colors32 = new Float32Array(this._colors);
        //BABYLON.VertexData.ComputeNormals(this._positions32, this._indices, this._normals);
        //this._normals32 = new Float32Array(this._normals);
        //this._fixedNormal32 = new Float32Array(this._normals);
        //var vertexData = new BABYLON.VertexData();
        //vertexData.set(this._positions32, BABYLON.VertexBuffer.PositionKind);
        //vertexData.indices = this._indices;
        //vertexData.set(this._normals32, BABYLON.VertexBuffer.NormalKind);
        //if (this._uvs32) {
        //    vertexData.set(this._uvs32, BABYLON.VertexBuffer.UVKind);;
        //}
        //if (this._colors32) {
        //    vertexData.set(this._colors32, BABYLON.VertexBuffer.ColorKind);
        //}
        //var mesh = new BABYLON.Mesh(this.name, this._scene);
        //vertexData.applyToMesh(mesh, this._updatable);
        //this.mesh = mesh;
        //this.mesh.isPickable = this._pickable;

        //// free memory
        //this._positions = null;
        //this._normals = null;
        //this._uvs = null;
        //this._colors = null;

        //if (!this._updatable) {
        //    this.particles.length = 0;
        //}

        this.mesh = m;
        this.shapeData = shapeData;
        return m;
    }

    public buildMesh(): BABYLON.Mesh {
        if (this.nbParticles === 0) {
            var triangle = BABYLON.MeshBuilder.CreateDisc("", { radius: 1, tessellation: 3 }, this._scene);
            this.addShape(triangle, 1);
            triangle.dispose();
        }
        this._indices32 = (this._needs32Bits) ? new Uint32Array(this._indices) : new Uint16Array(this._indices);
        this._positions32 = new Float32Array(this._positions);
        this._uvs32 = new Float32Array(this._uvs);
        this._colors32 = new Float32Array(this._colors);

        if (this.recomputeNormals) {
            BABYLON.VertexData.ComputeNormals(this._positions32, this._indices32, this._normals);
        }
        this._normals32 = new Float32Array(this._normals);
        this._fixedNormal32 = new Float32Array(this._normals);
        if (this._mustUnrotateFixedNormals) {  // the particles could be created already rotated in the mesh with a positionFunction
            this._unrotateFixedNormals();
        }

        // Only setting the positions, since that is all the current SPS will modify. Note that colors are now handled in the sub meshes, and not the color32 buffer,
        // so thost are never made updatable.
        this.mesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, this._positions32, true);
        this.mesh.isPickable = this._pickable;

        // free memory
        if (!this._depthSort) {
            (<any>this._indices) = null;
        }
        (<any>this._positions) = null;
        (<any>this._normals) = null;
        (<any>this._uvs) = null;
        (<any>this._colors) = null;

        if (!this._updatable) {
            this.particles.length = 0;
        }

        return this.mesh;
    }


    /**
    * Digests the mesh and generates as many solid particles in the system as wanted. Returns the SPS.
    * These particles will have the same geometry than the mesh parts and will be positioned at the same localisation than the mesh original places.
    * Thus the particles generated from `digest()` have their property `position` set yet.
    * `mesh` ( Mesh ) is the mesh to be digested
    * `facetNb` (optional integer, default 1) is the number of mesh facets per particle, this parameter is overriden by the parameter `number` if any
    * `delta` (optional integer, default 0) is the random extra number of facets per particle , each particle will have between `facetNb` and `facetNb + delta` facets
    * `number` (optional positive integer) is the wanted number of particles : each particle is built with `mesh_total_facets / number` facets
    */
    public digest(mesh: BABYLON.Mesh, options?: { facetNb?: number; number?: number; delta?: number }): CustomSolidParticleSystem {

        return this;
    }

    //reset copy
    private _resetCopy() {
        this._copy.position.x = 0;
        this._copy.position.y = 0;
        this._copy.position.z = 0;
        this._copy.rotation.x = 0;
        this._copy.rotation.y = 0;
        this._copy.rotation.z = 0;
        this._copy.rotationQuaternion = null;
        this._copy.scaling.x = 1;
        this._copy.scaling.y = 1;
        this._copy.scaling.z = 1;
        this._copy.uvs.x = 0;
        this._copy.uvs.y = 0;
        this._copy.uvs.z = 1;
        this._copy.uvs.w = 1;
        this._copy.color = null;
    }

    private _meshBuilder(p: any, shape: any, positions: any, meshInd: any, indices: any, meshUV: any, uvs: any, meshCol: any, colors: any, meshNor: any, normals: any, idx: any, idxInShape: any, options: any): BABYLON.SolidParticle {
        var i;
        var u = 0;
        var c = 0;
        var n = 0;

        this._resetCopy();
        if (options && options.positionFunction) {        // call to custom positionFunction
            options.positionFunction(this._copy, idx, idxInShape);
            this._mustUnrotateFixedNormals = true;
        }

        if (this._copy.rotationQuaternion) {
            this._quaternion.copyFrom(this._copy.rotationQuaternion);
        } else {
            this._yaw = this._copy.rotation.y;
            this._pitch = this._copy.rotation.x;
            this._roll = this._copy.rotation.z;
            this._quaternionRotationYPR();
        }
        this._quaternionToRotationMatrix();

        for (i = 0; i < shape.length; i++) {
            this._vertex.x = shape[i].x;
            this._vertex.y = shape[i].y;
            this._vertex.z = shape[i].z;

            if (options && options.vertexFunction) {
                options.vertexFunction(this._copy, this._vertex, i);
            }

            this._vertex.x *= this._copy.scaling.x;
            this._vertex.y *= this._copy.scaling.y;
            this._vertex.z *= this._copy.scaling.z;

            BABYLON.Vector3.TransformCoordinatesToRef(this._vertex, this._rotMatrix, this._rotated);
            positions.push(this._copy.position.x + this._rotated.x, this._copy.position.y + this._rotated.y, this._copy.position.z + this._rotated.z);
            if (meshUV) {
                uvs.push((this._copy.uvs.z - this._copy.uvs.x) * meshUV[u] + this._copy.uvs.x, (this._copy.uvs.w - this._copy.uvs.y) * meshUV[u + 1] + this._copy.uvs.y);
                u += 2;
            }

            if (this._copy.color) {
                this._color = this._copy.color;
            } else if (meshCol && meshCol[c] !== undefined) {
                this._color.r = meshCol[c];
                this._color.g = meshCol[c + 1];
                this._color.b = meshCol[c + 2];
                this._color.a = meshCol[c + 3];
            } else {
                this._color.r = 1;
                this._color.g = 1;
                this._color.b = 1;
                this._color.a = 1;
            }
            colors.push(this._color.r, this._color.g, this._color.b, this._color.a);
            c += 4;

            if (!this.recomputeNormals && meshNor) {
                this._normal.x = meshNor[n];
                this._normal.y = meshNor[n + 1];
                this._normal.z = meshNor[n + 2];
                BABYLON.Vector3.TransformNormalToRef(this._normal, this._rotMatrix, this._normal);
                normals.push(this._normal.x, this._normal.y, this._normal.z);
                n += 3;
            }

        }

        for (i = 0; i < meshInd.length; i++) {
            indices.push(p + meshInd[i]);
        }

        if (this._pickable) {
            var nbfaces = meshInd.length / 3;
            for (i = 0; i < nbfaces; i++) {
                this.pickedParticles.push({ idx: idx, faceId: i });
            }
        }
        return this._copy;
    }

    // returns a shape array from positions array
    private _posToShape(positions: any): BABYLON.Vector3[] {
        var shape: any = [];
        for (var i = 0; i < positions.length; i += 3) {
            shape.push(new BABYLON.Vector3(positions[i], positions[i + 1], positions[i + 2]));
        }
        return shape;
    }

    // returns a shapeUV array from a Vector4 uvs
    private _uvsToShapeUV(uvs: any): number[] {
        var shapeUV: any = [];
        if (uvs) {
            for (var i = 0; i < uvs.length; i++)
                shapeUV.push(uvs[i]);
        }
        return shapeUV;
    }

    // adds a new particle object in the particles array
    private _addParticle(idx: number, idxpos: number, idxind: number, model: BABYLON.ModelShape, shapeId: number, idxInShape: number): BABYLON.SolidParticle {
        var sp = new BABYLON.SolidParticle(idx, idx, idxpos, idxind, model as any, shapeId, idxInShape, this as any);
        this.particles.push(sp);
        return sp;
    }

    private _unrotateFixedNormals() {
        // To Implement
    }

    public set depthSortParticles(val: boolean) {
        this._depthSortParticles = val;
    }

    public addShape(mesh: BABYLON.Mesh, nb: number, options?: { positionFunction?: any; vertexFunction?: any }): number {
        var meshPos = <BABYLON.FloatArray>mesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
        var meshInd = <BABYLON.IndicesArray>mesh.getIndices();
        var meshUV: any = [];
        var meshCol = mesh.isVerticesDataPresent(BABYLON.VertexBuffer.ColorKind) ? <BABYLON.FloatArray>mesh.getVerticesData(BABYLON.VertexBuffer.ColorKind) : [];
        var meshNor = <BABYLON.FloatArray>mesh.getVerticesData(BABYLON.VertexBuffer.NormalKind);
        var bbInfo;
        if (this._particlesIntersect) {
            bbInfo = mesh.getBoundingInfo();
        }

        var shape = this._posToShape(meshPos);
        var shapeUV = this._uvsToShapeUV(meshUV);
        var shapeInd = this._uvsToShapeUV(meshInd);
        var shapeNor = this._uvsToShapeUV(meshNor);
        var shapeCol = this._uvsToShapeUV(meshCol);

        var posfunc = options ? options.positionFunction : null;
        var vtxfunc = options ? options.vertexFunction : null;

        //var modelShape = new CustomModelShape(this._shapeCounter, shape, meshInd.length, shape.length, shapeUV, posfunc, vtxfunc);
        var modelShape = new BABYLON.ModelShape(this._shapeCounter, shape, shapeInd, shapeNor, shapeCol, shapeUV, posfunc, vtxfunc, null);
        // particles
        var sp;
        var currentCopy;
        var idx = this.nbParticles;
        for (var i = 0; i < nb; i++) {
            var currentPos = this._positions.length;
            var currentInd = this._indices.length;
            currentCopy = this._meshBuilder(this._index, shape, this._positions, meshInd, this._indices, meshUV, this._uvs, meshCol, this._colors, meshNor, this._normals, idx, i, options);
            if (this._updatable) {
                sp = this._addParticle(idx, currentPos, currentInd, modelShape, this._shapeCounter, i);
                sp.position.copyFrom(currentCopy.position);
                sp.rotation.copyFrom(currentCopy.rotation);
                if (currentCopy.rotationQuaternion && sp.rotationQuaternion) {
                    sp.rotationQuaternion.copyFrom(currentCopy.rotationQuaternion);
                }
                if (currentCopy.color && sp.color) {
                    sp.color.copyFrom(currentCopy.color);
                }
                sp.scaling.copyFrom(currentCopy.scaling);
                sp.uvs.copyFrom(currentCopy.uvs);
            }
            this._index += shape.length;
            idx++;
        }
        this.nbParticles += nb;
        this._shapeCounter++;
        return this._shapeCounter - 1;
    }

    // rebuilds a particle back to its just built status : if needed, recomputes the custom positions and vertices
    private _rebuildParticle(particle: BABYLON.SolidParticle): void {
        this._resetCopy();
        if (particle._model._positionFunction) {        // recall to stored custom positionFunction
            particle._model._positionFunction(this._copy, particle.idx, particle.idxInShape);
        }

        if (this._copy.rotationQuaternion) {
            this._quaternion.copyFrom(this._copy.rotationQuaternion);
        } else {
            this._yaw = this._copy.rotation.y;
            this._pitch = this._copy.rotation.x;
            this._roll = this._copy.rotation.z;
            this._quaternionRotationYPR();
        }
        this._quaternionToRotationMatrix();

        this._shape = particle._model._shape as any;

        particle.position.x = 0;
        particle.position.y = 0;
        particle.position.z = 0;
        particle.rotation.x = 0;
        particle.rotation.y = 0;
        particle.rotation.z = 0;
        particle.rotationQuaternion = null;
        particle.scaling.x = 1;
        particle.scaling.y = 1;
        particle.scaling.z = 1;
    }

    /**
    * Rebuilds the whole mesh and updates the VBO : custom positions and vertices are recomputed if needed.
    */
    public rebuildMesh(): void {
        for (var p = 0; p < this.particles.length; p++) {
            this._rebuildParticle(this.particles[p]);
        }
    }

    public containsTransparent() {

        for (var particleIndex = 0; particleIndex < this.nbParticles; particleIndex++) {

            var particle = this.particles[particleIndex];

            if (particle.color.a != 1) {
                return true;
            }
        }

        return false;
    }

    public getAlphaParticles(getOpaque: boolean): BABYLON.SolidParticle[] {

        var particles: BABYLON.SolidParticle[] = [];


        for (var particleIndex = 0; particleIndex < this.nbParticles; particleIndex++) {

            var particle = this.particles[particleIndex];

            if (getOpaque && particle.color.a == 1) {

                particles.push(particle);
            }
            else if (!getOpaque && particle.color.a != 1) {

                particles.push(particle);
            }
        }

        return particles;
    }

    /**
    *  Sets all the particles : this method actually really updates the mesh according to the particle positions, rotations, colors, textures, etc.
    *  This method calls `updateParticle()` for each particle of the SPS.
    *  For an animated SPS, it is usually called within the render loop.
    * @param start The particle index in the particle array where to start to compute the particle property values _(default 0)_
    * @param end The particle index in the particle array where to stop to compute the particle property values _(default nbParticle - 1)_
    * @param update If the mesh must be finally updated on this call after all the particle computations _(default true)_
    */
    public setParticles(start: number = 0, end: number = this.nbParticles - 1, update: boolean = true): void {
        if (!this._updatable) {
            return;
        }

        // custom beforeUpdate
        this.beforeUpdateParticles(start, end, update);

        this._cam_axisX.x = 1.0;
        this._cam_axisX.y = 0.0;
        this._cam_axisX.z = 0.0;

        this._cam_axisY.x = 0.0;
        this._cam_axisY.y = 1.0;
        this._cam_axisY.z = 0.0;

        this._cam_axisZ.x = 0.0;
        this._cam_axisZ.y = 0.0;
        this._cam_axisZ.z = 1.0;
        const tempVectors = BABYLON.TmpVectors.Vector3;
        // if the particles will always face the camera
        if (this.billboard) {
            // compute the camera position and un-rotate it by the current mesh rotation
            this._yaw = this.mesh.rotation.y;
            this._pitch = this.mesh.rotation.x;
            this._roll = this.mesh.rotation.z;
            this._quaternionRotationYPR();
            this._quaternionToRotationMatrix();
            this._rotMatrix.invertToRef(this._invertMatrix);
            this._camera._currentTarget.subtractToRef(this._camera.globalPosition, this._camDir);
            BABYLON.Vector3.TransformCoordinatesToRef(this._camDir, this._invertMatrix, this._cam_axisZ);
            this._cam_axisZ.normalize();

            // set two orthogonal vectors (_cam_axisX and and _cam_axisY) to the rotated camDir axis (_cam_axisZ)
            BABYLON.Vector3.CrossToRef(this._cam_axisZ, this._axisX, this._cam_axisY);
            BABYLON.Vector3.CrossToRef(this._cam_axisY, this._cam_axisZ, this._cam_axisX);
            this._cam_axisY.normalize();
            this._cam_axisX.normalize();
        }

        BABYLON.Matrix.IdentityToRef(this._rotMatrix);
        var idx = 0;
        var index = 0;
        var colidx = 0;
        var colorIndex = 0;
        var uvidx = 0;
        var uvIndex = 0;
        var pt = 0;


        if (this._computeBoundingBox) {
            BABYLON.Vector3.FromFloatsToRef(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE, this._minimum);
            BABYLON.Vector3.FromFloatsToRef(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE, this._maximum);
        }

        // particle loop
        end = (end > this.nbParticles - 1) ? this.nbParticles - 1 : end;
        for (var p = start; p <= end; p++) {
            this._particle = this.particles[p];
            this._shape = this._particle._model._shape as any;
            this._shapeUV = this._particle._model._shapeUV;

            // call to custom user function to update the particle properties
            this.updateParticle(this._particle);

            if (this._particle.isVisible) {

                // particle rotation matrix
                if (this.billboard) {
                    this._particle.rotation.x = 0.0;
                    this._particle.rotation.y = 0.0;
                }
                if (this._computeParticleRotation || this.billboard) {
                    if (this._particle.rotationQuaternion) {
                        this._quaternion.copyFrom(this._particle.rotationQuaternion);
                    } else {
                        this._yaw = this._particle.rotation.y;
                        this._pitch = this._particle.rotation.x;
                        this._roll = this._particle.rotation.z;
                        this._quaternionRotationYPR();
                    }
                    this._quaternionToRotationMatrix();
                }

                if (this._computeParticleVertex || this._computeBoundingBox || this._computeParticleColor || this._computeParticleTexture) {
                    for (pt = 0; pt < (this._particle._model as any)._vertexLength; pt++) {

                        idx = index + pt * 3;
                        colidx = colorIndex + pt * 4;
                        uvidx = uvIndex + pt * 2;

                        var vert = this._shape[idx / 3];
                        if (this._computeParticleVertex) {

                            this._positions32[idx] = vert.x;
                            this._positions32[idx + 1] = vert.y
                            this._positions32[idx + 2] = vert.z;
                        }


                        if (this._computeBoundingBox) {
                            /*
                            if (this._positions32[idx] < this._minimum.x) {
                                this._minimum.x = this._positions32[idx];
                            }
                            if (this._positions32[idx] > this._maximum.x) {
                                this._maximum.x = this._positions32[idx];
                            }
                            if (this._positions32[idx + 1] < this._minimum.y) {
                                this._minimum.y = this._positions32[idx + 1];
                            }
                            if (this._positions32[idx + 1] > this._maximum.y) {
                                this._maximum.y = this._positions32[idx + 1];
                            }
                            if (this._positions32[idx + 2] < this._minimum.z) {
                                this._minimum.z = this._positions32[idx + 2];
                            }
                            if (this._positions32[idx + 2] > this._maximum.z) {
                                this._maximum.z = this._positions32[idx + 2];
                            }
                            */
                        }

                        // normals : if the particles can't be morphed then just rotate the normals, what if much more faster than ComputeNormals()
                        if (!this._computeParticleVertex) {

                            /*
                            this._w = (this._normal.x * this._rotMatrix.m[3]) + (this._normal.y * this._rotMatrix.m[7]) + (this._normal.z * this._rotMatrix.m[11]) + this._rotMatrix.m[15];
                            this._rotated.x = ((this._normal.x * this._rotMatrix.m[0]) + (this._normal.y * this._rotMatrix.m[4]) + (this._normal.z * this._rotMatrix.m[8]) + this._rotMatrix.m[12]) / this._w;
                            this._rotated.y = ((this._normal.x * this._rotMatrix.m[1]) + (this._normal.y * this._rotMatrix.m[5]) + (this._normal.z * this._rotMatrix.m[9]) + this._rotMatrix.m[13]) / this._w;
                            this._rotated.z = ((this._normal.x * this._rotMatrix.m[2]) + (this._normal.y * this._rotMatrix.m[6]) + (this._normal.z * this._rotMatrix.m[10]) + this._rotMatrix.m[14]) / this._w;
                            */
                        }

                        if (this._computeParticleColor) {

                            if ((this._particle as any).material.name != "VertexColorMaterial") {

                                this._colors32[colidx] = 1;
                                this._colors32[colidx + 1] = 1;
                                this._colors32[colidx + 2] = 1;
                                this._colors32[colidx + 3] = 1;
                            }
                            else {
                                this._colors32[colidx] = this._origColors[colidx]
                                this._colors32[colidx + 1] = this._origColors[colidx + 1]
                                this._colors32[colidx + 2] = this._origColors[colidx + 2]
                                this._colors32[colidx + 3] = this._origColors[colidx + 3]
                            }
                        }

                        if (this._computeParticleTexture) {

                            this._uvs32[uvidx] = this._shapeUV[pt * 2] * (this._particle.uvs.z - this._particle.uvs.x) + this._particle.uvs.x;
                            this._uvs32[uvidx + 1] = this._shapeUV[pt * 2 + 1] * (this._particle.uvs.w - this._particle.uvs.y) + this._particle.uvs.y;
                        }
                    }
                }
                else {
                    var pt = (this._particle._model as any)._vertexLength - 1;
                    idx = index + pt * 3;
                    colidx = colorIndex + pt * 4;
                    uvidx = uvIndex + pt * 2;
                }
            }
            // particle not visible : scaled to zero and positioned to the camera position
            else {
                for (pt = 0; pt < (this._particle._model as any)._vertexLength; pt++) {

                    idx = index + pt * 3;
                    colidx = colorIndex + pt * 4;
                    uvidx = uvIndex + pt * 2;

                    var vert = this._shape[idx / 3];

                    this._positions32[idx] = 0;
                    this._positions32[idx + 1] = 0;
                    this._positions32[idx + 2] = 0;

                    if (this._computeBoundingBox) {
                        /*
                        if (this._positions32[idx] < this._minimum.x) {
                            this._minimum.x = this._positions32[idx];
                        }
                        if (this._positions32[idx] > this._maximum.x) {
                            this._maximum.x = this._positions32[idx];
                        }
                        if (this._positions32[idx + 1] < this._minimum.y) {
                            this._minimum.y = this._positions32[idx + 1];
                        }
                        if (this._positions32[idx + 1] > this._maximum.y) {
                            this._maximum.y = this._positions32[idx + 1];
                        }
                        if (this._positions32[idx + 2] < this._minimum.z) {
                            this._minimum.z = this._positions32[idx + 2];
                        }
                        if (this._positions32[idx + 2] > this._maximum.z) {
                            this._maximum.z = this._positions32[idx + 2];
                        }
                        */
                    }

                    // normals : if the particles can't be morphed then just rotate the normals, what if much more faster than ComputeNormals()
                    if (!this._computeParticleVertex) {

                        /*
                        this._w = (this._normal.x * this._rotMatrix.m[3]) + (this._normal.y * this._rotMatrix.m[7]) + (this._normal.z * this._rotMatrix.m[11]) + this._rotMatrix.m[15];
                        this._rotated.x = ((this._normal.x * this._rotMatrix.m[0]) + (this._normal.y * this._rotMatrix.m[4]) + (this._normal.z * this._rotMatrix.m[8]) + this._rotMatrix.m[12]) / this._w;
                        this._rotated.y = ((this._normal.x * this._rotMatrix.m[1]) + (this._normal.y * this._rotMatrix.m[5]) + (this._normal.z * this._rotMatrix.m[9]) + this._rotMatrix.m[13]) / this._w;
                        this._rotated.z = ((this._normal.x * this._rotMatrix.m[2]) + (this._normal.y * this._rotMatrix.m[6]) + (this._normal.z * this._rotMatrix.m[10]) + this._rotMatrix.m[14]) / this._w;
                        */
                    }

                    if (this._computeParticleColor) {

                        if ((this._particle as any).material.name != "VertexColorMaterial") {

                            this._colors32[colidx] = 1;
                            this._colors32[colidx + 1] = 1;
                            this._colors32[colidx + 2] = 1;
                            this._colors32[colidx + 3] = 1;
                        }
                        else {
                            this._colors32[colidx] = this._origColors[colidx]
                            this._colors32[colidx + 1] = this._origColors[colidx + 1]
                            this._colors32[colidx + 2] = this._origColors[colidx + 2]
                            this._colors32[colidx + 3] = this._origColors[colidx + 3]
                        }
                    }

                    if (this._computeParticleTexture) {


                        this._uvs32[uvidx] = this._shapeUV[pt * 2] * (this._particle.uvs.z - this._particle.uvs.x) + this._particle.uvs.x;
                        this._uvs32[uvidx + 1] = this._shapeUV[pt * 2 + 1] * (this._particle.uvs.w - this._particle.uvs.y) + this._particle.uvs.y;
                    }
                }
            }

            // increment indexes for the next particle
            index = idx + 3;
            colorIndex = colidx + 4;
            uvIndex = uvidx + 2;
        }

        // if the VBO must be updated
        if (update) {

            if (this._computeParticleColor) {
                this.mesh.setVerticesData(BABYLON.VertexBuffer.ColorKind, this._colors32);
            }
            if (this._computeParticleTexture) {
                this.mesh.updateVerticesData(BABYLON.VertexBuffer.UVKind, this._uvs32, false, false);
            }
            if (this._computeParticleVertex) {

                this.mesh.updateVerticesData(BABYLON.VertexBuffer.PositionKind, this._positions32, false, false);
            }

            this._applyMaterialAssignments();

            if (!this.mesh.areNormalsFrozen) {
                if (this._computeParticleVertex) {
                    // recompute the normals only if the particles can be morphed, update then also the normal reference array _fixedNormal32[]
                    //BABYLON.VertexData.ComputeNormals(this._positions32, this._indices, this._normals32);
                    //for (var i = 0; i < this._normals32.length; i++) {
                    //this._fixedNormal32[i] = this._normals32[i];
                    //}
                }
            }

            if (this._dirtyParticleVertices) {
                this._computeParticleVertex = false;
            }

            this.clearDirtyFlags();
        }
        if (this._computeBoundingBox) {
            this.mesh.setBoundingInfo(new BABYLON.BoundingInfo(this._minimum, this._maximum));
            this.mesh.getBoundingInfo().update(this.mesh._worldMatrix);
        }
        this.afterUpdateParticles(start, end, update);
    }

    private _quaternionRotationYPR(): void {
        this._halfroll = this._roll * 0.5;
        this._halfpitch = this._pitch * 0.5;
        this._halfyaw = this._yaw * 0.5;
        this._sinRoll = Math.sin(this._halfroll);
        this._cosRoll = Math.cos(this._halfroll);
        this._sinPitch = Math.sin(this._halfpitch);
        this._cosPitch = Math.cos(this._halfpitch);
        this._sinYaw = Math.sin(this._halfyaw);
        this._cosYaw = Math.cos(this._halfyaw);
        this._quaternion.x = (this._cosYaw * this._sinPitch * this._cosRoll) + (this._sinYaw * this._cosPitch * this._sinRoll);
        this._quaternion.y = (this._sinYaw * this._cosPitch * this._cosRoll) - (this._cosYaw * this._sinPitch * this._sinRoll);
        this._quaternion.z = (this._cosYaw * this._cosPitch * this._sinRoll) - (this._sinYaw * this._sinPitch * this._cosRoll);
        this._quaternion.w = (this._cosYaw * this._cosPitch * this._cosRoll) + (this._sinYaw * this._sinPitch * this._sinRoll);
    }

    private _quaternionToRotationMatrix(): void {


        this._rotMatrix.setRowFromFloats(0, 1.0 - (2.0 * (this._quaternion.y * this._quaternion.y + this._quaternion.z * this._quaternion.z)), 2.0 * (this._quaternion.x * this._quaternion.y + this._quaternion.z * this._quaternion.w), 2.0 * (this._quaternion.z * this._quaternion.x - this._quaternion.y * this._quaternion.w), 0);
        this._rotMatrix.setRowFromFloats(1, 2.0 * (this._quaternion.x * this._quaternion.y - this._quaternion.z * this._quaternion.w), 1.0 - (2.0 * (this._quaternion.z * this._quaternion.z + this._quaternion.x * this._quaternion.x)), 2.0 * (this._quaternion.y * this._quaternion.z + this._quaternion.x * this._quaternion.w), 0);
        this._rotMatrix.setRowFromFloats(2, 2.0 * (this._quaternion.z * this._quaternion.x + this._quaternion.y * this._quaternion.w), 2.0 * (this._quaternion.y * this._quaternion.z - this._quaternion.x * this._quaternion.w), 1.0 - (2.0 * (this._quaternion.y * this._quaternion.y + this._quaternion.x * this._quaternion.x)), 0);
        this._rotMatrix.setRowFromFloats(3, 0, 0, 0, 1.0);
    }

    /**
    * Disposes the SPS
    */
    public dispose(): void {
        this._disposeMaterialAssignments();
        this.mesh.dispose();
        this.vars = null;
        // drop references to internal big arrays for the GC
        this._positions = null;
        this._indices = null;
        this._normals = null;
        this._uvs = null;
        this._colors = null;
        this._uvs32 = null;
        this._colors32 = null;
        this._origColors = null;
        this.pickedParticles = null;
    }

    /**
    * Visibilty helper : Recomputes the visible size according to the mesh bounding box
    * doc : http://doc.babylonjs.com/overviews/Solid_Particle_System#sps-visibility
    */
    public refreshVisibleSize(): void {
        if (!this._isVisibilityBoxLocked) {
            this.mesh.refreshBoundingInfo();
        }
    }
    public setMixdAlpha(hasAlpha: boolean): void {
        this._containsMixedAlpha = hasAlpha;
    }
    /**
    * Visibility helper : Sets the size of a visibility box, this sets the underlying mesh bounding box.
    * @param size the size (float) of the visibility box
    * note : this doesn't lock the SPS mesh bounding box.
    * doc : http://doc.babylonjs.com/overviews/Solid_Particle_System#sps-visibility
    */
    public setVisibilityBox(size: number): void {
        var vis = size / 2;
        this.mesh.setBoundingInfo(new BABYLON.BoundingInfo(new BABYLON.Vector3(-vis, -vis, -vis), new BABYLON.Vector3(vis, vis, vis)));
    }


    // getter and setter
    public get isAlwaysVisible(): boolean {
        return this._alwaysVisible;
    }

    /**
    * Sets the SPS as always visible or not
    * doc : http://doc.babylonjs.com/overviews/Solid_Particle_System#sps-visibility
    */
    public set isAlwaysVisible(val: boolean) {
        this._alwaysVisible = val;
        this.mesh.alwaysSelectAsActiveMesh = val;
    }

    /**
    * Sets the SPS visibility box as locked or not. This enables/disables the underlying mesh bounding box updates.
    * doc : http://doc.babylonjs.com/overviews/Solid_Particle_System#sps-visibility
    */
    public set isVisibilityBoxLocked(val: boolean) {
        this._isVisibilityBoxLocked = val;
        this.mesh.getBoundingInfo().isLocked = val;
    }

    public get isVisibilityBoxLocked(): boolean {
        return this._isVisibilityBoxLocked;
    }

    // Optimizer setters
    /**
    * Tells to `setParticles()` to compute the particle rotations or not.
    * Default value : true. The SPS is faster when it's set to false.
    * Note : the particle rotations aren't stored values, so setting `computeParticleRotation` to false will prevents the particle to rotate.
    */
    public set computeParticleRotation(val: boolean) {
        this._computeParticleRotation = val;
    }
    /**
    * Tells to `setParticles()` to compute the particle colors or not.
    * Default value : true. The SPS is faster when it's set to false.
    * Note : the particle colors are stored values, so setting `computeParticleColor` to false will keep yet the last colors set.
    */
    public set computeParticleColor(val: boolean) {
        this._computeParticleColor = val;
    }
    /**
    * Tells to `setParticles()` to compute the particle textures or not.
    * Default value : true. The SPS is faster when it's set to false.
    * Note : the particle textures are stored values, so setting `computeParticleTexture` to false will keep yet the last colors set.
    */
    public set computeParticleTexture(val: boolean) {
        this._computeParticleTexture = val;
    }
    /**
    * Tells to `setParticles()` to call the vertex function for each vertex of each particle, or not.
    * Default value : false. The SPS is faster when it's set to false.
    * Note : the particle custom vertex positions aren't stored values.
    */
    public set computeParticleVertex(val: boolean) {
        this._computeParticleVertex = val;
    }
    /**
    * Tells to `setParticles()` to compute or not the mesh bounding box when computing the particle positions.
    */
    public set computeBoundingBox(val: boolean) {
        this._computeBoundingBox = val;
    }

    // getters
    public get computeParticleRotation(): boolean {
        return this._computeParticleRotation;
    }

    public get computeParticleColor(): boolean {
        return this._computeParticleColor;
    }

    public get hasVertexColor(): boolean {

        var allOverridden = true;
        for (var x = 0; x < this.particles.length; x++) {
            if ((this.particles[x] as any).material == null || (this.particles[x] as any).material.name == "VertexColorMaterial") {
                allOverridden = false;
                break;
            }
        }
        return this._hasVertexColor && !allOverridden;
    }
    public get computeParticleTexture(): boolean {
        return this._computeParticleTexture;
    }

    public get computeParticleVertex(): boolean {
        return this._computeParticleVertex;
    }

    public get computeBoundingBox(): boolean {
        return this._computeBoundingBox;
    }

    // =======================================================================
    // Particle behavior logic
    // these following methods may be overwritten by the user to fit his needs


    /**
    * This function does nothing. It may be overwritten to set all the particle first values.
    * The SPS doesn't call this function, you may have to call it by your own.
    * doc : http://doc.babylonjs.com/overviews/Solid_Particle_System#particle-management
    */
    public initParticles(): void {
    }

    /**
    * This function does nothing. It may be overwritten to recycle a particle.
    * The SPS doesn't call this function, you may have to call it by your own.
    * doc : http://doc.babylonjs.com/overviews/Solid_Particle_System#particle-management
    */
    public recycleParticle(particle: BABYLON.SolidParticle): BABYLON.SolidParticle {
        return particle;
    }

    /**
    * Updates a particle : this function should  be overwritten by the user.
    * It is called on each particle by `setParticles()`. This is the place to code each particle behavior.
    * doc : http://doc.babylonjs.com/overviews/Solid_Particle_System#particle-management
    * ex : just set a particle position or velocity and recycle conditions
    */
    public updateParticle(particle: BABYLON.SolidParticle): BABYLON.SolidParticle {
        return particle;
    }

    /**
    * Updates a vertex of a particle : it can be overwritten by the user.
    * This will be called on each vertex particle by `setParticles()` if `computeParticleVertex` is set to true only.
    * @param particle the current particle
    * @param vertex the current index of the current particle
    * @param pt the index of the current vertex in the particle shape
    * doc : http://doc.babylonjs.com/overviews/Solid_Particle_System#update-each-particle-shape
    * ex : just set a vertex particle position
    */
    public updateParticleVertex(particle: BABYLON.SolidParticle, vertex: BABYLON.Vector3, pt: number): BABYLON.Vector3 {
        return vertex;
    }

    /**
    * This will be called before any other treatment by `setParticles()` and will be passed three parameters.
    * This does nothing and may be overwritten by the user.
    * @param start the particle index in the particle array where to stop to iterate, same than the value passed to setParticle()
    * @param stop the particle index in the particle array where to stop to iterate, same than the value passed to setParticle()
    * @param update the boolean update value actually passed to setParticles()
    */
    public beforeUpdateParticles(start?: number, stop?: number, update?: boolean): void {
    }
    /**
    * This will be called  by `setParticles()` after all the other treatments and just before the actual mesh update.
    * This will be passed three parameters.
    * This does nothing and may be overwritten by the user.
    * @param start the particle index in the particle array where to stop to iterate, same than the value passed to setParticle()
    * @param stop the particle index in the particle array where to stop to iterate, same than the value passed to setParticle()
    * @param update the boolean update value actually passed to setParticles()
    */
    public afterUpdateParticles(start?: number, stop?: number, update?: boolean): void {
    }
}

export default MeshManager;
